import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { InvoiceMoney } from '@/components/invoices/InvoiceBits';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import {
  QuoteLineItemsTable,
  type CompanySalesTax,
} from '@/components/quotes/QuoteLineItemsTable';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { selectableStages, useQuoteStageOptions } from '@/lib/use-quote-options';
/*
 * ⚠️ DATES ON THIS RESOURCE ARE BARE CALENDAR DAYS, and nothing in this file
 * may build one with `new Date()`.
 *
 * `issueDate` and `validTill` are `YYYY-MM-DD` — a day, not an instant. The two
 * ways of getting one out of a `Date` are both wrong in opposite hemispheres:
 * `toISOString().slice(0,10)` hands back TOMORROW for anyone east of Greenwich
 * after their local evening, and parsing `'2026-08-28'` back through `new Date()`
 * reads it as UTC midnight and moves the day BACKWARDS for everyone west of it.
 * A quote issued on the wrong day is a wrong date on a customer-facing document.
 *
 * `todayIsoDate` assembles today from LOCAL calendar parts, and `addDaysIso`
 * uses a `Date` only as a calendar calculator — constructed from explicit local
 * components and read back through the local getters, so the month-length and
 * leap-year arithmetic is right and UTC is never touched. They are the two
 * functions this app produces a calendar day with; there is no third.
 *
 * `normaliseStatusName` is the app's one stage/status name normaliser
 * (case-fold + whitespace collapse). It lives beside the invoice statuses but
 * is not invoice-specific — `quote-utils` matches quote STAGE names through the
 * very same import, and a second normaliser here would be the drift that puts
 * one screen's idea of "Draft" beside another's.
 */
import { normaliseStatusName, todayIsoDate } from '@/lib/invoice-utils';
import {
  CONTACT_NOT_ON_COMPANY,
  contactScopeIsAuthoritative,
  primaryContactSeed,
} from '@/lib/contact-scope';
import { ContactPickerField } from '@/components/contacts/ContactPickerField';
import { addDaysIso } from '@/lib/quote-utils';
/*
 * ⚠️ The quote/invoice vocabulary lives in ONE module, and this form must not
 * grow a second copy of any of it. `DOCUMENT_LINE_AMOUNT_OPTIONS` in
 * particular was inlined here and in `InvoiceFormModal` and the two labellings
 * diverged on 2026-08-26 — the same customer-facing tax treatment worded two
 * ways across the halves of one conversion. Import; never re-inline.
 */
import {
  AMOUNT_PATTERN,
  CURRENCY_PATTERN,
  DOCUMENT_LINE_AMOUNT_OPTIONS,
  TOTAL_KEYS,
  TOTAL_LABELS,
  UNCLEARABLE_TOTALS,
  USE_COMPANY_DEFAULT,
  toDocumentLineAmountType,
} from '@/components/documents/document-form';
/*
 * The note box at the foot of this form, shared with `InvoiceFormModal`.
 *
 * ⚠️ It is not a column on `d_quotes` — it is a `d_notes` row, the same activity
 * system the Tasks & notes drawer shows — so it is a SECOND request against a
 * quote that has to exist first. `writeDraftNote` never throws and never sends
 * an empty body; the component owns every sentence about what became of it, so
 * the two forms cannot word one outcome two ways. Read `draft-note.ts` before
 * changing either half.
 */
import { DraftNoteSection } from '@/components/documents/DocumentFormBits';
import { writeDraftNote, type DraftNoteOutcome } from '@/components/documents/draft-note';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { isRealDate, without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
/*
 * The planned basket a raise seeds the line grid with. Imported rather than
 * restated — `plannedProductLines()` owns the one judgement a seed needs, which
 * is whether a planned decimal quantity can be a line quantity.
 */
import type { PlannedProductLine } from '@/lib/api';
import { cx } from '@/lib/cx';
/*
 * The two sentences a deal conversion cannot do without, imported rather than
 * re-worded. They were written for the confirmation dialog this modal replaced,
 * and both are facts the reader cannot see anywhere on this form: a deal line
 * carries no tax rate, and the deal's stage moves (to the quote-sent stage,
 * owner 2026-10-18; the deal's lines never do). Re-typing either here
 * would be a second copy of a sentence the invoice route still renders from the
 * original, and the two would drift.
 */
/*
 * ✅ **RESOLVED 2026-08-30, and the note that stood here is now history.**
 * *SUPERSEDED: "`CONVERTED_LINES_HAVE_NO_TAX` IS DELIBERATELY NOT IMPORTED ANY
 * MORE, AND ITS ABSENCE IS THE FIX … **The shared constant is left exactly as it
 * is, on purpose.** `DealDetailPage` and `InvoiceDetailPage` still render it,
 * and correcting a sentence for three surfaces from inside one form would reword
 * copy on two screens nobody has re-measured … hand it back to the shared module
 * only once the invoice route's own behaviour has been READ rather than
 * assumed."*
 *
 * **It has now been read, which is the condition that note set.** The invoice
 * route seeds a copied line's rate from the company exactly as this one does —
 * and on that route a `0.00` is additionally UNREACHABLE, because
 * `COMPANY_HAS_NO_TAX_RATE` refuses the conversion before the transaction opens
 * whenever the deal has lines and the company records none.
 *
 * So the two routes do **not** share one true sentence, and the resolution is
 * two constants rather than one restored: `CONVERTED_LINE_TAX_RATE` (this route,
 * conditional, because `0.00` is still reachable here — a quote is an offer and
 * is never refused for a missing company rate) and
 * `CONVERTED_INVOICE_LINE_TAX_RATE` (the other, unconditional). Both live in
 * `deal-conversion.ts`, side by side, so neither can be reworded without the
 * other being read. `CONVERTED_LINES_HAVE_NO_TAX` is **deleted**, not renamed —
 * an absent export fails at `tsc` on every one of its three old call sites,
 * where a rename would have handed each of them a new name for a claim that had
 * become route-dependent.
 *
 * ⚠️ **The private `CONVERTED_LINE_TAX_RATE` this file used to declare is gone
 * with it.** Its wording — the owner's own, from the copy pass — is what moved
 * into the shared module verbatim; the reasoning behind every clause moved with
 * it. Do not re-declare a local copy.
 */
/*
 * ⚠️ `CONVERTED_LINE_TAX_RATE` IS DELIBERATELY NOT IMPORTED, AND ITS ABSENCE IS
 * AGAIN THE FIX — for the second time, on the same constant, for a better
 * reason than last time.
 *
 * This route no longer renders a SENTENCE about where a converted line's tax
 * comes from. It renders the company's ACTUAL rate, tagged with the company it
 * was read for (see the `taxRateSource` block further down). That is strictly
 * better than the constant: a figure read from the record cannot drift from the
 * record, whereas a sentence describing it always can — which is exactly how
 * this constant's predecessor came to be false on both routes at once.
 *
 * ⚠️ **The constant is still exported and still correct — do NOT delete it, and
 * do NOT re-add this import to "use" it.** Its twin
 * `CONVERTED_INVOICE_LINE_TAX_RATE` IS rendered, on the invoice route, where
 * the live-rate treatment is impossible: that conversion refuses outright when
 * the company records no rate, so there is no rate on screen to show and only a
 * sentence will do.
 *
 * ⚠️ **Every other mention of `CONVERTED_LINE_TAX_RATE` in this file is a
 * comment**, including several explaining its history. So a grep for the name
 * finds it and it looks used. Grep the SURFACE — the render — not the wording.
 */
import { QUOTE_CONVERSION_MOVES_THE_DEAL } from '@/lib/deal-conversion';
/*
 * ⚠️ The deal→quote line overrides live in ONE module, and this form must not
 * grow a second copy of any of it. The discount pair in particular is resolved
 * there and nowhere else: `discount` and `discountAmount` are mutually
 * exclusive columns, so a second place deciding which of them a row means is a
 * second place that can decide it differently.
 */
/*
 * ⚠️ **ONLY THE ROUTING HALF IS IMPORTED HERE NOW.** *SUPERSEDED: the seed
 * (`convertLineEditOf`), the change test (`convertLineEditsMatch`), the
 * validator (`validateConvertLineEdit`) and the payload builder
 * (`convertLineOverrides`) were all called from this file, because this file
 * held the boxes.* `QuoteLineItemsTable`'s convert mode holds them now and calls
 * all four; what stays here is the refusal routing, which cannot move — the
 * array index a 422 names is a position in WHAT WAS SENT, and this form is what
 * holds the array it sent.
 *
 * ⚠️ **AND IT IS `convertLineSetFieldErrors` NOW, NOT `convertLineFieldErrors`.**
 * *SUPERSEDED: "what stays here is `convertLineFieldErrors`".* The route gained
 * `lineSet` — the complete final list — and a `lineSet` refusal names an ARRAY
 * POSITION rather than something that can be turned back into a `dealLineId`,
 * because a line ADDED on this screen has no `dealLineId` at all. So the grid
 * hands up `rowKeys` beside the entries and that array is what the routing
 * reads. `convertLineFieldErrors` still exists and still belongs to `lines`,
 * which is what `InvoiceFormModal` sends.
 */
import {
  convertLineSetFieldErrors,
  type DealConvertInput,
  type DealConvertLineSetEntry,
} from '@/lib/deal-convert-lines';
/*
 * ⚠️ **`CONVERTED_LINES_HAVE_NO_TAX` IS DELIBERATELY NOT IMPORTED ANY MORE, AND
 * ITS ABSENCE IS THE FIX.** It reads *"A deal line carries no tax rate, so every
 * line arrives with none set and 0.00 tax. Somebody has to set a rate on each
 * line."* — which stopped being true of this route on 2026-08-30, when a
 * converted line began taking its rate from the COMPANY through the same lookup
 * a line create uses. The corrected sentence is {@link CONVERTED_LINE_TAX_RATE},
 * below, and it has to cover BOTH halves of the new rule.
 *
 * ⚠️ **The shared constant is left exactly as it is, on purpose.**
 * `DealDetailPage` and `InvoiceDetailPage` still render it, and correcting a
 * sentence for three surfaces from inside one form would reword copy on two
 * screens nobody has re-measured. This is a divergence with a reason, not a
 * second copy of one sentence: hand it back to the shared module only once the
 * invoice route's own behaviour has been read rather than assumed.
 */
/*
 * ⚠️ **`inclusionLine` IS NO LONGER IMPORTED, AND THE RULE IT CARRIES HAS NOT
 * MOVED — the CALLER has.** *SUPERSEDED: the convert table named each line's
 * campaign inclusions itself, through the one copy of the singular/plural rule
 * that prints on a customer's document.* `QuoteLineItemsTable` renders that
 * read-back now, through `InclusionSummary` and `@/lib/inclusions`. **Nothing
 * here may pluralise by appending a letter** — "5 campuss" is what that
 * produces — so if a count ever comes back to this file, it comes back through
 * that module and not through a template literal.
 */
/*
 * ⚠️ `formatDecimalString` went with the convert panel's discount SUB-LINE,
 * which is a real Discount box now. Nothing else here formatted a decimal —
 * `InvoiceMoney` does its own — so the import went with it rather than being
 * left as an unused re-export somebody would later reach for by habit.
 */
/*
 * ⚠️ **`pluralise` went with the convert table's inclusion count**, and
 * `formatDecimalString` went with its discount sub-line before that. Nothing in
 * this file formats a decimal or pluralises a noun any more — `InvoiceMoney`
 * does its own, and the line grid does both — so neither is left as an unused
 * import somebody would later reach for by habit.
 */
/*
 * ⚠️ The default SUBJECT is not built here. `defaultDocumentName` is the ONE
 * copy of the owner's `<company short name> - Plans - <current cycle>` pattern,
 * shared with `DealFormModal` — a deal's `dealName` and a quote's `subject` are
 * different columns on different tables and one naming convention, and two
 * implementations would agree on the day they were written and drift the first
 * time either was edited. `companyDocumentName` is no longer imported directly:
 * it is the first segment of that helper, and reaching past it is how the second
 * copy starts.
 */
import { currentCycle, defaultDocumentName } from '@/lib/document-naming';
import { useCycleOptions } from '@/lib/use-cycle-options';
/*
 * ⚠️ The RESOLVED default terms, and there is no second door to them. The
 * settings card binds to the RAW stored value and this form seeds from the
 * effective one; the two differ exactly when nothing is configured, and swapping
 * them reproduces the bug the feature exists to fix. Read the hook's header
 * before calling `api.getQuoteTermsSettings` from here instead.
 */
import { useQuoteTermsDefault } from '@/lib/use-quote-terms-default';
/*
 * ⚠️ The conversion arithmetic lives in ONE module and this form does none of
 * it. A rate is a decimal string with up to six places and the totals are
 * decimal strings with two — multiplying either through a JavaScript number is
 * the float round-trip every money path in this app exists to avoid, and it
 * fails quietly: the figure renders, looks right, and is a penny out. Import
 * `toBaseCurrency`; never write `Number(rate) * Number(total)` here.
 */
import {
  CURRENCY_RATE_MAX_DP,
  exchangeRateOf,
  formatRateLine,
  isValidCurrencyRate,
  toBaseCurrency,
  type ExchangeRate,
} from '@/lib/currency-conversion';
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
/*
 * ⚠️ **`useUserOptions` is gone from this form, and its absence is the point.**
 * The Owner picker it fed was replaced by a read-only ACCOUNT LEAD, which is
 * derived through the company server-side and carries no picker, no directory
 * read and no write key. Re-importing it here would be the first step back
 * towards an editable owner — see the account-lead block in the render.
 */
/*
 * ⚠️ **Nothing from `@/lib/xero-tax-rates` is imported here any more, and the
 * warning above `FormValues` is live again.** `LINE_AMOUNT_OPTIONS` is the
 * CONTACT vocabulary (`INCLUSIVE | EXCLUSIVE | NONE`) where a document takes
 * `NOTAX`, so importing it back would be a 422 on every save that used it. The
 * one thing it was safe for — labelling the COMPANY's own stored
 * `xeroSalesLineAmountType` in the read-only sales-defaults row — went with
 * that row, on the owner's instruction. The document control below uses
 * `DOCUMENT_LINE_AMOUNT_OPTIONS` and must keep doing so.
 */
/*
 * 🔴 **`@/lib/use-project-options` and `@/lib/xero-tracking-location` are no
 * longer imported here at all — 2026-08-31.** *SUPERSEDED: "Only the hook now …
 * the one thing it still reads a project for is the region and intake behind
 * the two Xero tracking hints."* Those hints were the last reader, and they went
 * with the tracking overrides.
 *
 * ⚠️ **`trackingLocationOf` is very much alive elsewhere and must not be
 * inlined if it comes back here.** It is the one copy of the rule that a project
 * covering two or more regions pushes with NO Location — `regions[0].name` is
 * the wrong shortcut, in the reassuring direction.
 */
import { useXero } from '@/lib/xero-context';
/*
 * The push vocabulary.
 *
 * ⚠️ **`pushIntentFor` AND `linkOf` ARE GONE FROM THIS FILE.** *SUPERSEDED:
 * "`pushIntentFor` is the ONLY producer of the branded token `api.pushToXero`
 * will not move without — a call site cannot write one down, it can only derive
 * one from the link, which is what the server derives its own create-vs-update
 * decision from."* All of that is still true of `pushIntentFor`; what changed is
 * that **this form makes no Xero call at all any more**, so it has no intent to
 * derive and no link to read one from. The server pushes inside the save.
 *
 * `xeroErrorLines` stays: it is how every other surface in this app renders
 * Xero's own words with the generic wrapper dropped, and a refusal the SERVER
 * reports has to read the same as one a button produced.
 */
/*
 * ⚠️ `conversionPushReport` is the SHARED classifier, not a second one. It is
 * what `InvoiceFormModal` and `BillQuoteLinesModal` already put in front of the
 * other three conversion endpoints, and it is the reason `skipped` and `pushed`
 * cannot reach a band from here: it answers `null` for both. Read its own doc
 * before changing anything this file does with the report — in particular that
 * the entity is the entity of `data`, which on `POST /deals/{id}/convert` is a
 * QUOTE and never the deal in the URL.
 */
import {
  conversionPushReport,
  xeroErrorLines,
  type XeroPushReport,
  type XeroRowPushReport,
} from '@/lib/xero';
import { XeroPushReportBanner } from '@/components/xero/XeroRowPushControl';
/*
 * ⚠️ `xeroCooldownNotSentMessage`, NOT `xeroCooldownMessage`. The latter is
 * read-shaped — it says a LIST could not be read and ends by telling the reader
 * that nothing they do will help. Both halves are wrong here: nothing was read,
 * the quote was WRITTEN and saved, and there is something the reader does later
 * — send it from the quote's own page. Leading with the limit reads as "your
 * quote failed", and the natural answer to that is to create it again, which is
 * how a cooldown becomes duplicate documents in an accounting system.
 */
/*
 * ⚠️ **`noteXeroRateLimit` and `xeroCooldownSecondsRemaining` ARE NO LONGER
 * IMPORTED, and their absence is a real loss rather than a tidy-up.** This form
 * used to refuse to spend a call while a session-wide deadline was live, and to
 * RECORD a limit learned from its own push so the reference-data reads would
 * stop asking too. It makes no call to learn one from any more. Refusing to
 * SAVE because Xero is rate-limited would be refusing to store somebody's work
 * over a quota, so neither can move into the save. `xero-cooldown.ts` still
 * serves the reads and the manual push on the quote's own page.
 *
 * `xeroCooldownNotSentMessage` stays, because the server can still report a
 * rate limit as the reason a save-time push did not land — and the wording rule
 * below is unchanged by where the fact came from.
 */
import { xeroCooldownNotSentMessage } from '@/lib/xero-cooldown';
import { brandingThemeOptions, useXeroBrandingThemes } from '@/lib/use-xero-branding-themes';
import {
  Button,
  Combobox,
  Field,
  Input,
  Modal,
  Select,
  /*
   * ⚠️ **The eight table primitives and `Skeleton` went with the hand-built
   * convert table.** This form renders no table of its own any more: line items
   * are `QuoteLineItemsTable` on all three paths, which is the whole point of
   * the change. Do not re-import one to lay out a figure — that is how the
   * second table started.
   */
  Textarea,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { billingGateRefusalOf } from '@/lib/company-approval';
import { CREATE_AND_PREVIEW_LABEL } from '@/lib/quote-pdf-preview';
import type {
  Deal,
  DealProduct,
  ProjectCity,
  Quote,
  QuoteCreateInput,
  QuoteProductCreateInput,
  QuoteStage,
  QuoteUpdateInput,
} from '@/lib/types';

/**
 * What a caller may seed a NEW quote with. See {@link QuoteFormModalProps.prefill}.
 *
 * Every field is optional and an absent one seeds nothing — an empty object is
 * a blank form, not a form full of blanks.
 */
export interface QuoteFormPrefill {
  subject?: string;
  company?: { id: number; name: string };
  contact?: { id: number; name: string };
  deal?: { id: number; name: string };
  /** A three-letter ISO code. Uppercased on the way in, as the picker does. */
  currency?: string;
  /**
   * The planned basket the new quote OPENS WITH — rows on the line grid,
   * visible and adjustable, before anything is created.
   *
   * ⚠️ **THIS IS THE ONE PREFILL KEY THAT DOES NOT LAND IN `values`.** Every
   * other key here is a header field and goes through {@link withPrefill}. A
   * line item is `POST /quotes/{id}/products` and **cannot exist before the
   * quote does**, so this key seeds the DRAFT GRID instead and is written by
   * the existing two-phase create: quote first, then each line, then a report
   * naming any that were refused. Nothing new happens on save.
   *
   * ⚠️ **PREFILLING IS NOT RAISING.** These are a seed and not a commitment —
   * the person still sees the modal, still edits the rows, and still presses
   * Save. Nothing on this path creates a document without that.
   *
   * ⚠️ **`PlannedProductLine` is imported from `@/lib/api`, deliberately, and
   * is not restated here.** `plannedProductLines()` already decides whether a
   * planned decimal quantity can be a line quantity; a look-alike type in this
   * file would be a second home for that decision.
   *
   * ⚠️ **A row whose `lineQuantity` is `null` opens with an EMPTY quantity box
   * and BLOCKS SAVE**, naming the planned figure. It is not rounded to 1 and it
   * is not quietly omitted — an omitted `quantity` is 1 on the server, which is
   * the same wrong number arriving by a longer route. See
   * `QuoteLineItemsTable`'s `seededRowOf`.
   *
   * ⚠️ **It is part of the REMOUNT KEY** — see {@link createKeyOf}. Two planned
   * rows for the same company produce the same key on every other field, so
   * without it the second raise would open carrying the first plan's basket.
   *
   * 🔴 **THIS IS ALSO THE ONLY WAY A PROJECT CAN TRAVEL INTO A NEW DOCUMENT —
   * 2026-09-11.** `PlannedProductLine.project` seeds the LINE's project picker.
   * ⛔ **There is deliberately no `project` key on THIS interface and there
   * cannot be one**: a header project picker was removed from all three
   * document forms on the owner's instruction, `projectId` is absent from
   * `FormValues` structurally, and an explicit `null` on a header `projectId`
   * CLEARS live data with a `200`. A project belongs to a LINE here, which is
   * the model `f_quote_products.project_id` actually has.
   */
  lines?: readonly PlannedProductLine[];
}

export interface QuoteFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  quote?: Quote;
  onSaved: (quote: Quote) => void;
  /**
   * Offers a second submit, "Create and preview", on a CREATE (plain or
   * `convertFrom` a deal) — never on an edit. It runs the identical save path;
   * only a FULLY successful save calls this INSTEAD of `onSaved` (callers
   * navigate to `quotePreviewHref(quote.id)`). Anything less — a refused line,
   * a refused note, a refused header PATCH, a push Xero would not take — stays
   * open exactly as the plain create does, so the report is not unmounted.
   * Omitted = no second button.
   */
  onCreatedPreview?: (quote: Quote) => void;
  /**
   * Hands the caller the SERVER'S report of what the save did to Xero.
   *
   * ⚠️ **CORRECTED 2026-08-30. This used to CAUSE the push, and it does not
   * any more.** It was `onXeroAutoPush?: (saved: Quote) => Promise<void>`,
   * awaited after the PATCH, and its note read:
   *
   * > *"The owner asked for a linked quote to be re-sent to Xero on every save.
   * > … So the capability travels with its reporting channel. **A caller that
   * > cannot report the outcome does not get the push**, and that is a property
   * > of the type rather than a rule someone has to remember — `QuoteDetailPage`
   * > passes it, `QuotesPage` does not, and a save from the list therefore does
   * > not auto-push and claims nothing."*
   *
   * **The push now happens on the server, inside the PATCH.** So half of that
   * is still true and half of it is now false, which is exactly why it is
   * quoted rather than deleted:
   *
   * - ✅ *the capability travels with its reporting channel* — still true of
   *   the REPORT. A caller that passes nothing is told nothing.
   * - ⛔ *a caller that cannot report the outcome does not get the push* — no
   *   longer in this app's gift. `QuotesPage` saves a quote and the server
   *   pushes it whether or not this prop is passed. Omitting it buys silence,
   *   not safety.
   *
   * ## What it still guarantees
   *
   * - **Only on an EDIT.** A create is a `POST`, and the server answers a skip
   *   for a quote that is not in Xero yet, so there is nothing here to report
   *   — the create's own deliberate first push has its own report band.
   * - **It cannot fail or roll back the save.** The PATCH has returned by the
   *   time this runs; there is no transaction across the two halves and nothing
   *   here may imply there is.
   * - **It runs on the acceptance retry too**, which is the save most likely to
   *   be the last edit anybody makes.
   *
   * ⚠️ **The RAW server report goes up, not a classified outcome.** The
   * vocabulary belongs to `@/lib/xero` and the copy belongs to the page;
   * classifying it here would put a second reader of the same wire shape in a
   * component that has nowhere to render the result. `undefined` is what an
   * older server, or a rollback, sends — and it is an ordinary answer, not an
   * error. `autoPushOutcomeOf` is where that is decided, once.
   */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
  /**
   * The LINE GRID wrote or deleted a line on its own — a caller's copy of this
   * quote's line items is stale.
   *
   * ⚠️ **This is the half `onSaved` does not cover, and that gap was a real
   * defect.** The grid renders its own "Save N rows", which writes without this
   * form's footer Save ever being pressed; after it the footer Save is
   * correctly DISABLED (there is nothing left to send) and the secondary button
   * reads "Cancel", so Cancel is the only way out and is the word that makes
   * leaving feel safe. `finish()` then falls to `onClose` — no `onSaved`, no
   * refetch — and a list showing a SERVER-COMPUTED field derived from those
   * very lines (`lineProjectStatus` on `/quotes`) keeps showing the old value.
   * Nothing errors.
   *
   * ⛔ **Not folded into `onSaved`, deliberately.** `onSaved` has eleven mount
   * points and some of them NAVIGATE — firing it from a grid-only save would
   * take somebody away from a modal they are still working in. This carries no
   * quote and asks for nothing but a refetch.
   *
   * ⚠️ **It MAY FIRE WHILE THIS MODAL IS STILL OPEN, and refetching underneath
   * an open modal is correct rather than premature.** The list genuinely is
   * stale at that instant; waiting for the close would put the signal back on
   * the four exit paths this prop exists to stop depending on.
   */
  onLinesSaved?: () => void;
  /**
   * Pre-selects and LOCKS the company, for a form opened from a company record.
   *
   * Create mode's counterpart to the pin an edit already builds for the quote's
   * own company: the id seeds the form and the name pins the option, so the
   * field shows a real name without a lookup. While it is set the company
   * Combobox is replaced by a read-only field — the company is the reason this
   * form was opened, so it is stated rather than offered as a choice that can
   * be changed by accident and saved wrong.
   *
   * Same shape and same behaviour as `ContactFormModal`'s prop of this name.
   */
  lockedCompany?: { id: number; name: string };
  /**
   * Values a NEW quote STARTS from — a seed, and emphatically not a lock.
   *
   * ⚠️ **This is not `lockedCompany` with a wider surface, and it must never
   * grow into one.** `lockedCompany` replaces the company Combobox with a
   * read-only Input because the company is the *reason* that form was opened.
   * A quote raised from a deal is the opposite case: the person is at the
   * moment of deciding, and the thing they are most likely to change is
   * precisely the thing this filled in — a different contact at the same
   * company, a currency the deal was priced in a year ago. So every key here
   * lands in `values` and in nothing else. Every control renders exactly as it
   * would on a blank form, and every one of them is editable before Save.
   *
   * ⚠️ **CREATE ONLY.** It is applied in the `useState` initialiser and never
   * again, and only when there is no `quote`. Seeding an edit would overwrite a
   * stored value with a caller's guess; re-applying it later would fight the
   * user's own typing.
   *
   * Each entry carries its NAME as well as its id because the three lookups are
   * searched pickers: the id alone selects nothing until a page of the
   * directory happens to contain it, so the name is pinned into the option list
   * the same way a record's own link is. Same reasoning as `lockedCompany`
   * carrying a name it never sends.
   *
   * ⚠️ **There is no `project` key here, and there cannot be one.** This form
   * holds no `projectId` — see `ID_KEYS` — so a project seed would have nowhere
   * to land and nothing to send it with. `InvoiceFormModal`'s prefill does
   * carry one, because an invoice keeps its header project.
   */
  prefill?: QuoteFormPrefill;
  /**
   * The deal this form is CONVERTING — which makes Save a conversion rather
   * than a create, and is a different thing again from `prefill`.
   *
   * ⚠️ **Save calls `POST /deals/{id}/convert`, not `POST /quotes`.** That is
   * the whole reason this is its own prop rather than a fatter prefill: convert
   * copies every line item, with its project, its cities and its campaign
   * inclusions, in ONE server-side transaction. A line is
   * `POST /quotes/{id}/products` and cannot exist before the quote does, so a
   * plain prefilled create would silently drop every one of them.
   *
   * ⚠️ **CORRECTED — convert takes TWO mutually exclusive LINE keys now.**
   * *SUPERSEDED, twice: "Convert's body schema is `z.object({}).strict()`: it
   * accepts no overrides at all."; then "It is still `.strict()` and it carries
   * **`lines`**, a per-line override of `quantity`, `unitPrice`, the discount
   * pair and `description`."* It carries `lines` OR **`lineSet`** — the complete
   * final list of lines, which this form is what sends
   * (`@/lib/deal-convert-lines`). **It carries no HEADER key**, which is the
   * half this paragraph was really about and which is unchanged through both
   * corrections.
   *
   * So the header fields on this form still cannot ride along with the
   * conversion. Whatever the person changed there is sent as a SECOND write,
   * `PATCH /quotes/{newId}`, bounded by {@link CONVERT_FOLLOW_UP_PATCH_KEYS} —
   * which means the quote is real before those edits land, and a failure between
   * the two is reported as exactly that rather than as "nothing was saved". The
   * LINE edits are not in that second write and never were: they ride on the
   * conversion itself, so a refused header PATCH leaves them already applied.
   *
   * ⚠️ **CREATE ONLY, and it never combines with `quote`.** Ignored outright
   * when a `quote` is given; the header seeds come from
   * {@link convertBaselineFromDeal}, which mirrors the server's own copy field
   * for field, and the company-derived seeds are switched off so a company
   * default cannot overwrite what the deal actually holds.
   *
   * ⚠️ **THREE OF THE DEAL'S HEADER FIELDS ARE CARRIED BY THE SERVER AND CANNOT
   * BE SHOWN OR EDITED HERE, and that is a property of this form rather than an
   * omission.** `POST /deals/{id}/convert` copies the deal's `ownerId`,
   * `accountLeadId` and `projectId` onto the quote; this form has no
   * `FormValues` key for any of the three — see the notes on `ID_KEYS` — so
   * there is nothing to seed and nothing a payload built here could send. They
   * arrive correctly and invisibly. Do NOT add keys for them to "complete" the
   * prefill: a `projectId` or `ownerId` reachable by the diff emits an explicit
   * `null` on the next edit, and an explicit `null` CLEARS.
   */
  convertFrom?: Deal;
  /**
   * Whether the quote already has line items — or `'unknown'` when the caller
   * cannot tell.
   *
   * Same contract as the invoice form: the caller knows, the quote record does
   * not say — `GET /quotes` returns no line count. It flips the totals section
   * from four inputs to a read-only note, because on a quote with lines the
   * lines own the totals and anything typed here is overwritten by the next
   * line write. Defaults to `false`, which is the right answer for a create and
   * for the three Zoho imports that carry no lines at all.
   *
   * ⚠️ **`'unknown'` is treated exactly like `true`**, because an unknown line
   * count has to fail safe. Offering the inputs when the lines might own the
   * totals is the DESTRUCTIVE direction: a header PATCH writes literally what
   * it is sent and never re-derives, the next line write silently replaces it,
   * the replaced figures are kept nowhere — and `convert` copies all four onto
   * the invoice verbatim, so the disagreement is inherited rather than
   * corrected on the way across. Refusing the inputs merely sends the user to
   * the detail page, which knows the count and offers them there.
   */
  hasLineItems?: boolean | 'unknown';
}

/** Server-enforced maximums, mirrored so the control stops where the API would. */
const MAX_LENGTHS = {
  subject: 255,
  /*
   * THREE reference columns deliberately have NO entry here, and no entry in
   * `FormValues` either — `quoteNumber` (64), `xeroQuoteNumber` (50) and
   * `xeroQuoteId` (100). A maximum length is a property of a control, so the
   * absence of one is the tell that this form renders none. The three are
   * frozen for three DIFFERENT reasons, and flattening them loses the reason
   * each is unwritable:
   *
   * - **`quoteNumber` is allocated by the SERVER and is READ-ONLY on BOTH
   *   create and PATCH.** Measured on the wire 2026-08-27:
   *   `PATCH /quotes/9 {"quoteNumber":…}` → `422`, `path: "quoteNumber"`,
   *   *"allocated by the server"*. It was a hand-typed unique column until
   *   that day, and the input over it now produces nothing but a guaranteed
   *   refusal. See the render branch, which displays it and never offers it.
   * - **`xeroQuoteNumber` IS written by a successful push.** A hand-typed box
   *   over it competes with a real identifier rather than sitting over an
   *   empty column, and Xero re-numbers a quote on some edits, so the server
   *   reads it back on every push. It is a value to display, never a key.
   * - **`xeroQuoteId` is written by NOTHING.** No code populates that column.
   *   So `null` there says nothing whatever about whether the quote is in
   *   Xero — the real GUID lives in `f_xero_links` and arrives as the flat
   *   `id` on the link envelope. It is frozen because a hand-typed GUID over
   *   an unpopulated column looks authoritative to every later reader and
   *   points nothing anywhere.
   *
   * Following `ProductFormModal`: all three keys are absent from the form's
   * value type entirely, so `changedKeys` cannot reach them and no payload this
   * form builds can carry them. That is structural, not a guard someone can
   * forget — and on `quoteNumber` a guard was exactly what failed, because the
   * key stayed in `FormValues` and the edit branch went on sending it for
   * hours after the server started refusing it.
   */
  /** The CUSTOMER's PO number — the third reference on this form, and theirs. */
  reference: 255,
  /** `TEXT`. Long enough for real payment and validity wording. */
  terms: 16_000,
  /**
   * `CHAR(36)`. The value only ever comes from the picker, so this is a floor
   * under a stored value rather than something a user can trip over.
   */
  xeroBrandingThemeId: 36,
} as const;

/*
 * `DOCUMENT_LINE_AMOUNT_OPTIONS`, `toDocumentLineAmountType`,
 * `USE_COMPANY_DEFAULT`, `AMOUNT_PATTERN`,
 * `CURRENCY_PATTERN`, `TOTAL_KEYS`, `TOTAL_LABELS` and `UNCLEARABLE_TOTALS`
 * are all imported from `@/components/documents/document-form`, shared with
 * `InvoiceFormModal` — a local copy drifted once already. Do not re-declare
 * any of them.
 *
 * Two of those are worth restating at the call site because getting either
 * wrong is a 422 or a silent money change rather than a lint warning:
 *
 * - ⚠️ A **DOCUMENT**'s line-amount type takes `NOTAX`; a **COMPANY**'s
 *   `xeroSalesLineAmountType` takes `NONE`. Same meaning, different word.
 *   `LINE_AMOUNT_OPTIONS` from `@/lib/xero-tax-rates` is the company's enum
 *   and importing it here would be a 422 on every save that used it.
 * - ⚠️ `''` is not a fourth member of either enum. It is the ABSENCE of a
 *   value, stored as `null`, which means "fall back to the company's Xero
 *   sales default" — never "no tax", which is what `NOTAX` says. The same is
 *   true of a line's `xeroTaxType`: a tax CODE is not derivable from a
 *   percentage, because `ZERORATEDOUTPUT`, `NONE` and `EXEMPTOUTPUT` are all
 *   0% in this tenant.
 */

/** Every value is a string, so the edit baseline can be diffed key by key. */
interface FormValues {
  subject: string;
  /*
   * ⚠️ **There is deliberately no `quoteNumber` key here.** It is allocated by
   * the server and is read-only on create AND on PATCH, so its absence from
   * this interface is what makes the removal safe rather than merely invisible:
   * no payload this form builds can express it, and no 422 can name it. See
   * `MAX_LENGTHS` above.
   */
  stageId: string;
  contactId: string;
  companyId: string;
  dealId: string;
  /*
   * ⚠️ **There is deliberately no `projectId` key here.** The header project
   * picker was removed on the owner's instruction — "the project selection has
   * to be at an item level only and not at the main quote level" — and its
   * absence from this interface is what makes the removal safe rather than
   * merely invisible. See the note on `ID_KEYS` below.
   */
  /*
   * ⚠️ **There is deliberately no `ownerId` key here**, and its absence is
   * load-bearing in exactly the way `projectId`'s is. The owner asked for the
   * ACCOUNT LEAD in the Owner picker's place, and an account lead is derived
   * through the company server-side — so this form no longer offers a control
   * that could choose an owner.
   *
   * **Removing the CONTROL does not remove the WRITE**, which is why the key
   * came out of this interface rather than just out of the render. Two things
   * would have gone wrong had it stayed: on a CREATE the payload builder would
   * have sent whatever the invisible value held, and on any branch
   * `KNOWN_WRITE_KEYS` — derived from this very interface — would have gone on
   * claiming a 422 naming `ownerId` could be MARKED, routing it to an input
   * that is no longer on screen. That is the "one field needs attention,
   * highlighted below" report with nothing highlighted.
   *
   * The COLUMN is untouched and still writable elsewhere: `d_quotes.owner_id`
   * is real, `QuoteUpdateInput` still declares `ownerId`, and a quote that has
   * an owner keeps it — the diff never sees the key, so no save from this form
   * can move it. Re-adding it to `FormValues` starts writing it again with
   * nothing on screen saying what is being written.
   */
  issueDate: string;
  validTill: string;
  currency: string;
  /**
   * The exchange rate this quote is PINNED to, as an exact decimal string.
   *
   * ⚠️ **`''` is not a rate of 1.** It is the absence of a pin, stored as
   * `null`, and it means Xero applies its own daily rate when the document is
   * pushed. Defaulting an empty box to `1` would assert that the document
   * currency and the organisation's base currency are at parity — a claim
   * nobody made, on a document a customer is invoiced against — so nothing in
   * this form ever substitutes a figure for a blank. Same family of mistake as
   * `''` on `xeroLineAmountType` meaning "no tax" rather than "use the
   * company's default".
   *
   * A string end to end, like every other money value here: `DECIMAL(18,6)`
   * server-side, and `Number()` on it is a float round-trip. It is only ever
   * parsed inside `@/lib/currency-conversion`.
   */
  currencyRate: string;
  subTotal: string;
  discountTotal: string;
  taxTotal: string;
  grandTotal: string;
  reference: string;
  terms: string;
  xeroLineAmountType: string;
  xeroBrandingThemeId: string;
}

/**
 * Keys that carry a foreign key id.
 *
 * ⚠️ **`projectId` is NOT in this list, and its absence is load-bearing.**
 *
 * The owner removed the header project picker — "the project selection has to
 * be at an item level only and not at the main quote level, so remove from
 * there" — and a control coming off the screen does **not** stop a key going
 * out on the wire. Across this API an omitted key leaves a column untouched
 * while an explicit `null` CLEARS it, so a `projectId: null` emitted merely
 * because nothing fills the box would silently strip the project from every
 * quote that has one the first time anybody edited its subject. Quote 6 in
 * production carries one, and the `cities` derived through it would go with
 * it: a 200, no warning, nothing to restore from.
 *
 * That is closed **structurally, not by a guard**: `projectId` is not a
 * `FormValues` key, so it cannot appear in `changedKeys` (the PATCH loop), it
 * is not in this list, so the POST loop never reads it, and
 * `KNOWN_WRITE_KEYS` cannot claim a 422 naming it can be marked. There is no
 * check for a later reader to delete, because there is no check. Re-adding the
 * key to either list starts writing it again with nothing on screen saying
 * what is being written.
 *
 * The COLUMN is untouched and still means what it meant: since migration 067
 * the header project is not the authority for a line's cities or for its Xero
 * tracking — each LINE carries its own `projectId` and `cityIds` — but it is
 * still the **fallback for a quote no line of which names a project**. A quote
 * that already has one keeps it; a quote created here simply has none, and its
 * lines carry the projects instead.
 *
 * The quote's own `cities` are still DERIVED through that stored project and
 * are still not a field on a quote at all — there is no `cityIds` on this
 * resource, and the write schemas are `.strict()`, so sending one is a 422.
 */
const ID_KEYS = ['stageId', 'contactId', 'companyId', 'dealId'] as const;

/**
 * Keys that are plain nullable text — '' becomes null on the wire.
 *
 * ⚠️ *SUPERSEDED 2026-08-31, and the removal is the point: this list held
 * `xeroTrackingLocation` and `xeroTrackingTourType` "for their PLUMBING, not
 * for what their null means" — on those two, `null` was an instruction to
 * DERIVE rather than an absence.* **Both keys are gone from this form
 * entirely.** The derivation they overrode is untouched and still runs
 * server-side; what went is this form's ability to override it. See the note on
 * `FormValues`.
 */
const TEXT_KEYS = ['reference', 'terms', 'xeroBrandingThemeId'] as const;

/*
 * ⚠️ THE TOTALS GUARD lives across three places in this file and all three are
 * load-bearing: `changedKeys` drops a locked total, `buildPayload` refuses to
 * put one in either payload, and `validate` refuses an emptied `subTotal` or
 * `grandTotal`. `TOTAL_KEYS`, `TOTAL_LABELS` and `UNCLEARABLE_TOTALS` come
 * from the shared document vocabulary — this form had NO totals guard at all
 * while the invoice form did, which is the money bug those three now close.
 *
 * `sub_total` and `grand_total` are `NOT NULL DEFAULT 0.00`, so sending `null`
 * passes zod and then takes the column-default path: the figure is silently
 * rewritten to 0.00 rather than rejected. `discountTotal` and `taxTotal` ARE
 * nullable, and null there means "not recorded", never zero — clearing those
 * is honest and is allowed.
 */

/**
 * The co-validated date pair — `issueDate` / `validTill`, not the invoice's
 * `billDate` / `dueDate`. Same trap, different nouns.
 *
 * `issueDate > validTill` is a 422 reported at path `validTill`, but the server
 * only runs that check when BOTH dates are present in the same body. A PATCH
 * that moves only `issueDate` past a stored `validTill` therefore succeeds and
 * leaves the quote inconsistent — and a quote that stops being valid before it
 * was raised is nonsense the conversion would inherit. So whenever either one
 * changed, the payload carries both: the server only re-checks the pair when it
 * can see the pair.
 */
const DATE_KEYS = ['issueDate', 'validTill'] as const;

/**
 * The header project's cities on a CREATE — empty, permanently.
 *
 * A quote raised from this form carries no header project (see `ID_KEYS`), so
 * there is no set to derive and `[]` is the true answer rather than a
 * placeholder. Hoisted to module scope so the identity is stable across
 * renders: a fresh `[]` in the JSX is a new prop value every time, which is a
 * needless invalidation in whatever the grid memoises on it.
 */
const NO_HEADER_CITIES: ProjectCity[] = [];

/**
 * What Save says when the line half of an edit did not go through.
 *
 * ⚠️ **NEITHER SENTENCE REPEATS WHAT THE GRID ALREADY SAID.** The grid renders
 * the refusal on the row it belongs to, in the server's own words; a second
 * account of it up here would be a paraphrase competing with the real one. What
 * these add is the part the grid cannot know — whether anything ELSE was
 * written — and where to look.
 *
 * ⚠️ **The second is the one that matters and the one that is easy to get
 * wrong.** "Nothing was saved" over a header PATCH that returned 200 is a lie a
 * person acts on: they re-enter details that are already stored, or abandon a
 * record believing it untouched. The house rule for the two-phase create is the
 * same rule — say which half landed.
 */
const LINES_NOT_SAVED =
  'The line items were not saved — see the report on the rows above.';
const HEADER_SAVED_LINES_NOT =
  'The quote’s details were saved. Its line items were not — see the report on the rows above.';

/**
 * How long a new quote is valid for, in days.
 *
 * The owner's default, and it is a DEFAULT rather than a rule: the Valid till
 * box is an ordinary editable date and the server enforces nothing but
 * `issueDate <= validTill`. Named rather than inlined so the one place it is
 * decided is greppable — and so nobody reads a bare `30` beside a date and
 * wonders whether it is days, a payment term, or a percentage.
 */
const DEFAULT_VALID_FOR_DAYS = 30;

/**
 * The blank baseline with the two date defaults applied. **CREATE ONLY.**
 *
 * ⚠️ Both days come from {@link todayIsoDate} / {@link addDaysIso} and never
 * from `new Date()` — see the note on those imports. A UTC-vs-local slip here
 * dates a quote a day wrong for a whole hemisphere, silently, on a document a
 * customer receives.
 *
 * Applied in the `useState` initialiser and nowhere else, exactly like
 * {@link withPrefill}: React runs it once, so no effect can come back and
 * re-assert a date under somebody's cursor. On an EDIT it is not called at all
 * — a stored `issueDate` is the record's own, and a stored blank `validTill` is
 * a deliberate open-ended quote rather than a field waiting to be filled.
 */
function withDefaultDates(base: FormValues): FormValues {
  const issueDate = todayIsoDate();
  return { ...base, issueDate, validTill: addDaysIso(issueDate, DEFAULT_VALID_FOR_DAYS) };
}

/**
 * The stage a NEW quote starts at, found in the loaded master by its own name.
 *
 * ⚠️ **No id is hard-coded, and none can be.** Stages are a reference master an
 * admin edits — `f_quote_stages` — so an id that is Draft in this database is
 * something else in the next one, and seeding a number would eventually put a
 * quote straight into `Accepted`, which raises an invoice.
 *
 * ⚠️ **There is no `isDraft` flag to read.** `QuoteStage` carries `isAccepted`
 * and `isDeclined` (migration 080) and nothing for Draft, so the row's NAME is
 * the only identity available. That is the same fallback `quote-utils` uses for
 * the two flagged stages when the flags are absent, through the same
 * normaliser — and if the backend ever adds `isDraft`, this function's body is
 * the single place that changes.
 *
 * ⚠️ **Matched EXACTLY after normalising, never by containment.**
 * `name.includes('draft')` is the mistake `quote-utils` records against the
 * acceptance sets: it would match a `Draft — do not send` or a
 * `Redrafting` row an admin adds later, and a wrong default stage is a wrong
 * state on every quote raised afterwards.
 *
 * Retired rows are skipped: `selectableStages` does not offer an inactive stage
 * on a create, so seeding one would put a value in the form that the picker
 * renders as blank — which reads as "no stage" over a field that is carrying
 * one.
 *
 * `null` when the list has not arrived, when it failed, or when this
 * organisation simply has no Draft row. The caller leaves the field unset for
 * all three: guessing at another stage is how a quote gets created as `Sent`.
 */
function draftStageOf(stages: readonly QuoteStage[]): QuoteStage | null {
  return stages.find((stage) => stage.isActive && normaliseStatusName(stage.name) === 'draft') ?? null;
}

function toFormValues(quote?: Quote): FormValues {
  return {
    subject: quote?.subject ?? '',
    /*
     * ⚠️ No `quoteNumber` baseline either, for the same reason there is no
     * `projectId` one below. Seeding it here would put the stored number back
     * into `values`, and from there into `changedKeys` the moment anything
     * touched it — which is a guaranteed 422 now that the server allocates it.
     * The stored number is read straight off `quote.quoteNumber` where it is
     * displayed, and never mirrored into form state.
     */
    stageId: quote?.stage ? String(quote.stage.id) : '',
    contactId: quote?.contact ? String(quote.contact.id) : '',
    companyId: quote?.company ? String(quote.company.id) : '',
    dealId: quote?.deal ? String(quote.deal.id) : '',
    /*
     * ⚠️ No `projectId` baseline either. Seeding one here would put the stored
     * project back into `values`, and from there into `changedKeys` the moment
     * anything cleared it — which is the exact write the removal exists to
     * prevent. The stored project is read straight off `quote.project` where
     * it is displayed, and never mirrored into form state.
     */
    /*
     * ⚠️ No `ownerId` baseline either, for the same reason there is no
     * `projectId` one above. Seeding it here would put the stored owner back
     * into `values`, and from there into `changedKeys` the moment anything
     * cleared it — which is the exact write the removal exists to prevent. The
     * quote's OWNER is no longer shown by this form at all; what is shown is
     * its resolved `accountLead`, read straight off the record where it is
     * displayed and never mirrored into form state, because **this form does
     * not write a lead** — the owner's instruction, not a server refusal.
     *
     * (This said "because it is read-only server-side". ⚠️ FALSE since
     * migration 082: `accountLeadId` is on the quote write schema and `null`
     * there means DERIVE. See the Account lead field for the correction.)
     */
    issueDate: quote?.issueDate ?? '',
    validTill: quote?.validTill ?? '',
    currency: quote?.currency ?? '',
    /*
     * `null` becomes '' for the same reason every other nullable does — the
     * control holds a string — and here the two genuinely mean the same thing:
     * no rate is pinned. It must never become '1'.
     */
    currencyRate: quote?.currencyRate ?? '',
    subTotal: quote?.subTotal ?? '',
    discountTotal: quote?.discountTotal ?? '',
    taxTotal: quote?.taxTotal ?? '',
    grandTotal: quote?.grandTotal ?? '',
    reference: quote?.reference ?? '',
    terms: quote?.terms ?? '',
    /*
     * A NEW quote starts at `EXCLUSIVE`; an existing one keeps whatever it
     * stores, and a stored `null` still means "use the company's Xero sales
     * default" and still renders as `''`.
     *
     * ⚠️ The two halves are deliberately different, and the ternary rather
     * than a `??` is the point. Seeding an existing document would rewrite a
     * tax treatment already printed on a customer's PDF; seeding a new one is
     * the owner's decision that every quote and invoice is tax-exclusive.
     */
    xeroLineAmountType: quote ? (quote.xeroLineAmountType ?? '') : 'EXCLUSIVE',
    xeroBrandingThemeId: quote?.xeroBrandingThemeId ?? '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ **These are the keys the form SENDS.** A 422's `path` is a request key,
 * and on this resource every one of them is also a `FormValues` key — so the
 * list is derived from `toFormValues()` and cannot drift from the controls.
 * There is deliberately no `projectId`, no `cityIds` and no `quoteNumber` in
 * it. This form no longer chooses a project — that lives on the line items now
 * — a quote's cities are derived through whatever project it holds rather than
 * written, and the number is allocated by the server. None of the three is ever
 * sent, so none can come back on a 422 `path`; such a path could only ever be
 * something this form cannot mark. Every write schema here is `.strict()`
 * besides.
 *
 * ⚠️ `quoteNumber` is the one that shows why the list must be DERIVED rather
 * than hand-written. When the server made it read-only, dropping it from
 * `FormValues` removed it from here in the same edit; a hand-maintained list
 * would have gone on claiming a 422 naming it could be marked, at a control
 * that no longer exists.
 *
 * Anything outside the list goes to the form-level banner, because a path can
 * be non-empty and still bind to nothing — which is the worse case, since it
 * looks bindable: it is routed to an input that does not exist, vanishes from
 * the banner, and still inflates the "fields need attention" count.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

/**
 * Keys that exist on an EDIT and are not rendered — and not sent — on a CREATE.
 *
 * 🔴 **EMPTY as of 2026-08-31, and deliberately kept.** *SUPERSEDED: "The two
 * keys that exist on an EDIT and are not rendered … the two Xero tracking
 * overrides — they are a repair for a quote whose derived tracking is wrong, and
 * there is nothing to repair on a quote that does not exist yet."* Those two were
 * its only members, and **the controls are gone from this form altogether** — so
 * they are out of `FormValues`, which is the stronger closure this list could
 * only ever approximate. There is nothing left to suppress.
 *
 * ⚠️ **The empty array is the finding, in the shape of `hasUnsentChanges()`'s
 * empty parameter list.** It is not an oversight and not dead code awaiting
 * deletion: `knownWriteKeys()` and `buildPayload` still consult it, so the next
 * key that must exist on an edit and not on a create has one place to be named
 * and three enforcement sites already wired. Rebuilding this later is where
 * somebody wires two of the three.
 *
 * ⚠️ **`quoteNumber` is NOT a member of this list, and must not become one** —
 * checked again on the pass that emptied it, because an empty list is exactly
 * where a plausible-looking third key gets added. It is read-only on create AND
 * on PATCH, so suppressing it on one branch would leave the edit branch sending
 * a guaranteed 422. A field the server owns comes out of `FormValues` entirely,
 * not out of one branch's key list — and it did.
 *
 * ⚠️ **It is a list of KEYS rather than checks scattered about**, because a
 * member has to be true in three places at once and they must not drift: the
 * control is not rendered, `buildPayload` does not send it, and
 * `knownWriteKeys()` does not claim a 422 naming it can be marked — a form that
 * reports "one field needs attention, it is highlighted below" with nothing
 * highlighted, because the path was routed to an input that is not on screen.
 *
 * ⚠️ **`currencyRate` is deliberately NOT in this list.** The currency picker
 * renders on a create, so a quote can be raised in a currency other than the
 * organisation's base one from the outset — and the rate is pinned against the
 * document, not discovered later. Its state is shown and its dialog reachable
 * on both branches, so a 422 naming it has somewhere on screen to land.
 */
const CREATE_SUPPRESSED_KEYS: readonly string[] = [];

/**
 * This app's own refusal for the company on a NEW quote — `null` when there is
 * nothing to refuse.
 *
 * Owner's rule, 2026-08-31: **a new quote requires a company.** Create-time
 * only; see the call site in `validate`, which also excludes the CONVERT path.
 *
 * ⚠️ **THE REQUIRED-COMPANY HALF REALLY IS OURS, and it is all that is left
 * here.** `companyId` is genuinely nullable server-side, and the approval guard
 * SKIPS a document that names no company at all — so nothing on the API refuses
 * a company-less quote and this sentence is the only thing that does.
 *
 * ⚠️ **THE APPROVAL HALF WAS NEVER OURS AND HAS BEEN REMOVED — corrected
 * 2026-08-31.** *SUPERSEDED, and it was written into this comment as a fact:
 * "`POST /quotes` … accepts one against a company in any approval state".* It
 * does not. `assertCompanyApproved` (migration 099) is called from
 * `createQuote` and throws a **409** carrying one of three codes and a sentence
 * strictly better than the two invented here — it NAMES the company, says an
 * approver has been emailed a link, and says everything else on the company
 * still works. See `billingGateRefusalOf` in `@/lib/company-approval`, which is
 * where that refusal is now read, and `reportFailure`, which banners it.
 *
 * > A client rule and a server rule wording the same refusal is two sentences
 * > about one fact, and the client's is the one that goes stale — silently,
 * > because it renders perfectly either way.
 */
function newQuoteCompanyError(companyId: string): string | null {
  if (companyId.trim() === '') {
    return 'Choose a company — a new quote has to be raised against one.';
  }
  return null;
}

/**
 * The Combobox footnote reporting companies withheld from the list because they
 * are not approved — `undefined` when none were.
 *
 * ⚠️ It exists because a company somebody can see on `/companies` and cannot
 * find in this dropdown reads as a broken fetch, and they go hunting. A narrowed
 * list that says nothing is the silent failure this codebase already records
 * against retired picker options.
 *
 * ⚠️ It says the company is not APPROVED, never that it does not exist. The
 * wording covers `pending` and `rejected` alike without claiming which, because
 * the list cannot say which without naming the company — and the count is the
 * part that stops the hunt.
 */
function withheldCompanyNote(withheld: number): string | undefined {
  if (withheld <= 0) return undefined;
  return withheld === 1
    ? '1 company is not listed — a quote can only be raised against an approved company.'
    : `${withheld} companies are not listed — a quote can only be raised against an approved company.`;
}

/** The subset of {@link KNOWN_WRITE_KEYS} this form is actually rendering. */
function knownWriteKeys(isEdit: boolean): readonly string[] {
  if (isEdit) return KNOWN_WRITE_KEYS;
  return KNOWN_WRITE_KEYS.filter(
    (key) => !CREATE_SUPPRESSED_KEYS.includes(key),
  );
}

/**
 * What actually happened when the draft lines were written after a create.
 *
 * ⚠️ **This type exists because the two halves of one Save are two different
 * outcomes, and there is no transaction across them.** `POST /quotes` and each
 * `POST /quotes/{id}/products` are separate requests; the quote can be created
 * and a line still be refused. Nothing here rolls anything back, because
 * nothing here CAN — deleting a quote to undo a failed line would destroy the
 * work that succeeded, and describing it as "rolled back" when it was not is
 * the failure the owner explicitly ruled out.
 *
 * So the outcome is recorded per line and reported by position, and the form
 * stops offering to create a quote the moment one exists.
 */
/**
 * Where the push that follows a CREATE got to.
 *
 * ⚠️ **NOT ONE OF THESE MAY BE CONFUSED WITH THE SAVE, which has already
 * succeeded in every single case.** By the time any of them exists the quote
 * is in the database with an id, and on four of the six Xero has nothing. The
 * whole reason this is a discriminated union rather than an error string is
 * that the four failures send a reader to four different places, and the copy
 * that would cover all of them — "could not save" — is the one sentence that is
 * false in every one.
 *
 * - `sent` — Xero took it.
 * - `skipped-cooldown` — **nothing was sent, on purpose.** Xero's shared call
 *   budget is spent, so the request was not made rather than made and refused.
 *   See the pre-check for why that is permanent behaviour and not a workaround.
 * - `company-not-linked` — the ORDINARY answer on a fresh dataset. Worded as a
 *   state to fix on the company record, never as an error about the quote.
 * - `rate-limited` — the same fact as `skipped-cooldown` arriving from the
 *   server instead of from the local deadline. Transient, and the only thing to
 *   do is come back later.
 * - `refused` — anything else Xero or the API said, in the server's own words.
 * - `unreachable` — no answer at all. ⚠️ **Nothing about Xero's copy is known**;
 *   the request may have landed. No sentence here may describe its state.
 */
type CreatePushOutcome =
  | { kind: 'sent' }
  /*
   * ⚠️ **The SERVER decided not to push, and that is ORDINARY.** A quote with no
   * lines yet, or one whose company nobody has linked, is a normal draft — the
   * server checks both BEFORE calling Xero so a person who has done nothing
   * wrong does not cost the tenant a call to be refused. Rendering this as an
   * error alarms every new quote in the system, which is why it is its own kind
   * and not folded into `refused`.
   */
  | { kind: 'not-sent-yet'; reason: string }
  /*
   * The two cooldown cases carry SECONDS, not a formatted string.
   * `xeroCooldownNotSentMessage` is the one place that turns a wait into a
   * sentence, and re-deriving a duration here — or storing a string that goes
   * stale the moment it is held — is the drift that module exists to stop.
   */
  | { kind: 'skipped-cooldown'; seconds: number }
  | { kind: 'company-not-linked'; companyId: number | null; companyName: string | null }
  | { kind: 'rate-limited'; seconds: number }
  | { kind: 'refused'; messages: string[] }
  | { kind: 'unreachable' };

/**
 * The SERVER's own account of the push it made during the save, as the outcome
 * this form renders.
 *
 * ⚠️ **`null` means SAY NOTHING, and it is an ordinary answer.** A server
 * predating the `xeroPush` contract omits the key, and so does a rollback —
 * every reader has to render exactly as it did before the key existed. Turning
 * an absent report into "not sent" would be this form asserting something about
 * SEED's accounting system that it was never told.
 *
 * ⚠️ **NOTHING HERE MAY BE WORDED AS THE SAVE HAVING FAILED.** The quote is
 * stored on every branch below — the response that carried this report is the
 * response that stored it. Xero does not enforce invoice-number uniqueness, so
 * somebody who reads a refused push as "my save was lost" and saves again is the
 * path to two live documents; leading with the record having been saved is the
 * control, not a preference. `CreatePushNote` is where that wording lives.
 *
 * `skipped` is deliberately NOT mapped onto `refused`: see the
 * {@link CreatePushOutcome} member.
 */
function createPushFromServer(
  report: XeroPushReport | undefined,
  saved: Quote,
): CreatePushOutcome | null {
  if (report === undefined) return null;
  if (report.status === 'pushed') return { kind: 'sent' };
  if (report.status === 'skipped') return { kind: 'not-sent-yet', reason: report.reason };
  if (report.status === 'failed') {
    /*
     * ⚠️ The two special-cased codes keep the sentences they already had, so a
     * refusal reads identically whether a button produced it or a save did.
     * `XERO_COMPANY_NOT_LINKED` in particular is a state to fix on the COMPANY
     * record and must never read as an error about the quote.
     */
    if (report.code === 'XERO_COMPANY_NOT_LINKED') {
      return {
        kind: 'company-not-linked',
        companyId: saved.company?.id ?? null,
        companyName: saved.company?.name ?? null,
      };
    }
    return { kind: 'refused', messages: xeroErrorLines([report.message], report.message) };
  }
  /*
   * A status this build has never heard of. Saying nothing is the only honest
   * rendering — guessing which known status a newer one resembles is how a
   * refusal comes to be reported as a success.
   */
  return null;
}

interface DraftLineOutcome {
  /** How many lines were sent. Never zero — no outcome is recorded otherwise. */
  attempted: number;
  /** How many came back 2xx. */
  saved: number;
  /**
   * The `xeroPush` from the LAST line write that succeeded, or `undefined`.
   *
   * ⚠️ **The last one, not the first, and not a merge of them.** Every line
   * write pushes the whole document again, so each report supersedes the one
   * before it — the earlier ones describe a quote that was still missing lines.
   * Merging them would invent a state no single request ever observed.
   *
   * ⚠️ **`undefined` is "no report", never "not pushed".** A server predating
   * the `xeroPush` contract omits the key entirely, and so does a rollback; a
   * reader that turned an absent key into a failure would alarm every user of
   * an older build. {@link createPushFromServer} answers `null` for it, which is
   * this form's word for *say nothing*.
   */
  lastXeroPush: XeroPushReport | undefined;
  /**
   * The ones that did not, by their **1-based position in the grid as it was
   * submitted**.
   *
   * Position rather than id, because a line that was refused HAS no id — and
   * position rather than product name, because this form holds no product
   * directory and inventing one request to label an error message would be a
   * second thing to go wrong. The report says the position was not created and
   * is not in the list below, which is the fact that stops someone assuming it
   * is there.
   */
  failures: { position: number; message: string }[];
}

/**
 * Why one line was refused, in a sentence a person can act on.
 *
 * ⚠️ **Not `error.message`.** On a 422 that string is only the generic
 * "Validation failed" — the sentence naming the key that was actually refused
 * lives in `details`, and on a quote line that is where every useful refusal
 * is: the three `cityId` sentences, the `projectId` one, the strict-schema
 * rejections. A report that said "Line 3 was not created: Validation failed"
 * would be worse than the raw error.
 *
 * Everything is form-level here **by construction**: this form renders no
 * control for any line key, so there is nothing on screen for a path to bind
 * to and every part of the refusal has to be spelled out in the text. The key
 * is prefixed exactly as `ApiError.routeFieldErrors` does, and skipped when the
 * message already names it, so the app words an unbindable path one way.
 */
function lineFailureMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return 'The server could not be reached, so this line was not sent.';
  }
  const parts = [...caught.formMessages];
  for (const [key, message] of Object.entries(caught.fieldErrors)) {
    parts.push(message.includes(key) ? message : `${key}: ${message}`);
  }
  return parts.length > 0 ? parts.join(' ') : caught.message;
}

export function QuoteFormModal({
  open,
  onClose,
  quote,
  onSaved,
  onCreatedPreview,
  onXeroPush,
  onLinesSaved,
  lockedCompany,
  prefill,
  convertFrom,
  hasLineItems = false,
}: QuoteFormModalProps) {
  if (!open) return null;
  return (
    /*
     * The locked company belongs in the KEY, not only in the props: it seeds
     * state at mount, so a second open for a different company has to start a
     * fresh form rather than quietly keep the first one's seeded value.
     *
     * ⚠️ The PREFILL is in the key for exactly the same reason, and it is the
     * one that bites: a deal page opens this to raise a quote, the person
     * cancels, and the app navigates to another deal. Same mounted modal, new
     * seed — and without the key the second open would show the FIRST deal's
     * name, company and contact, which is a wrong document raised silently
     * rather than an obviously broken form. Keyed on the ids, never the names:
     * a name is only ever rendered, and remounting when one sharpens would
     * throw away whatever had already been typed.
     */
    <QuoteForm
      key={quote?.id ?? createKeyOf(lockedCompany, prefill, convertFrom)}
      onClose={onClose}
      quote={quote}
      onSaved={onSaved}
      onCreatedPreview={onCreatedPreview}
      onXeroPush={onXeroPush}
      onLinesSaved={onLinesSaved}
      lockedCompany={lockedCompany}
      prefill={prefill}
      convertFrom={convertFrom}
      hasLineItems={hasLineItems}
    />
  );
}

/** The remount key for a CREATE — see the note at the call site. */
function createKeyOf(
  lockedCompany: { id: number } | undefined,
  prefill: QuoteFormPrefill | undefined,
  convertFrom: Deal | undefined,
): string {
  /*
   * ⚠️ FIRST, and it is the one that matters most. This modal is mounted once
   * on a deal page; converting deal 12, cancelling, and navigating to deal 13
   * keeps the same component, and without the deal in the key the second open
   * would carry the FIRST deal's baseline while `runConvert` posted against the
   * second deal's id. That is a quote built from one deal and billed against
   * another, raised silently rather than obviously broken.
   */
  if (convertFrom) return `convert:${convertFrom.id}`;
  /*
   * 🔴 **THE LOCK AND THE SEED COMPOSE — 2026-09-13.** *SUPERSEDED: a lone
   * `if (lockedCompany) return 'new:' + lockedCompany.id;` sitting immediately
   * above `if (!prefill) return 'new';`.* That was not arbitrary — the lock
   * genuinely does seed state at mount, so it does belong in the key — but as a
   * SHORT-CIRCUIT it **answered the whole question on its own and made the line
   * fingerprint below UNREACHABLE for every caller that passes a lock.** And a
   * lock beside seeded lines is not a hypothetical pairing: it is precisely what
   * `CombineDocumentFlow` passes, on the path the fingerprint was written for.
   *
   * ⚠️ **It was masked, not absent.** `SalesTrackerPage` mounts that flow only
   * while `combining !== null` and this modal returns `null` when closed, so
   * there is a fully unmounted render between opens and the key is never
   * compared. The defect becomes reachable the moment a caller keeps the flow
   * mounted across opens, or lets a chooser confirm a second, different basket
   * for the same company with no close in between — which is the shape of the
   * company-projects flow. The symptom is the one this whole function exists to
   * prevent: the second raise silently reuses the FIRST mount's basket, a valid
   * document, a `201`, and lines the person did not choose.
   *
   * ⚠️ **`InvoiceFormModal` and `DealFormModal` were already composing** — the
   * deal form's own `createKeyOf` doc says the quote form reads the lock
   * "because there it IS the whole key", which described exactly the
   * short-circuit. **The asymmetry between the three forms was the tell**, and
   * the quote form was the odd one out.
   *
   * ⚠️ **WHICH DIRECTION EACH TERM GUARDS**, because a remount key is wrong in
   * two opposite ways and only one of them is visible:
   *   - **`convertFrom`** keeps its own branch: a conversion baseline is a
   *     different DOCUMENT, not a seed over a blank one, so it shares no terms
   *     with the rest and composing it would only invent segments that are
   *     always empty.
   *   - **the lock** guards against a second open for a DIFFERENT COMPANY
   *     keeping the first one's seeded `companyId`. Omit it and the form serves
   *     a stale company.
   *   - **the seed terms** guard against a second open for the same company
   *     with a DIFFERENT basket, deal or contact. Omit them — which is what the
   *     short-circuit did — and the form serves a stale basket.
   *   - **nothing here is keyed on a NAME**, in either direction: a name is only
   *     ever rendered, so remounting when one sharpens would throw away typed
   *     work for no change in what the form means.
   *
   * ⚠️ **`'new'` is still the answer for a create with NEITHER a lock nor a
   * seed**, byte-identical to before, so no caller that seeds nothing changes
   * key or remounts for a formatting decision.
   */
  if (!lockedCompany && !prefill) return 'new';
  /*
   * ⚠️ **THE LINES ARE IN THE KEY, AND WITHOUT THEM THE PLANNER IS BROKEN.**
   * Two planned rows for the same company agree on every other field here, so
   * the key would be identical and the second open would carry the FIRST plan's
   * basket — a quote built from one plan and raised against another, silently.
   * That is the same failure the `convertFrom` clause above exists to prevent.
   *
   * ⚠️ **Keyed on the CONTENT, never on the array's identity.** A parent maps
   * `plannedProductLines(row)` inline, so the array is a fresh object on every
   * render; keying on identity would remount the form on every keystroke and
   * throw away whatever had been typed.
   *
   * 🔴 **THE PLANNED AMOUNT JOINED THE FINGERPRINT ON 2026-09-03.**
   * *SUPERSEDED: "Product and quantity are the only two things a seed carries,
   * so they are the whole of the fingerprint."* That was true when written and
   * stopped being true the moment a planned line could carry money: a seed now
   * also carries a price, derived from the plan amount.
   *
   * ⚠️ **Without it, two plans for the same company selling the same products
   * in the same quantities at DIFFERENT AMOUNTS produce an identical key** — so
   * the second one opened reuses the first mount and the grid carries the FIRST
   * plan's prices, on a document somebody then sends. Exactly the failure the
   * paragraph above describes, one field further down, and it arrived because
   * the sentence naming the fingerprint complete was left standing while the
   * thing it described grew.
   *
   * `plannedAmount` rather than the derived `unitPrice`, because it is what the
   * plan actually says: two plans differing only in quantity already differ in
   * the quantity term, and the amount is the figure a person changed.
   *
   * 🔴 **AND THE PROJECT JOINED THE FINGERPRINT ON 2026-09-11, for the third
   * time this paragraph has had to grow.** A seed may now name a project, and
   * the sales-tracker board is a grid of `(project, company)` pairs — so **two
   * cards in the SAME COLUMN of the same row's company, or the same company
   * chased on two projects, are exactly the pair that agrees on every other
   * term here.** Without it the second card opened reuses the first mount and
   * the grid carries the FIRST project, on a document somebody then sends:
   * correct-looking, billed to the right customer, filed against the wrong
   * engagement, and the board's `(company, project)` sync then advances the
   * wrong card.
   *
   * ⚠️ **The pattern is the finding, not the field.** This sentence has now
   * been left standing past its own subject twice — once when a seed gained a
   * price and once when it gained a project. **Any member added to
   * `PlannedProductLine` that a person can differ on belongs here**, and the
   * failure is always the same: a stale mount, a `201`, and nothing wrong on
   * screen.
   */
  const lines = (prefill?.lines ?? [])
    .map(
      (line) =>
        `${line.productId}x${line.quantity}@${line.plannedAmount ?? ''}#${line.project?.id ?? ''}`,
    )
    .join(',');
  /*
   * ⚠️ **EVERY SEGMENT IS EMITTED EVEN WHEN EMPTY, and the separators are what
   * keep the terms from bleeding into one another.** A lock with no seed reads
   * `new:782:seed::::`, a seed with no lock `new::seed:...`. Dropping an empty
   * segment would let two different opens collapse onto one string — which is
   * the whole failure this function exists to prevent, reintroduced as tidying.
   */
  return `new:${lockedCompany?.id ?? ''}:seed:${prefill?.deal?.id ?? ''}:${
    prefill?.company?.id ?? ''
  }:${prefill?.contact?.id ?? ''}:${lines}`;
}

/**
 * The seed, applied to a blank baseline. Create only — the one caller is the
 * `useState` initialiser, which is what keeps this from ever fighting the user.
 *
 * An absent key seeds nothing. `''` on `subject` is respected as a deliberate
 * blank rather than treated as absent, because the caller had to write it.
 */
function withPrefill(base: FormValues, prefill: QuoteFormPrefill | undefined): FormValues {
  if (!prefill) return base;
  const next = { ...base };
  if (prefill.subject !== undefined) next.subject = prefill.subject;
  if (prefill.company) next.companyId = String(prefill.company.id);
  if (prefill.contact) next.contactId = String(prefill.contact.id);
  if (prefill.deal) next.dealId = String(prefill.deal.id);
  // Uppercased here as `onCurrencyChange` uppercases what a person picks: a
  // code stored in lower case on an older record must not read as a different
  // currency from the same code chosen in the picker.
  if (prefill.currency !== undefined) next.currency = prefill.currency.toUpperCase();
  return next;
}

/**
 * The header values `POST /deals/{id}/convert` is ABOUT TO WRITE, as form
 * strings — the convert BASELINE, and nothing else.
 *
 * ⚠️ **IT REPRODUCES THE SERVER'S OWN COPY, FIELD FOR FIELD — that is the whole
 * specification**, exactly as `quote-to-invoice.ts` is for the other conversion.
 * Every value below was read out of `insertQuoteFromDeal` in the backend's
 * `deal-convert.ts`: the deal's NAME becomes `subject`, and `company_id`,
 * `contact_id`, `currency` and the Draft stage come across with it. A baseline
 * that quietly differs from what convert writes does not merely look wrong — it
 * decides what the follow-up PATCH sends, so a field seeded to something the
 * server never wrote is re-sent needlessly, and a field seeded to something the
 * server DID write is silently never corrected.
 *
 * ⚠️ **`ownerId`, `accountLeadId` and `projectId` are copied by the server and
 * are absent from `FormValues`**, so they are absent here too. See the note on
 * `convertFrom` — they arrive on the quote and this form neither shows nor
 * writes them.
 *
 * ⚠️ **`xeroLineAmountType` is `''` rather than the create default
 * `'EXCLUSIVE'`.** Convert's INSERT names no `xero_line_amount_type` at all, so
 * the stored value is `null` — "use the company's Xero sales default". Seeding
 * the create default into the BASELINE would make the form show `EXCLUSIVE`
 * over a quote that is going to store `null`, with nothing sent to close the
 * gap. It is seeded into the VALUES instead (see
 * {@link convertValuesFromDeal}), where it differs from the baseline and is
 * therefore actually written.
 *
 * ⚠️ **The four totals stay `''`.** The server recomputes all four from the
 * lines it has just copied, in the same transaction, and returns them — so
 * anything seeded here would be a figure the header claims and its lines do
 * not justify.
 */
function convertBaselineFromDeal(deal: Deal, draftStageId: string): FormValues {
  return {
    ...toFormValues(),
    xeroLineAmountType: '',
    subject: deal.dealName,
    dealId: String(deal.id),
    companyId: deal.company ? String(deal.company.id) : '',
    contactId: deal.contact ? String(deal.contact.id) : '',
    // Uppercased for the same reason `withPrefill` uppercases: a code stored in
    // lower case on an older deal must not read as a different currency from
    // the same code chosen in the picker.
    currency: deal.currency === null ? '' : deal.currency.toUpperCase(),
    stageId: draftStageId,
  };
}

/**
 * The convert baseline plus the three values this FORM asserts on top of it.
 *
 * ⚠️ **Every field this adds differs from the baseline on purpose, and that is
 * how it reaches the server at all.** The follow-up PATCH carries exactly the
 * keys that differ, so a seed that matched the baseline would be shown to the
 * person and then never written — the failure this file already records against
 * a form promising a value it does not send.
 *
 *  - **`issueDate` is today**, from {@link todayIsoDate} and never `new Date()`.
 *    Convert writes no issue date — `d_quotes.issue_date` is `DEFAULT NULL` —
 *    and a quote that records no day it was raised is not what anybody converts
 *    a deal expecting.
 *  - **`validTill` is the DEAL'S CLOSING DATE where it has one**, falling back
 *    to issue date + {@link DEFAULT_VALID_FOR_DAYS}. ⚠️ A closing date already
 *    in the past makes `issueDate > validTill`, which `validate` refuses at
 *    `validTill` BEFORE anything is created — a visible, fixable box on a form
 *    where nothing yet exists, rather than a 422 against a quote that does.
 *  - **`xeroLineAmountType` is `'EXCLUSIVE'`**, the same default a quote
 *    created from scratch takes. See {@link convertBaselineFromDeal}.
 */
function convertValuesFromDeal(baseline: FormValues, deal: Deal): FormValues {
  const issueDate = todayIsoDate();
  return {
    ...baseline,
    issueDate,
    validTill: deal.closingDate ?? addDaysIso(issueDate, DEFAULT_VALID_FOR_DAYS),
    xeroLineAmountType: 'EXCLUSIVE',
  };
}

/**
 * The follow-up write a CONVERT needs, and nothing more.
 *
 * ⚠️ **A bound, not merely `changedKeys`.** It is what stops this form ever
 * naming a key that is a 422 on a quote, because a single such key fails the
 * WHOLE call — and the call in question is the one carrying the user's edits
 * onto a document that already exists.
 *
 * ⚠️ *SUPERSEDED 2026-08-31: "`xeroTrackingLocation` and `xeroTrackingTourType`
 * are deliberately ABSENT. They are {@link CREATE_SUPPRESSED_KEYS} …"* Both keys
 * are now absent from `FormValues` itself, so they have the **stronger** closure
 * described in the next paragraph and need no mention in this list at all.
 *
 * ⚠️ **`quoteNumber`, `projectId`, `ownerId`, `accountLeadId` and the two Xero
 * tracking overrides cannot appear here because they are not `FormValues` keys
 * at all** — the stronger closure, and the one several separate notes in this
 * file exist to keep.
 */
const CONVERT_FOLLOW_UP_PATCH_KEYS = [
  'subject',
  'stageId',
  'contactId',
  'companyId',
  'dealId',
  'issueDate',
  'validTill',
  'currency',
  'currencyRate',
  'subTotal',
  'discountTotal',
  'taxTotal',
  'grandTotal',
  'reference',
  'terms',
  'xeroLineAmountType',
  'xeroBrandingThemeId',
] as const satisfies readonly (keyof QuoteUpdateInput)[];

/**
 * The one label a LOCKED field shows when it has nothing to show.
 *
 * ⚠️ **An empty read-only box is a CLAIM, and the wrong one.** It reads as a
 * field somebody left blank, where the truth is a deal that names no company or
 * records no currency. It is the same word the Currency Combobox already offers
 * as its empty option, held once so the locked render and the picker cannot come
 * to disagree about how "nothing" is spelled.
 */
const NOT_SET = 'Not set';

/*
 * ⚠️ **`CONVERT_FIELD_FROM_DEAL` AND `CONVERT_FIELD_TAX` ARE GONE — owner's
 * stricter copy pass, 2026-09-05.** They were `Field` hints reading "From the
 * deal" and "Set by the conversion", beside the read-only Company, Currency and
 * Tax boxes in convert mode.
 *
 * They were kept through the first sweep as why-a-control-is-locked sentences
 * and were then removed by name: under the narrowed bar only a server error, a
 * refusal, a `ConfirmDialog` consequence, an `EmptyState` and the named
 * customer-money warnings survive, and a lock nothing refuses is none of those.
 *
 * ⚠️ **What the reader loses, recorded rather than forgotten.** A read-only box
 * with nothing beside it reads as broken, and that objection was the original
 * reason these existed — the decision is that the boxes carry `readOnly` and
 * their values and must speak for themselves. The facts they stated are still
 * true and still not otherwise visible: `POST /deals/{id}/convert` really does
 * copy the company and the currency ({@link convertBaselineFromDeal} reproduces
 * that copy field for field), while the tax treatment is NOT on a deal at all —
 * `d_deals` has no `xero_line_amount_type`, so the conversion writes none and
 * this form asserts `EXCLUSIVE` on top of it (see {@link convertValuesFromDeal}).
 * That asymmetry is why the two were ever worded differently, and it is the
 * thing to re-derive rather than re-guess if either sentence is ever restored.
 *
 * ⚠️ **NEITHER WAS EVER ABOUT A LINE'S `taxRate`, and their removal is not a
 * precedent for one — in EITHER direction.** Those were the DOCUMENT's
 * `xeroLineAmountType`: one value for the whole quote, deciding how Xero prints
 * the customer's document. A line's rate is a different column under a
 * different, separately-taken decision, measured in `QuoteLineItemsTable.tsx`
 * above `taxRateSource`.
 */

/*
 * ⚠️ **`CONVERTED_LINE_TAX_RATE` IS NO LONGER DECLARED HERE — it moved to
 * `@/lib/deal-conversion` on 2026-08-30**, verbatim, together with every
 * constraint its doc comment recorded: it names the COMPANY, it stays
 * CONDITIONAL so it promises no figure, and it keeps `0.00` from reading as
 * zero-rated. See the import at the head of this file for why the move happened
 * then and not earlier, and why the invoice route gets a second constant rather
 * than sharing this one.
 *
 * ⚠️ One clause the old doc carried is worth keeping here, where the control it
 * refers to is: what was deliberately dropped from the RENDERED sentence in the
 * owner's copy pass is the read-only/editable split on a line's rate box. That
 * belongs beside the controls it describes, in `QuoteLineItemsTable.tsx`, not in
 * a preview panel that has no rate box on it.
 */
/**
 * One ROW's entry as a comparable string, or `null` when the array no longer
 * holds that row at all.
 *
 * ⚠️ **CORRECTED — LOOKED UP BY ROW KEY THROUGH A PARALLEL ARRAY, NOT BY
 * `dealLineId`.** *SUPERSEDED: "One line's override as a comparable string …
 * `overrides.find((entry) => entry.dealLineId === id)`."* A `lineSet` entry for
 * a line somebody ADDED has no `dealLineId`, so an id lookup could not address
 * the one kind of row a refusal is most likely to land on. `rowKeys[i]` is the
 * row `entries[i]` came from — the grid builds both in one pass — so the
 * position is the link.
 *
 * ⚠️ **`JSON.stringify` is safe here ONLY because `convertLineSetEntryOf` in
 * `@/lib/deal-convert-lines` assigns its keys in a fixed order**, so two equal
 * entries always serialise identically. It is a FINGERPRINT for "is this the
 * same request", never a value anything is read back out of. If that builder
 * ever assigns conditionally in a different order, this comparison starts
 * reporting false differences — which is the harmless direction here (a stale
 * refusal is dropped), and is worth knowing about anyway.
 */
function entryFingerprint(
  entries: readonly DealConvertLineSetEntry[],
  rowKeys: readonly string[],
  rowKey: string,
) {
  const index = rowKeys.indexOf(rowKey);
  const found = index === -1 ? undefined : entries[index];
  return found === undefined ? null : JSON.stringify(found);
}

/** Whether two entry arrays say the same thing, in the same order. */
function sameEntries(
  a: readonly DealConvertLineSetEntry[],
  b: readonly DealConvertLineSetEntry[],
) {
  if (a.length !== b.length) return false;
  return a.every((entry, index) => JSON.stringify(entry) === JSON.stringify(b[index]));
}

/*
 * ⛔ **THE CONSTANT ITSELF IS GONE — the sentence MOVED, it was not deleted.**
 * *SUPERSEDED, the rendered text: "Tax rate comes from the company. With none
 * recorded, lines arrive at 0.00 — unset, not zero-rated."*
 *
 * It now lives as `convertedTaxRateCopy` in `QuoteLineItemsTable.tsx`, carried
 * by the Tax rate CELL of the converted row — which is exactly where
 * the block above says it belongs: *"beside the controls it describes … not in a
 * preview panel that has no rate box on it."* There is a rate cell there now.
 *
 * ⚠️ *SUPERSEDED: "as `CONVERTED_TAX_RATE_REASON`".* That constant is gone in
 * its turn — one sentence became three on 2026-08-30, when the cell started
 * printing the company's actual percentage instead of naming its source.
 *
 * ⚠️ **DO NOT RE-ADD IT HERE.** Two copies of one sentence in two files is the
 * drift this document keeps recording, and this one would be the copy nobody
 * re-reads. The constraints written out above still govern the wording at the
 * other end, with ONE of them spent: *SUPERSEDED, "it stays conditional"* — the
 * cell reads the figure off the company record now rather than declining to
 * name one. The binding constraint is unchanged and matters more for it: a
 * company that states NO rate must never render `0.00` or `0%`.
 */

function QuoteForm({
  onClose,
  quote: initialQuote,
  onSaved,
  onCreatedPreview,
  onXeroPush,
  onLinesSaved,
  lockedCompany,
  prefill,
  convertFrom,
  hasLineItems,
}: {
  onClose: () => void;
  quote?: Quote;
  onSaved: (quote: Quote) => void;
  /** See {@link QuoteFormModalProps.onCreatedPreview}. */
  onCreatedPreview?: (quote: Quote) => void;
  /** See {@link QuoteFormModalProps.onXeroPush} — the report, never a push. */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
  /**
   * See {@link QuoteFormModalProps.onLinesSaved} — the line grid saved on its
   * own, which `onSaved` does not cover, and which may fire while this form is
   * still open.
   */
  onLinesSaved?: () => void;
  lockedCompany?: { id: number; name: string };
  prefill?: QuoteFormPrefill;
  convertFrom?: Deal;
  hasLineItems: boolean | 'unknown';
}) {
  const formId = useId();
  const subjectRef = useRef<HTMLElement | null>(null);

  /*
   * The record this form is now editing, which is NOT always the one it was
   * opened with.
   *
   * A create leaves the modal open — a line item is `POST /quotes/{id}/products`
   * and cannot exist before the quote does, so the lines are entered against
   * the record the create just returned. From that moment the form is an EDIT
   * of that record: same baseline, same diff, same PATCH. `created` also
   * carries the re-read after a line write, because a line write rewrites the
   * four header totals in its own transaction and returns none of them.
   */
  const [created, setCreated] = useState<Quote | null>(null);
  /**
   * True once THIS form created the quote it is now showing.
   *
   * Separate from `created !== null`, which a post-line-write refetch also
   * sets. Only the create needs to be announced, and only the create turns
   * Cancel into Done.
   */
  const [justCreated, setJustCreated] = useState(false);
  const quote = created ?? initialQuote;
  const isEdit = quote !== undefined;

  /*
   * ── The draft line items, before there is a quote to attach them to ──────
   *
   * ⚠️ A line is `POST /quotes/{id}/products` and there is no bulk endpoint, so
   * a line CANNOT be written before the quote exists. Creating with lines is
   * therefore necessarily two phases, and the owner chose how that is reported:
   * **one Save, reported honestly**. The grid below holds the rows locally
   * while `quote` is undefined and hands them up here as exactly the bodies the
   * POST takes; this form writes them, one at a time, straight after the quote
   * comes back.
   *
   * `blockedDraftRows` is the rows the grid is WITHHOLDING because they would
   * be refused — no product, a bad percentage, a city the line's project does
   * not declare, a `(product, project)` pair another row already claims. It is
   * not cosmetic: submitting with a blocked row would create the quote and
   * silently drop that row, so Save is disabled until it is zero. Reading
   * `draftLines.length` alone would miss it entirely.
   */
  const [draftLines, setDraftLines] = useState<QuoteProductCreateInput[]>([]);
  const [blockedDraftRows, setBlockedDraftRows] = useState(0);
  const [lineOutcome, setLineOutcome] = useState<DraftLineOutcome | null>(null);

  /*
   * ── The note, before there is a quote to attach it to ────────────────────
   *
   * Same shape as the draft lines above, and for the same reason: a note is
   * `POST /notes` carrying a `quoteId`, so it cannot be written before the
   * quote exists. It is therefore phase two of one Save, reported honestly —
   * `noteOutcome` is `null` while nothing has been attempted, which is a
   * different fact from a note that was refused and must never render as one.
   *
   * ⚠️ **Nothing here reaches `buildPayload`.** There is no `notes` key on a
   * quote and sending one would be a `.strict()` 422 naming it; the note is a
   * separate request whose failure belongs beside the note box, never marked on
   * a header field.
   */
  const [noteDraft, setNoteDraft] = useState('');
  const [noteOutcome, setNoteOutcome] = useState<DraftNoteOutcome | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  /* Read and written synchronously, so a double click cannot write two notes. */
  const noteLock = useRef(false);

  /*
   * ── CONVERTING A DEAL ─────────────────────────────────────────────────────
   *
   * ⚠️ Read from `initialQuote`, never from `quote`. `created` never fills in on
   * this path — a successful conversion closes the form — but the narrowing is
   * structural rather than incidental: `convertFrom` is a create-side mode, and
   * an edit ignores it outright.
   */
  const convertDeal = initialQuote === undefined ? convertFrom : undefined;
  const isConverting = convertDeal !== undefined;
  /**
   * The quote a conversion CREATED, held only when the follow-up PATCH was then
   * refused.
   *
   * ⚠️ **It is what stops a second `POST /deals/{id}/convert`.** That endpoint
   * answers no 409 and a deal may legitimately be quoted many times, so a
   * retried Save on this form must re-send the PATCH and nothing else —
   * otherwise the recovery from a refused header edit is a second quote.
   */
  const [convertedQuote, setConvertedQuote] = useState<Quote | null>(null);
  /** Why a conversion was refused. Cleared at the start of every attempt. */
  const [convertError, setConvertError] = useState<string | null>(null);
  /*
   * ⚠️ **WHY `convertedQuote` ALONE CANNOT SAY WHAT WENT WRONG ANY MORE.**
   *
   * That state used to have exactly one cause — a refused header PATCH — so the
   * band below read the cause off its presence. It now has a SECOND: a
   * conversion whose header was perfectly fine and whose Xero push was refused.
   * Telling that reader "your header changes were refused" sends them to correct
   * a field that is not marked and was never wrong, which is the same defect
   * `InvoiceFormModal` records against its own twin of this band.
   *
   * So the cause is carried explicitly and the band reads THIS, never its own
   * presence. Set in one place: the `catch` around the follow-up PATCH.
   */
  const [convertHeaderRefused, setConvertHeaderRefused] = useState(false);
  /*
   * ⚠️ **XERO REFUSED THE QUOTE THIS CONVERSION CREATED, AND NOTHING ELSE
   * ANYWHERE CARRIES THAT FACT.**
   *
   * `POST /deals/{id}/convert` pushes the quote it made and swallows a failure
   * so the conversion still answers `201`. It does not re-read the record on that
   * branch **because nothing was written** — so the quote arrives with no Xero
   * link, and its own page cannot tell that state from a quote nobody has pushed
   * yet. Drop this and the person is told the conversion worked, which it did,
   * and never learns the customer's document is not in the accounts.
   *
   * ⚠️ **`pushed` and `skipped` can never reach it.** `conversionPushReport`
   * answers `null` for both, deliberately — a band on the commonest outcome in
   * the system is worse than no band. See it for the whole argument.
   */
  const [convertPush, setConvertPush] = useState<XeroRowPushReport | null>(null);
  /*
   * The deal's lines, read once and ONLY to be described.
   *
   * ⚠️ **CORRECTED — this read is not "ONLY to be described", and it is not a
   * baseline for overrides either.** *SUPERSEDED, twice: "Nothing here writes
   * them and nothing here may … This read exists so the person converting can
   * see what is coming across before it exists…"; then "**`POST
   * /deals/{id}/convert` takes a `lines` override**, so the rows are the
   * baseline a person edits before the quote exists."* It takes **`lineSet`**,
   * the complete final list, so these rows are the **STARTING POINT** of a list
   * somebody can edit, add to and delete from — not a fixed set with four
   * editable figures on it. The totals half is unchanged through both
   * corrections: the band still reads these to tell whether the lines will own
   * the four figures.
   *
   * ⚠️ **Nothing here still writes a DEAL line, and that is the claim most at
   * risk now that rows can be DELETED.** Discarding a row leaves it off the
   * QUOTE; the deal's own basket is untouched. The deal's STAGE does move
   * (owner 2026-10-18: to the quote-sent stage, server-side, never backwards),
   * which is what `QUOTE_CONVERSION_MOVES_THE_DEAL` says on screen; the deleted
   * `CONVERSION_LEAVES_THE_DEAL_ALONE` is superseded.
   *
   * ⚠️ **`lines: null` is "not read", never "none".** A failed read must not be
   * described as an empty basket — the lines are whatever they are and every one
   * of them still crosses. The three states are kept apart for that reason.
   */
  const [dealLines, setDealLines] = useState<{
    lines: DealProduct[] | null;
    loading: boolean;
    error: string | null;
  }>({ lines: null, loading: isConverting, error: null });
  /**
   * The COMPLETE FINAL list of lines the grid has produced — **exactly the
   * `lineSet` array `POST /deals/{id}/convert` takes** — with the row key each
   * entry came from and whether the set is untouched.
   *
   * ⚠️ **The GRID owns the boxes now, not this form.** *SUPERSEDED: a
   * `Record<number, ConvertLineEdit>` of what each row's boxes held, seeded here
   * from the read and patched by a hand-built table.* The owner's instruction on
   * 2026-08-30 was that the deal→quote modal must present its lines exactly as
   * the New Quote modal does, so `QuoteLineItemsTable` grew a third mode and the
   * bespoke table went with the state that fed it. What arrives here is the
   * finished array.
   *
   * ⚠️ **CORRECTED — IT IS `lineSet`, NOT `lines`, AND `null` IS NOT `[]`.**
   * *SUPERSEDED: "the per-line overrides … and empty when nothing has been
   * edited. ⚠️ **Empty until the lines arrive**, which is still the guard
   * against a blank override: a conversion fired before the read lands carries
   * no `lines` key at all rather than one full of empty strings."* On `lineSet`
   * an EMPTY ARRAY IS A QUOTE WITH NO LINES — it is not "no changes" — so the
   * unread state cannot be spelled `[]` any more and is `null` instead. That is
   * the whole reason the grid emits a nullable object rather than an array.
   *
   * `pristine` is what replaces the old length-of-zero test: it is true when
   * every row is the deal's own, in the deal's own order, untouched — and only
   * then is the `lineSet` key omitted entirely, which is the behaviour every
   * conversion has always had.
   */
  const [convertLineSet, setConvertLineSet] = useState<{
    entries: DealConvertLineSetEntry[];
    rowKeys: string[];
    pristine: boolean;
  } | null>(null);
  /**
   * How many converted rows the grid is refusing.
   *
   * ⚠️ **Client refusals only** — the grid says so on the callback. A server
   * error already sits beside the box that caused it and Convert must stay
   * pressable so the correction can be re-sent.
   */
  const [convertBlockedRows, setConvertBlockedRows] = useState(0);
  /**
   * The last entry array {@link onConvertLinesChange} accepted.
   *
   * ⚠️ A ref rather than the state, because that handler runs from the grid's
   * emit effect and has to compare against what it last SAW, not against the
   * value of a render that has already been scheduled.
   */
  const lastConvertEntries = useRef<DealConvertLineSetEntry[]>([]);
  /**
   * The row keys that went with them, in the same order.
   *
   * ⚠️ **Kept beside the entries so the two can be pruned against each other**,
   * and it is the reason a stale refusal can be dropped at all: a `lineSet`
   * refusal is marked on a ROW, and whether that refusal is still current is a
   * question about the ENTRY at that row's position.
   */
  const lastConvertRowKeys = useRef<string[]>([]);
  /**
   * The server's own per-line refusals, put back on the control that caused
   * them.
   *
   * ⚠️ **Cleared at the start of every attempt**, exactly as `serverErrors` is
   * on the ordinary save path: a refusal about the request before last, left
   * marked on a box somebody has since corrected, is a form that cannot be
   * argued with.
   */
  const [lineServerErrors, setLineServerErrors] = useState<
    Record<string, Record<string, string>>
  >({});

  useEffect(() => {
    if (convertDeal === undefined) return;
    const controller = new AbortController();
    const { signal } = controller;
    /*
     * No `setDealLines({ loading: true })` here, and it is not an omission: the
     * initial state is already `loading: isConverting`, and the modal is KEYED
     * on the deal's id, so a different deal remounts this component rather than
     * re-running the effect over stale rows.
     */
    api
      .listDealProducts(convertDeal.id, signal)
      .then((response) => {
        if (signal.aborted) return;
        /*
         * ⚠️ **Nothing is seeded here any more.** The grid seeds every box from
         * the line it renders — `convertRowOf` in `QuoteLineItemsTable` — and
         * the change test compares against a fresh `convertLineEditOf` of the
         * same line, so a second seeder here would be a second answer to "what
         * did this row start as" and would report rows as edited that nobody
         * touched.
         */
        setDealLines({ lines: response.data, loading: false, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setDealLines({
          lines: null,
          loading: false,
          error:
            caught instanceof ApiError
              ? caught.message
              : 'The request did not complete — check your connection.',
        });
      });
    return () => controller.abort();
  }, [convertDeal]);

  const { ability } = useAuth();

  /*
   * ⚠️ `/notes` is one of the THREE modules the server genuinely enforces by
   * permission set rather than by role, so this is a real gate and not a view
   * decision — and it is `ability`, never `user.role` and never
   * `/permissions/me` read directly. It stays an optimisation all the same: a
   * set can change under an access token that lasts about five days, so the
   * POST's own 403 is handled in `writeDraftNote` regardless.
   */
  const noteDenial = ability.canCreate('notes')
    ? null
    : (denialReason(ability, 'create', 'notes') ?? 'Your account cannot add notes.');

  /*
   * ⚠️ THE TOTALS GUARD. Only a definite `false` hands the four stored totals
   * to the header; `true` and `'unknown'` both hand them to the lines.
   *
   * A quote created in this form is `'unknown'` from the moment it exists: the
   * line-items table below is live and this form is not told what it has
   * saved, so claiming the header still owns the totals would be a guess in
   * the one direction that costs money.
   */
  /*
   * ⚠️ **Draft lines take ownership of the totals BEFORE either exists.**
   *
   * This is the "never write header totals and lines in one user action" rule,
   * applied to the one action that could now do both. The moment there is a
   * draft line, this Save is going to `POST` it — and every line write rewrites
   * all four header totals from the lines in its own transaction. Sending
   * `subTotal`/`grandTotal` on the create as well would put two writers on one
   * figure inside a single click: the header's value would land, the first
   * line's write would silently replace it, and the replaced figure is kept
   * nowhere. `convert` then copies all four onto the invoice verbatim, so the
   * disagreement would be billed rather than corrected.
   *
   * `true` rather than `'unknown'`: this form knows perfectly well that lines
   * are coming, and saying so reads better than "may own these figures".
   */
  const draftLinesPending = !isEdit && draftLines.length > 0;

  /*
   * ⚠️ **There is no document-level Xero project requirement, and none should
   * be added back.** Tracking is optional in Xero's API — a large share of
   * SEED's live lines already carry none, and Xero accepts every one — so a
   * projectless line is normal, not an error state. Almost no products are
   * sold through projects at all, so a warning demanding one would be wrong
   * far more often than right, and it would point at a control (a header
   * project picker) that no longer exists.
   *
   * ⚠️ **What is NOT loosened:** a LINE whose product genuinely IS sold
   * through projects and names none is still refused at write time by the
   * per-line guard (`kind: 'project-required'` in `src/lib/line-project.ts`).
   * Do not re-add a document-level warning on the strength of that guard — it
   * is a different claim about a different thing. And never set an UNRELATED
   * project just to satisfy a guard: a line mis-filed under the wrong project
   * mis-reports permanently, which is worse than untracked.
   */
  /*
   * ⚠️ CONVERTING, the lines are the DEAL'S and the server writes them, so the
   * four header totals are theirs the instant the quote exists. A read that has
   * not landed or that failed is `'unknown'`, which fails safe in the same
   * direction: a document with lines does not hand its totals to the header,
   * and a count nobody could read must not pretend otherwise.
   */
  const convertLineState: boolean | 'unknown' =
    dealLines.loading || dealLines.lines === null ? 'unknown' : dealLines.lines.length > 0;
  const lineItemState: boolean | 'unknown' = isConverting
    ? convertLineState
    : justCreated
      ? 'unknown'
      : draftLinesPending
        ? true
        : hasLineItems;
  const totalsLocked = lineItemState !== false;
  const countUnknown = lineItemState === 'unknown';

  /*
   * This inner form only exists while the modal is open — the wrapper returns
   * null otherwise — so every option source is simply enabled. The `enabled`
   * flag on these hooks is there for callers that keep a form mounted.
   *
   * ⚠️ **Hoisted ABOVE the baseline, and it has to be.** A converting form's
   * baseline names the Draft stage the server is about to write, and it can only
   * do that from this master. Moving it back down puts `stages` in its own
   * temporal dead zone.
   */
  // `failed` is deliberately not destructured — see the Stage field below.
  const { stages, loading: stagesLoading } = useQuoteStageOptions(true);

  /*
   * ⚠️ **CONVERTING, the baseline is the SERVER'S COPY, not a blank form.**
   *
   * The diff taken against it is therefore "what the person changed away from
   * what convert is about to write" — which is precisely, and only, what the
   * follow-up PATCH may carry. A blank baseline here would mark every seeded
   * value as an edit and PATCH the whole header back over itself immediately
   * after the server wrote it.
   *
   * ⚠️ **It depends on `stages`, which are FETCHED.** Until the master lands
   * there is no Draft row to name, so both the baseline and the values hold `''`
   * — consistent, and no diff. When it arrives the baseline gains the id and the
   * stage seed below writes the same id into the values, in effects flushed from
   * the same commit. The window in which the two disagree is sub-frame and no
   * click can land inside it; that is why the seed is allowed to keep its own
   * `stageTouched` guard rather than being folded in here.
   */
  const convertBaseline = useMemo(
    () =>
      convertDeal === undefined
        ? null
        : convertBaselineFromDeal(convertDeal, draftStageOf(stages)?.id.toString() ?? ''),
    [convertDeal, stages],
  );
  const baseline = useMemo(
    () => convertBaseline ?? toFormValues(quote),
    [convertBaseline, quote],
  );
  /*
   * The baseline stays the RECORD as loaded — it is what the PATCH diff is
   * taken against — while a locked company overrides only the live value.
   *
   * On a create the distinction is invisible: the create payload is built from
   * `values`, so the seeded company simply travels with it. On an edit it is
   * the point, because a locked company that differs from the stored one is a
   * genuine change and has to be diffed and sent as one.
   */
  /*
   * ⚠️ The prefill is applied HERE and nowhere else, and only on a create.
   *
   * In the initialiser it is a starting point: React runs it once, so every
   * later keystroke wins outright and no effect can come back and re-assert a
   * seeded value under someone's cursor. `initialQuote` guards the edit branch
   * — a stored subject, company or currency is the record's own and a caller's
   * guess must not overwrite it.
   *
   * A locked company still wins over a seeded one. The two never arrive
   * together today, but if they did, the locked one is the one with a read-only
   * control over it, and `values` has to agree with what is on screen.
   */
  /*
   * ⚠️ The DATE defaults are applied here too, and only on a create, for
   * exactly the reasons above: once, in the initialiser, where no later render
   * can re-assert them. See {@link withDefaultDates}.
   */
  const [values, setValues] = useState<FormValues>(() => {
    /*
     * ⚠️ A CONVERT takes neither `withPrefill` nor `withDefaultDates`. The
     * baseline is already the whole header, and the three values this form adds
     * on top of it are the ones that must DIFFER from it in order to be written
     * at all — see {@link convertValuesFromDeal}. Running the create-side seeds
     * over it would be a second opinion on values the server has already
     * decided, and `withDefaultDates` in particular would overwrite the deal's
     * own closing date with today + 30.
     */
    const seeded = initialQuote
      ? baseline
      : convertDeal
        ? convertValuesFromDeal(baseline, convertDeal)
        : withDefaultDates(withPrefill(baseline, prefill));
    return lockedCompany ? { ...seeded, companyId: String(lockedCompany.id) } : seeded;
  });
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * The LINE GRID's unsaved state, on an edit — reported up by the grid itself.
   *
   * 🔴 **THIS FORM USED TO CALL A GREYED-OUT SAVE "nothing has changed" WHILE
   * ROWS BESIDE IT READ "Unsaved".** `isDirty` is the HEADER diff and nothing
   * else, so somebody who came in to fix line items — which is most of what
   * this modal is opened for — met a disabled button and concluded the app was
   * broken. Reported by the owner on 2026-09-01 against QU-0463, and the audit
   * log settles what happened rather than leaving it to a theory: two
   * `GET /quotes/392/products` and **not one write**. The control they pressed
   * issued nothing, because the control that could have saved their work was
   * the grid's own, scrolled out of view above them.
   *
   * ⚠️ **A disabled control is a CLAIM.** This one asserted "there is nothing
   * to save" over a form that had plenty, and nothing anywhere contradicted it
   * — no error, no request, no log line. That is the shape this codebase keeps
   * paying for: the failure looks exactly like the app working.
   */
  const [lineEdits, setLineEdits] = useState<{ dirty: number; blocked: number }>({
    dirty: 0,
    blocked: 0,
  });
  /**
   * The grid's own save run, handed up by `registerSave`.
   *
   * ⚠️ **`null` means THERE IS NO GRID — never "nothing to save".** It is null
   * on a create (the draft grid registers nothing, because a draft row has
   * nowhere to be saved to) and null once the grid unmounts. Read it beside
   * `lineEdits.dirty`, never instead of it.
   */
  const saveLinesRef = useRef<(() => Promise<boolean>) | null>(null);
  /*
   * Stable, so the grid's registration effect runs on mount and unmount rather
   * than on every render of this form. An inline arrow here would re-register
   * continuously — harmless, but it would make the effect's dependency list a
   * lie about when registration happens.
   */
  const registerLineSave = useCallback((save: (() => Promise<boolean>) | null) => {
    saveLinesRef.current = save;
  }, []);
  /**
   * The save has landed and the Xero half is still running.
   *
   * Only ever true INSIDE `submitting`, so it narrows the button's label rather
   * than gating anything. It exists because the two phases fail differently and
   * a person who cannot tell them apart will read a slow Xero call as a save
   * that has not gone through — and press Save again, which on an edit is
   * harmless and on the acceptance retry would be a second push at a document
   * in an accounting system.
   */
  /*
   * ⚠️ **`pushingToXero` IS GONE.** *SUPERSEDED: a boolean that was true while
   * this form's own inline `POST /xero/create/quotes/{id}` was on the wire.*
   * There is no such call any more — the server pushes inside the save — so the
   * only thing anybody waits on is `submitting`, which already covers it.
   */
  /**
   * What the CREATE's push to Xero did. Null until a create has happened.
   *
   * Separate from every error state on this form, and deliberately never routed
   * through `formError` or `serverErrors`: those belong to the save, and the
   * save succeeded. See {@link CreatePushOutcome}.
   */
  const [createPush, setCreatePush] = useState<CreatePushOutcome | null>(null);
  /**
   * The push's own double-fire guard, and a ref for the same reason
   * `submitLock` is one: the retry button and a still-running submit both read
   * it inside the same tick, before any re-render.
   *
   * ⚠️ Two overlapping pushes of one quote is not a duplicated request the
   * server de-duplicates — `POST /xero/create/quotes/{id}` branches on the link
   * as it finds it, so two in flight can both see no link and both create, and
   * the tenant ends up with two Xero quotes for one of ours.
   */
  /*
   * ⚠️ **`pushLock` IS GONE, AND THE HAZARD IT GUARDED WENT WITH IT.**
   * *SUPERSEDED: "Two overlapping pushes of one quote is not a duplicated
   * request the server de-duplicates — `POST /xero/create/quotes/{id}` branches
   * on the link as it finds it, so two in flight can both see no link and both
   * create, and the tenant ends up with two Xero quotes for one of ours."* That
   * is still exactly true of that endpoint; this form no longer calls it. The
   * server's own document lock is what serialises save-time pushes now.
   */
  /**
   * ⚠️ The double-submit guard, and it is a **ref rather than `submitting`**.
   *
   * `setSubmitting(true)` does not take effect until React re-renders, so two
   * clicks — or a click and a stray Enter — inside the same tick both read
   * `submitting === false`, both pass, and both `POST /quotes`. That is not a
   * duplicated request the server de-duplicates; it is **two quotes**, each
   * with its own id and its own line items. A ref is written synchronously, so
   * the second caller sees the first one's write in the same tick and stops.
   *
   * The disabled/loading Button is the visible half and is not the guard: it
   * relies on the same render that has not happened yet.
   */
  const submitLock = useRef(false);
  /*
   * ⚠️ **`addedLinesWritten` IS GONE, AND SO IS THE HAZARD IT GUARDED.**
   * *SUPERSEDED: "True once the ADDED lines have been written on the convert
   * path. 🔴 **IT IS WHAT STOPS A RETRY BILLING THE CUSTOMER TWICE** … A convert
   * whose follow-up PATCH is refused leaves the form open with Save live, and
   * `runConvert` deliberately re-sends only the PATCH — but the added lines were
   * `POST /quotes/{id}/products`, which has **no idempotency of its own**: the
   * same body sent twice is two line items, on a document a customer reads."*
   *
   * There are no `POST /quotes/{id}/products` calls on the convert path any
   * more: an added line rides on the conversion as a `lineSet` entry with no
   * `dealLineId`, and `convertedQuote` already stops a second conversion. The
   * flag is deleted rather than left set-and-unread, because a re-entry guard
   * nothing reads is the shape this repo catalogues as *a function nothing
   * calls* — it reads as protection while protecting nothing.
   *
   * ⚠️ **The reasoning still applies WORD FOR WORD to the CREATE path**, which
   * does write its lines one at a time after the quote exists. If that path ever
   * grows a retry, it needs this flag.
   */
  const [totalsOpen, setTotalsOpen] = useState(false);
  /*
   * The exchange-rate dialog. Open state only — the typed rate lives inside the
   * dialog and reaches `values.currencyRate` on Save and never before, so an
   * abandoned edit cannot leave a half-typed rate in the form's payload.
   */
  const [rateDialogOpen, setRateDialogOpen] = useState(false);

  /*
   * Accepting a quote moves the stage and nothing else — the 409 that used to
   * require confirming an invoice-raise is gone server-side, so saving a stage
   * change is an ordinary PATCH, reported by `reportFailure` like any other key.
   */

  // `failed` is deliberately not destructured: the hint that reported a failed
  // currency read was removed on 2026-09-05 and nothing else consulted it.
  const { currencies, loading: currenciesLoading } = useCurrencyOptions(true);
  /*
   * ⚠️ Read for ONE reason: the CYCLE segment of a new quote's default subject.
   * See `defaultDocumentName` — a business year is a handful of rows, so this
   * is a single page rather than a typeahead, and nothing here feeds a picker.
   *
   * `!isEdit` because the seed is create-only: a stored subject is the
   * record's own and re-deriving it because the cycle rolled over would rename
   * a document that may already be with a customer. A failed or empty list is
   * not an error state here — `currentCycle` answers `null`, the helper drops
   * the segment, and the subject is simply `<company> - Plans`. `loading` and
   * `failed` are deliberately not read: there is nothing on screen to report
   * them to, and the seed re-applies when the rows land.
   */
  const { cycles } = useCycleOptions(!isEdit);
  /*
   * ⚠️ Read for ONE reason: to resolve the region and intake of the project
   * 🔴 **THE PROJECT FETCH IS GONE — 2026-08-31.** *SUPERSEDED: "this quote is
   * already stored against, so the two Xero tracking hints can say what is being
   * derived … `isEdit` rather than `true` because the tracking section is
   * edit-only."* Its **only** reader was the derived-tracking readout under the
   * two override boxes, and both went with the "Xero tracking and references"
   * section, so `useProjectOptions` here had no consumer left.
   *
   * ⚠️ **A request stopped being made, which is a behaviour change and not a
   * tidy-up**: opening an existing quote no longer spends a page of up to 100
   * projects out of the budget the office shares. ⚠️ *SUPERSEDED:
   * "120-per-60-seconds."* It is **12,000/60s** (corrected 2026-09-17) — the
   * request was dropped because nothing read it, not to save the budget. That
   * is strictly better, and it is worth stating rather than discovering.
   *
   * ⚠️ **The read-only Project box did NOT depend on it** — `headerProjectName`
   * comes straight off `quote.project.name`. Do not restore the fetch believing
   * that field needs it.
   */

  /*
   * The branding-theme list comes from the LIVE tenant, so it is asked for only
   * when there is a tenant to ask. `enabled` false while the connection is
   * still being read and whenever there is none: a request that can only fail
   * would put a red block on a form whose real state is "Xero is not
   * connected", which is a different sentence with a different action.
   */
  const xero = useXero();
  /*
   * ⚠️ `isEdit` is part of the condition, not decoration. The only thing that
   * reads these themes is the Letterhead picker, which no longer renders on a
   * create — so fetching them there spent a call against Xero's shared rate
   * limit to fill a control nobody would see, and every new quote paid it. The
   * limit being spent is what put a warning block on the create form in the
   * first place.
   */
  const brandingThemes = useXeroBrandingThemes(
    isEdit && !xero.loading && xero.connection.connected,
  );

  /*
   * ── What a NEW quote INHERITS FROM ITS COMPANY ──────────────────────────
   *
   * One request, one effect, four seeds: the SUBJECT, the CURRENCY, the
   * LETTERHEAD and the TAX treatment all come off the company record chosen on
   * a new quote. It began as the letterhead alone; the other three joined it
   * rather than growing their own effects, because four copies of "only on a
   * create, only while untouched, only for a company that has one" would agree
   * until one of them was edited.
   *
   * The rules are the letterhead's, and every one of them applies to all four:
   *
   * - ⚠️ **Never on an EDIT.** A stored value is the record's own. Re-seeding
   *   because the account default has since changed would silently restyle,
   *   re-price or rename a document nobody asked to touch, so `isEdit` stops
   *   this before it even asks.
   * - ⚠️ **Never over a choice the user has made.** Each seed has its own
   *   `…Touched` ref, written by that control's own `onChange` and by nothing
   *   else. They are tracked EXPLICITLY rather than inferred from an empty
   *   field, because '' is a real choice on three of the four — on the
   *   letterhead and the tax treatment it means "use the default", and on the
   *   subject it is a box somebody cleared — and a seed that read emptiness as
   *   "untouched" would undo the clearing on the next company change.
   * - ⚠️ **A value the CALLER seeded counts as touched.** A quote raised from a
   *   deal carries that deal's subject and the currency it was priced in; both
   *   were chosen deliberately by the caller, and quietly replacing them with
   *   the company's would undo the one thing the flow exists to carry across,
   *   at a control that says nothing about either. Same reading of the word
   *   `InvoiceFormModal` takes for its own subject seed.
   * - **Never disturb a field for a company that has none.** A `null` seeds
   *   nothing; it is not an instruction to clear. Where the company says
   *   nothing, whatever the form already had stands.
   *
   * Both entry paths are covered because both land in `values.companyId`: a
   * `lockedCompany` seeds it at mount, and the Combobox changes it later. So
   * an untouched form follows the company, and re-seeds when it moves.
   *
   * ⚠️ **Failure is silent and non-blocking.** These are conveniences on
   * optional fields — the form opens, works and saves with nothing seeded. A
   * red block here would be about a request the person never asked for.
   *
   * ⚠️ **NOTHING HERE IS GATED ON A XERO LIST BEING READABLE, and that is the
   * point.** Xero's reference reads are in a hard cooldown
   * (`src/lib/xero-cooldown.ts`), so the branding-theme list can be entirely
   * unreadable while this effect runs. A seeded theme id must still be carried
   * and shown exactly as a stored one is — which it is, because
   * `brandingThemeOptions` injects `storedThemeId` whether the live list has
   * one entry or none, and `themeCanOnlyClear` keeps the control usable rather
   * than freezing it. Never add a "is the list loaded" test in front of a seed:
   * that is the path that drops a real value because a list nobody asked for
   * could not be fetched.
   */
  const themeTouched = useRef(false);
  const [seededThemeId, setSeededThemeId] = useState<string | null>(null);
  /*
   * ⚠️ A prefilled subject counts as TOUCHED. See the rule above — this is the
   * ref that enforces it, and it is trimmed because a seed of `'   '` is as
   * absent as none and must not lock the company seed out of a subject that is
   * really still empty.
   */
  const subjectTouched = useRef((prefill?.subject ?? '').trim() !== '');
  /* Same reading for a prefilled currency: the caller chose it. */
  const currencyTouched = useRef((prefill?.currency ?? '').trim() !== '');
  const [seededCurrency, setSeededCurrency] = useState<string | null>(null);
  /*
   * ── The CONTACT seed — the company's designated primary ──────────────────
   *
   * ⚠️ **Touched starts TRUE on an edit, and that is not a long way of writing
   * `isEdit`.** A stored contact is the record's own, and so is a stored
   * ABSENCE: a quote deliberately left with no contact must not gain one
   * because somebody opened the form. Both are decisions this seed may not
   * overturn — which is why the flag is the record's existence rather than its
   * `contactId`, and why `onCompanyChange` is the one place that hands the
   * field back (see there).
   *
   * A prefilled contact counts as touched for the same reason a prefilled
   * subject and currency do: the caller chose it.
   */
  /*
   * ⚠️ **Touched starts TRUE on a CONVERT too**, and for the same reason it does
   * on an edit: `deal-convert.ts` copies the deal's `contact_id`, so the
   * document already carries its answer. A company default landing over it a
   * moment after the company read returned would make this form produce a
   * different quote from the button it replaced — and it would do so silently,
   * under a form the person has already read.
   */
  const contactTouched = useRef(
    initialQuote !== undefined || convertFrom !== undefined || prefill?.contact !== undefined,
  );
  /**
   * The primary contact read off the chosen company, TAGGED with that company.
   *
   * ⚠️ The tag is what makes the pin correct without a second write. A bare
   * `{id, name}` would go on offering the previous account's primary until a new
   * read landed — and clearing it from the company `onChange` and from the
   * effect's no-company branch would be two more places to keep in step. Same
   * shape as `useCompanyAccountLead`'s `loaded.companyId` and the lookup hook's
   * `result.scope`: state carries what it ANSWERS FOR.
   */
  const [seededContact, setSeededContact] = useState<{
    companyId: number;
    contact: { id: number; name: string } | null;
  } | null>(null);
  /**
   * The company’s own SALES TAX — its rate and its Xero code — TAGGED with the
   * company it was read for.
   *
   * ⚠️ *SUPERSEDED: "**Only the CONVERT path renders it** — a converted line has
   * no tax rate of its own, so the grid’s Tax rate column is a readout of this
   * figure. Every other mode has a live control and never consults it."*
   * **Every mount reads it now**, on the owner's report of 11 September 2026
   * that a new line item shows the Xero default and not the company's rate. A
   * live control is not the same thing as an INFORMED one: the server has always
   * seeded a created line from this figure, and the grid simply had no way to say
   * so, so the empty option read "Xero default" over a 20% save.
   *
   * ⚠️ **The tag is what keeps it honest, exactly as {@link seededContact}’s
   * does.** Untagged, a slow response for company A would answer for a form that
   * has since moved to company B — and the answer here is a percentage printed
   * beside a quote, not a pin that can be ignored.
   *
   * ⚠️ *SUPERSEDED: "`null` INSIDE the tag is a real answer — the company states
   * no rate, **which is every live company today**".* **Re-measured 11 September
   * 2026 over 100 live companies: all 100 state both, and 63 of them state a
   * genuine `0.00`.** `null` inside the tag is still a real answer and still not
   * the same fact as `null` for the whole slot — see `CompanySalesTax` — it is
   * simply no longer the ordinary case.
   *
   * ⚠️ **READ AND NEVER SENT.** No payload built by this form carries either
   * value; the server owns the figure and seeds the line itself.
   */
  const [companyTaxRate, setCompanyTaxRate] = useState<{
    companyId: number;
    rate: string | null;
    taxType: string | null;
  } | null>(null);
  /*
   * ⚠️ **`companyApproval` IS GONE — deleted 2026-08-31**, with the client-side
   * approval refusal it was the only reader of. The server's own gate
   * (`assertCompanyApproved`, migration 099) is the backstop now, and it is a
   * better one: it reads the company's row inside the same transaction as the
   * write, so there is no in-flight window and no tag needed to keep a stale
   * answer for company A from refusing company B.
   *
   * ⚠️ **Do not reinstate it as a "faster" pre-check.** A second reader of the
   * same fact is a second sentence about it, and this form has just been
   * corrected for having one. The picker's withholding is the optimisation; the
   * 409 is the guarantee.
   */
  /**
   * The company this form OPENED on, so a stored or prefilled contact can be
   * pinned for it and only for it.
   *
   * ⚠️ Captured at mount rather than compared against `baseline.companyId`,
   * which is the blank `''` on a create — a prefilled create would then read as
   * "the company changed" from its first frame, withhold the pin the prefill
   * exists to make legible, and announce a clearing that never happened.
   *
   * ⚠️ `useState` rather than `useRef`, though nothing ever writes it: it is
   * READ DURING RENDER, and a ref read during render is exactly the value React
   * does not re-render for. Same reason `validTillFollowsIssue` is state while
   * its four seed-guard siblings are refs — those are never rendered, this is.
   */
  const [openedCompanyId] = useState(values.companyId);
  /*
   * The DOCUMENT line-amount type. No prefill key carries one, so this starts
   * false unconditionally — but it is a ref for the same reason the others are:
   * '' on this control is the real, choosable value "use the company's Xero
   * default", not an empty box.
   */
  const lineAmountTouched = useRef(false);
  /**
   * Whether Valid till is still FOLLOWING the issue date, or has become the
   * user's own.
   *
   * ⚠️ **State rather than a ref, unlike its four siblings above, and the
   * difference is that this one is RENDERED.** The Valid till field carries a
   * hint saying it is following — which has to disappear the moment it stops —
   * and a ref read during render is a value React does not re-render for. The
   * siblings are refs because nothing shows them.
   *
   * ⚠️ **Starts FALSE on an EDIT, and that is not a long way of writing
   * `isEdit`.** A stored `validTill` is a validity date somebody committed to a
   * customer; it is already theirs, whoever set it and whenever, and starting
   * this false is what stops a corrected issue date silently moving the day the
   * quote lapses on.
   */
  /*
   * ⚠️ **A CONVERT that took the DEAL'S CLOSING DATE starts this FALSE.** That
   * date is a fact about the deal, not a default derived from the issue date, so
   * a corrected issue date must not silently move it to today + 30 — the same
   * rule that keeps a stored `validTill` still on an edit. A convert with no
   * closing date took the ordinary default and keeps the ordinary behaviour.
   */
  const [validTillFollowsIssue, setValidTillFollowsIssue] = useState(
    initialQuote === undefined && (convertFrom === undefined || convertFrom.closingDate === null),
  );
  const seedCompanyIdNumber = Number(values.companyId.trim());
  /*
   * ⚠️ **`isEdit` is not part of this test, and every seed carries it itself
   * instead.** On an edit the response is read and discarded at the
   * `if (isEdit) return;` below. Narrowing this to a create would spend one
   * fewer request against the shared limiter; left as it is here because that
   * changes what the form FETCHES, not what it shows.
   */
  const companyLookupId =
    !Number.isInteger(seedCompanyIdNumber) || seedCompanyIdNumber <= 0
      ? null
      : seedCompanyIdNumber;
  /*
   * The company the in-flight read is FOR. Aborting on change is most of the
   * answer; the stamp is what makes it structural — a slow response for
   * company A can never seed a form that has since moved to company B. It is
   * the same guard every other asynchronous slot in this app puts on its own
   * results.
   */
  const seedTargetRef = useRef<number | null>(null);

  useEffect(() => {
    seedTargetRef.current = companyLookupId;
    // No company, nothing to read. The seeded pin does not need clearing here:
    // it is tagged with the company it came from and `seededContactPin` stops
    // resolving the moment the form names a different one.
    if (companyLookupId === null) return;
    const controller = new AbortController();
    api
      .getCompany(companyLookupId, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        if (seedTargetRef.current !== companyLookupId) return;
        const company = envelope.data;

        /*
         * ── 0. THE CONTACT — the company's designated primary ─────────────
         *
         * ⚠️ **The one seed that is NOT create-only, and it sits above the
         * `isEdit` return deliberately.** Its guard is `contactTouched`, which
         * starts true on an edit and is handed back by `onCompanyChange` and
         * by nothing else — so on an edit this fires exactly when somebody has
         * moved the company, which is the moment the stored contact stopped
         * describing this document. It is not a re-derivation of a saved
         * record: the field it fills was emptied one gesture ago, by the user.
         *
         * ⚠️ `primaryContact: null` seeds NOTHING and is an ordinary state —
         * an account that designates nobody. It is never an instruction to
         * clear, which is why the branch below only ever writes an id.
         *
         * The pin is set either way, so moving to a company with no primary
         * drops the previous one's rather than leaving it selectable.
         */
        /*
         * ── THE COMPANY SALES TAX ─ read on EVERY path, seeded onto nothing ──
         *
         * Not a seed and not create-only, so it sits above the `isEdit ||
         * isConverting` return with the contact pin. It writes no form value —
         * it is a readout of the company record, shown beside the lines the
         * server will apply it to.
         *
         * ⚠️ **EVERY grid mount reads it, not only convert.** A blank
         * percentage on a NEW line is given this figure server-side, so the Tax
         * rate control and the Amount preview both have to know it or they
         * describe a save that is not going to happen — the owner's report of
         * 11 September 2026.
         *
         * ⚠️ **The rate is READ, never sent.** The server derives and owns it
         * from the company’s Xero tax code; a client that writes it back is a
         * second writer of a figure it did not compute — and a client that
         * seeded it into a draft would send the value as it stood when this
         * response landed, in preference to the one in force at Save.
         */
        setCompanyTaxRate({
          companyId: companyLookupId,
          rate: company.xeroSalesTaxRate,
          taxType: company.xeroSalesTaxType,
        });

        const primary = primaryContactSeed(company);
        setSeededContact({ companyId: companyLookupId, contact: primary });
        if (primary && !contactTouched.current) {
          setValues((prev) => {
            // Re-checked inside the updater, as the stage seed does: a pick
            // made between the render that scheduled this and the render that
            // applies it must still win.
            if (contactTouched.current || prev.contactId !== '') return prev;
            return { ...prev, contactId: String(primary.id) };
          });
        }

        /*
         * ⚠️ **EVERY seed below is CREATE-ONLY.** The request runs on both
         * branches, so the guard lives here rather than on the request. A
         * stored subject, currency, letterhead or tax treatment is the
         * record's own.
         */
        /*
         * ⚠️ **`|| isConverting`, and it covers the four seeds below at their
         * SOURCE rather than one at a time.** On a convert every one of them
         * would overwrite something `deal-convert.ts` has already decided — the
         * subject is the deal's NAME and the currency is the deal's own — and a
         * fifth seed added later is covered without anybody remembering to.
         * The READ itself is untouched: on a convert the response is simply read
         * and discarded, which is what still lets the contact pin resolve.
         */
        if (isEdit || isConverting) return;

        /*
         * ── 1. THE SUBJECT — the owner's document-name pattern ───────────
         *
         * ⚠️ **`<company short name> - Plans - <current cycle>`, and it is
         * built by `defaultDocumentName` rather than here.** A deal's name and
         * a quote's subject are now ONE convention across two tables, so the
         * pattern has one implementation; see the import note. It stays an
         * ordinary editable Subject box — a default, not a rule.
         *
         * A missing segment is DROPPED by the helper, never rendered as a gap:
         * with no cycle covering today the subject is `<company> - Plans`,
         * which is honest, where `Acme -  - ` reads as broken. `shortName` is
         * null on every company in production, so the long name is the normal
         * output and is correct rather than a bug to truncate.
         *
         * The owner asked for the company name plus the Xero number. **The
         * number half is deliberately absent and nothing here may fake it**:
         * `xeroQuoteNumber` is written by Xero on a successful push and does
         * not exist until then, so a quote being created has none. Allocating
         * or reserving one client-side is the read-then-write race the
         * free-text `quoteNumber` box already demonstrated in production. The
         * append belongs wherever the push result is handled, not here.
         *
         * Skipped rather than truncated when the name will not fit. A subject
         * is 255 and a company name can exceed it; cutting one mid-word puts a
         * severed sentence on a customer-facing document, and seeding a value
         * the form then refuses reads as the form breaking. An empty required
         * field the person fills in is the honest outcome.
         */
        if (!subjectTouched.current) {
          /*
           * ⚠️ The cycle list is FETCHED, so it is empty on the first render
           * and this effect re-runs when it lands — see the dependency array.
           * The re-run is what appends the cycle segment to a subject that was
           * seeded before the rows arrived; it cannot fight the user, because
           * `subjectTouched` is set by the first keystroke and by nothing else.
           */
          const name = defaultDocumentName(company, currentCycle(cycles));
          if (name !== '' && name.length <= MAX_LENGTHS.subject) {
            setValues((prev) => (prev.subject === name ? prev : { ...prev, subject: name }));
          }
        }

        /*
         * ── 2. THE CURRENCY — the company's default ──────────────────────
         *
         * ⚠️ The three-letter CODE, never the id. A quote stores `currency` as
         * a `CHAR(3)` with no foreign key to `/currencies`, and the company's
         * own `currency.id` is nullable precisely because the legal set is
         * XERO's rather than ours — a stored code may name no row in our
         * master at all. `code` is the only half of that object that is always
         * meaningful.
         *
         * Checked against `CURRENCY_PATTERN` before it lands. The server does
         * not fold case and refuses anything that is not exactly three capital
         * letters, so a code Xero holds in some other shape is dropped here
         * rather than seeded into a guaranteed 422 on a field the person never
         * touched.
         *
         * `setSeededCurrency` is the other half, and it is not bookkeeping:
         * see `currencyChoices` below. A code our master no longer offers would
         * otherwise render the picker BLANK over a form that is carrying it —
         * reading as "no currency" and one save from dropping it.
         *
         * The pinned exchange rate is untouched on purpose, and cannot be
         * stale: a rate can only be entered once a currency is chosen, and
         * choosing one sets `currencyTouched`, which stops this branch.
         */
        if (!currencyTouched.current) {
          // The billing region's currency when the company names none, as the
          // invoice form does.
          const own = (company.currency?.code ?? '').trim().toUpperCase();
          const code =
            own !== '' ? own : (company.billingRegionDefaults?.currencyCode ?? '').trim().toUpperCase();
          if (code !== '' && CURRENCY_PATTERN.test(code)) {
            setSeededCurrency(code);
            setValues((prev) => (prev.currency === code ? prev : { ...prev, currency: code }));
          }
        }

        /*
         * ── 3. THE LETTERHEAD — the company's branding theme ─────────────
         *
         * ⚠️ Only the id. `xeroBrandingThemeName` is NEVER resolved by the
         * server on a company read — it is always null — so it is not read
         * here and must not become a label; the live theme list is what names
         * a theme, and under the current cooldown the id stands alone.
         */
        if (!themeTouched.current) {
          const themeId = company.xeroBrandingThemeId;
          if (themeId !== null && themeId.trim() !== '') {
            setSeededThemeId(themeId);
            setValues((prev) =>
              prev.xeroBrandingThemeId === themeId
                ? prev
                : { ...prev, xeroBrandingThemeId: themeId },
            );
          }
        }

        /*
         * ── 4. THE TAX TREATMENT — the company's Xero sales default ──────
         *
         * ⚠️ **THE COMPANY IS THE ONLY SOURCE. There is no country rule here
         * and no 20% anywhere in this file.** A UK company carries its VAT
         * treatment on its own record; a company that carries nothing inherits
         * nothing, and the form's existing default — `''`, *"use the company's
         * Xero default"* — stands. Do not add a jurisdiction test: the day an
         * account bills through a different entity, a hard-coded country rule
         * is a wrong tax treatment on a document a customer is invoiced
         * against, and nothing on screen would say where it came from.
         *
         * ⚠️ **The two enums are DIFFERENT WORDS and this does not translate
         * between them.** A company's `xeroSalesLineAmountType` is the CONTACT
         * vocabulary (`INCLUSIVE | EXCLUSIVE | NONE`); a document's is
         * `INCLUSIVE | EXCLUSIVE | NOTAX`. `toDocumentLineAmountType` is the
         * app's one narrowing function and it answers `null` for anything
         * outside the document union — `NONE` included — so the two shared
         * members seed across and the divergent one does not.
         *
         * That is not a gap. `null` on a document means *"fall back to the
         * company's Xero sales default"*, and the server resolves it with
         * `document.lineAmountType ?? contactAmountTypeToDocument(company…)` —
         * so a company set to `NONE` pushes as `NOTAX` either way. Seeding
         * `NOTAX` here would buy an identical push and cost the one thing that
         * matters: it would FREEZE the document on today's answer, so a quote
         * would stop following a company whose accountant later corrects it.
         * Leaving it blank is both the honest render and the live one.
         *
         * ⚠️ **THE QUOTE HAS NO TAX-CODE FIELD, so the company's
         * `xeroSalesTaxType` is NOT seeded here and cannot be.** `xeroTaxType`
         * exists on `QuoteProduct` and `QuoteProductCreateInput` — a LINE
         * column — and on neither `Quote` nor `QuoteCreateInput`. There is
         * nothing on this header to put it in, and the server already applies
         * it on push: `resolveLineTaxType` is `line.xeroTaxType ??
         * companyDefault`, so a line that sets none inherits the company's
         * code without this form doing anything. Do not add a header control
         * for it — the write schemas are `.strict()`, so the key would be a
         * 422, and a picker over it would need the live tax-rate list, which
         * is exactly what the cooldown cannot read.
         */
        if (!lineAmountTouched.current) {
          const seeded = toDocumentLineAmountType(company.xeroSalesLineAmountType ?? '');
          if (seeded !== null) {
            setValues((prev) =>
              prev.xeroLineAmountType === seeded ? prev : { ...prev, xeroLineAmountType: seeded },
            );
          }
        }
      })
      .catch(() => {
        /*
         * See above — nothing seeded is a working form, not an error, so this
         * raises no banner.
         */
      });
    return () => controller.abort();
    /*
     * ⚠️ `cycles` is a real dependency, not a lint appeasement. It arrives one
     * render after the company does, and without it the subject would keep
     * whatever it was seeded with before the business years were known — the
     * cycle segment silently missing on every quote created in the first
     * moment of the form. The re-run costs at most one extra `getCompany`; the
     * seeds are idempotent (each compares before it writes) and every one of
     * them is still stopped by its own `…Touched` ref.
     *
     * `isEdit` flips exactly once, when a create becomes an edit of the record
     * it just made — which re-reads the company for the sales-defaults row and
     * takes the early return before any seed.
     */
    // `isConverting` joins the list because the guard above reads it. It is
    // constant for the life of this component -- the modal is KEYED on the deal
    // -- so it can never re-run this effect on its own.
  }, [companyLookupId, cycles, isEdit, isConverting]);

  /*
   * ── The Draft stage, seeded onto a NEW quote ────────────────────────────
   *
   * A quote starts life in Draft rather than with an empty Stage box. The row
   * is found by its own identity in the loaded master — see {@link
   * draftStageOf}, which explains why that is its NAME and why no id may be
   * written down.
   *
   * An effect rather than the `useState` initialiser, and it has to be: the
   * stage list is fetched, so it is empty on the first render and the seed can
   * only be applied once it lands. Which is also why the guard is a ref and not
   * "is the field empty" — by the time the list arrives the person may have
   * already picked something, or deliberately cleared the box back to "No
   * stage", and an emptiness test would silently undo both.
   *
   * ⚠️ **Never on an EDIT**, for the same reason as every other seed: a stored
   * stage is the record's own, and a quote sitting in `Sent` must not be
   * dragged back to Draft because a form re-opened. `isEdit` also covers the
   * moment after a create, when this form becomes an edit of the record it
   * just made.
   *
   * No Draft row — a list still loading, a list that failed, an organisation
   * that renamed the stage — leaves the field unset. That is the whole
   * instruction: guessing at another stage is how a quote gets created as
   * something the customer was never sent.
   */
  const stageTouched = useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (stageTouched.current) return;
    const draft = draftStageOf(stages);
    if (draft === null) return;
    const next = String(draft.id);
    setValues((prev) => {
      // Re-checked inside the updater: a pick made between the render that
      // scheduled this and the render that applies it must still win.
      if (stageTouched.current || prev.stageId !== '') return prev;
      return { ...prev, stageId: next };
    });
  }, [stages, isEdit]);

  /*
   * ── The organisation's default TERMS, seeded onto a NEW quote ────────────
   *
   * The Terms box has always been empty on a create, so the wording the server
   * applies when the key is omitted was never visible — and people typed it out
   * again by hand. This puts the real text in the box, where it can be read and
   * edited before the customer sees it.
   *
   * ⚠️ **A PLACEHOLDER IS NOT A VALUE, and that is the whole reason this
   * exists.** The box already carries `"Payable within 30 days…"` as grey
   * placeholder text; a placeholder submits nothing, cannot be edited, and is
   * not what the document ends up saying. Darkening it would have made the lie
   * more convincing rather than less. The fix is a real value in `values.terms`.
   *
   * ⚠️ **`useQuoteTermsDefault` hands back the RESOLVED default, never the
   * configured one** — read its header. The two differ exactly when nothing is
   * configured, which is the commonest case and the one the seed exists for.
   *
   * ⚠️ **An EMPTY default seeds NOTHING.** Not the string `"null"`, not a
   * placeholder, not a space. `defaultTerms === null` is "still in flight, or the
   * read failed" and the hook is deliberately silent about the second — a failed
   * read costs visibility and not correctness, because the create omits `terms`
   * and the server applies the same default itself. A blank-after-trim string is
   * an organisation that has deliberately configured no terms, and writing
   * whitespace into the box on its behalf would be inventing a value.
   *
   * ⚠️ **NEVER ON AN EDIT**, which is why the hook is passed `!isEdit` and the
   * effect checks it again. Terms on a stored quote are prose somebody worded
   * for a customer and may already have sent; overwriting them because the
   * ORGANISATION default has since moved is silent data loss on a
   * customer-facing field. `isEdit` also covers the moment after a create, when
   * this form becomes an edit of the record it just made. Passing `false`
   * additionally means no request is made at all.
   *
   * ⚠️ **A CONVERT DOES seed, unlike the four company-derived seeds above.**
   * Those are skipped because `deal-convert.ts` has already decided the subject,
   * the currency and the rest. `d_deals` has no terms column, so the conversion
   * decides nothing here — the same reasoning that has `xeroLineAmountType`
   * seeded into the convert VALUES rather than its baseline, and `terms` is in
   * {@link CONVERT_FOLLOW_UP_PATCH_KEYS}, so what the box shows is what the
   * follow-up PATCH writes.
   *
   * An effect rather than the `useState` initialiser, for the reason the Draft
   * stage seed above gives: the value is FETCHED, so it is absent on the first
   * render and can only be applied once it lands.
   */
  const termsTouched = useRef(false);
  const { defaultTerms } = useQuoteTermsDefault(!isEdit);
  useEffect(() => {
    if (isEdit) return;
    if (termsTouched.current) return;
    if (defaultTerms === null || defaultTerms.trim() === '') return;
    setValues((prev) => {
      // Re-checked inside the updater, as the stage and contact seeds are: an
      // edit made between the render that scheduled this and the render that
      // applies it must still win. `prev.terms !== ''` is the belt to
      // `termsTouched`'s braces — it also covers a value arriving from anywhere
      // this seed does not know about.
      if (termsTouched.current || prev.terms !== '') return prev;
      return { ...prev, terms: defaultTerms };
    });
  }, [defaultTerms, isEdit]);

  /*
   * Pins for the three searched pickers, memoised so the option lists are not
   * rebuilt on every keystroke elsewhere in the form.
   */
  /*
   * One pin, two sources. A locked company wins over the record's own: it is
   * why the form is open, and on a create it is the ONLY name available —
   * there is no record to read one from.
   */
  /*
   * ⚠️ A SEEDED link needs pinning exactly as a stored one does, and this is
   * the half of a prefill that is easy to leave out and impossible to see is
   * missing. `values.companyId` holding an id selects nothing until a page of
   * the directory happens to return that company — so an unpinned seed renders
   * as "Unassigned" over a form that is in fact carrying the id, and the person
   * either believes nothing was filled in or clears a field that was never
   * empty. The pin is what makes the seed legible.
   *
   * The record's own link wins where there is one: on an edit the stored value
   * is the truth, and the prefill is create-only in `values` anyway.
   */
  const companyPin = useMemo(() => {
    if (lockedCompany) return { id: lockedCompany.id, label: lockedCompany.name };
    if (quote?.company) return { id: quote.company.id, label: quote.company.name };
    return prefill?.company ? { id: prefill.company.id, label: prefill.company.name } : null;
  }, [quote, lockedCompany, prefill]);
  /**
   * Whether the company has moved away from the one this form opened on.
   *
   * Derived, not stored, so moving back to the original company restores the
   * original contact's pin rather than leaving the field permanently narrowed by
   * a flag nothing resets.
   */
  const companyMoved = values.companyId !== openedCompanyId;
  /**
   * The seeded primary, but only while it describes the company now chosen.
   *
   * Derived, so a company change drops the previous account's primary with no
   * write at all — see the note on `seededContact`.
   */
  const seededContactPin =
    seededContact !== null && seededContact.companyId === companyLookupId
      ? seededContact.contact
      : null;
  /**
   * The company sales tax as the GRID is able to know it — the same tag test as
   * the pin above, and for the same reason.
   *
   * ⚠️ **A tag mismatch is `known: false`, never `rate: null`.** Not read, read
   * and failed, and read for a DIFFERENT company are one fact: *we cannot say*.
   * Reporting any of them as “this company charges no tax” would put a claim
   * about a customer’s tax treatment on screen that no response supports.
   *
   * ⚠️ **Memoised, and that is not a micro-optimisation.** The grid depends on
   * this object's identity to decide whether to rebuild every row's money
   * preview; a fresh literal per render would recompute the whole map on every
   * keystroke in this form's header fields. `companyTaxRate` only changes when
   * a company read lands, so this changes then and not otherwise.
   */
  const companySalesTax = useMemo<CompanySalesTax>(
    () =>
      companyTaxRate !== null && companyTaxRate.companyId === companyLookupId
        ? { known: true, rate: companyTaxRate.rate, taxType: companyTaxRate.taxType }
        : { known: false },
    [companyTaxRate, companyLookupId],
  );
  /*
   * ⚠️ THREE sources, and the SEED is the one that is easy to leave out.
   *
   * The stored contact wins: on an edit it is the truth, and it must stay
   * offered even when it is not among the company's own — a contact dropped
   * from the list renders "Unassigned" over a record that holds one, and the
   * next save writes that emptiness back with a 200.
   *
   * The seeded primary needs pinning for the mirror-image reason given on
   * `companyPin` above: `values.contactId` holding an id selects nothing until
   * an option carries that value, and the scoped list has not landed yet at the
   * moment the seed is applied.
   */
  const contactPin = useMemo(() => {
    /*
     * ⚠️ The stored and prefilled pins are withheld once the company has MOVED.
     * Same rule as `intakeOptions`, which pins the stored intake only while the
     * cycle is still the stored one — and for a sharper reason: there the
     * stale choice is a guaranteed 422, here it is a guaranteed silent success.
     */
    if (!companyMoved) {
      if (quote?.contact) return { id: quote.contact.id, label: quote.contact.name };
      if (prefill?.contact) return { id: prefill.contact.id, label: prefill.contact.name };
    }
    return seededContactPin ? { id: seededContactPin.id, label: seededContactPin.name } : null;
  }, [quote, prefill, seededContactPin, companyMoved]);
  // Deals key their title `dealName`, not `name` — the one resource here that
  // does not follow the convention. A prefill has already resolved that, so its
  // `deal.name` is whatever the caller decided to call the deal.
  const dealPin = useMemo(() => {
    if (quote?.deal) return { id: quote.deal.id, label: quote.deal.dealName };
    return prefill?.deal ? { id: prefill.deal.id, label: prefill.deal.name } : null;
  }, [quote, prefill]);

  /*
   * A locked company needs no directory. The field is read-only, so fetching a
   * page of 100 companies would fill a picker nobody can open.
   * ⚠️ *SUPERSEDED: "would spend a request out of the 120-per-60-seconds budget
   * the whole office shares."* It is **12,000/60s** (corrected 2026-09-17); the
   * request is skipped because nothing can use it, not to save the budget.
   */
  /*
   * ⚠️ **`!isEdit && !isConverting` — the withholding is CREATE-ONLY**, and the
   * two exclusions are for different reasons.
   *
   * On an EDIT the rule does not apply at all: a quote already raised against a
   * company since rejected must stay editable, and narrowing the picker would
   * also make it impossible to move such a quote onto a company somebody is
   * still getting approved. Over-offering is the safe direction on a field whose
   * refusal is ours rather than the server's.
   *
   * On a CONVERT the field is read-only — the company is the DEAL's, copied
   * server-side — so a filtered list would narrow a control nobody can open, and
   * the owner ruled explicitly that a deal with no company stays convertible.
   */
  const companies = useLookupSearch(
    'companies',
    companyPin,
    lockedCompany === undefined,
    undefined,
    !isEdit && !isConverting,
  );
  /*
   * ⚠️ SCOPED. `contactCompanyId` is `null` when the form names no company, and
   * the picker is then EMPTY rather than falling back to the whole directory —
   * an unscoped list is the behaviour the owner asked to remove, and it is the
   * only way to pick a contact the company will contradict. `contactHint` says
   * why. Same shape as the intake picker's dependence on the cycle.
   */
  const contacts = useLookupSearch('contacts', contactPin, true, companyLookupId);
  const deals = useLookupSearch('deals', dealPin);

  /**
   * Whether the company has moved away from the one this form opened on, and
   * so whether a stored contact still describes this document.
   *
   * `values.contactId === ''` is part of the "cleared" sentence and not of the
   * pin gate: once somebody has chosen a replacement there is nothing to
   * explain, but the old contact must stay off the list either way.
   */
  const contactScope = { ...contacts.scope, cleared: companyMoved && values.contactId === '' };

  /**
   * The chosen company as `{id, name}`, for the Contact field's inline
   * "+ Add a new contact…" row to lock a new contact to.
   *
   * ⚠️ The name is read from the SAME option list the company control draws its
   * own label from, so the two can never disagree — if the field on screen names
   * a company, this resolves it, and if it does not there is nothing to lock to.
   * That covers the locked and converting modes as well, where no search runs
   * and the pinned row is the whole option list.
   */
  const contactCompany = useMemo(() => {
    if (companyLookupId === null) return null;
    const label = companies.options.find((option) => option.value === values.companyId)?.label;
    return label === undefined ? null : { id: companyLookupId, name: label };
  }, [companyLookupId, companies.options, values.companyId]);

  /**
   * The scoped options, with the pinned contact MARKED when it is not one of
   * this company's.
   *
   * ⚠️ The marking is gated on `contactScopeIsAuthoritative`; the OPTION never
   * is. While the list is loading, unreadable, or narrowed by a search term,
   * every stored contact looks absent from it — and accusing a perfectly correct
   * contact of belonging to another account on the strength of a request that
   * has not come back is a confident wrong answer where saying nothing costs
   * nothing. Same gate as `productOptions`' "Retired" reason in
   * `ProjectFormModal`.
   *
   * The pin is `options[0]` by construction (the hook unshifts it), but this is
   * written as a set difference rather than an index so it cannot quietly stop
   * marking the right row if that ever changes.
   */
  const markOffCompany = contactScopeIsAuthoritative(contactScope) && contacts.pinInjected;
  const pinnedContactValue = contactPin === null ? null : String(contactPin.id);
  const contactOptions = useMemo<ComboboxOption[]>(() => {
    if (!markOffCompany || pinnedContactValue === null) return contacts.options;
    return contacts.options.map((option) =>
      option.value === pinnedContactValue ? { ...option, hint: CONTACT_NOT_ON_COMPANY } : option,
    );
  }, [contacts.options, markOffCompany, pinnedContactValue]);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => {
      /*
       * ⚠️ Guard one of three: a locked total is not even a CHANGE.
       *
       * The inputs are not rendered while `totalsLocked`, so a total can only
       * differ from the baseline because the record moved underneath — which
       * is exactly what a line write does, since it rewrites all four in its
       * own transaction. Counting that as the user's edit would light up Save,
       * and `buildPayload` would then refuse to send it and report "Nothing
       * has changed yet" over a form that plainly says it has. Dropping the
       * key here is the honest reading: the lines changed it, not the person.
       */
      if (totalsLocked && (TOTAL_KEYS as readonly string[]).includes(key)) return false;
      return values[key].trim() !== baseline[key].trim();
    });
  }, [values, baseline, totalsLocked]);
  const isDirty = changedKeys.length > 0;

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /*
   * Changing the currency CLEARS any pinned exchange rate.
   *
   * ⚠️ Found in production on a real Xero document, not in review. An invoice
   * was set to AUD with a rate of 1.890950 — correct for AUD — and its currency
   * was then changed to INR. The rate survived, so Xero printed
   * "GBP 6,398.90" against a total of 12,100 INR. The true figure is about
   * GBP 93: overstated roughly SIXTY-NINE TIMES, on a document a customer
   * could have received.
   *
   * The arithmetic was right the whole way through. A rate is meaningless
   * without the PAIR it describes, and nothing was tying the two fields
   * together — so a number that was true of one currency was quietly applied
   * to another. It is the same family as "1 GBP = 0 AUD" that
   * `currency-conversion.ts` refuses to print: a rate that looks measured and
   * is arithmetic nonsense.
   *
   * Clearing loses a rate somebody typed, which is a real cost. Keeping it is
   * worse: an absent rate prints nothing and hands the document to Xero's own
   * daily rate, while a stale one prints a confident wrong answer. Only clear
   * on an actual CHANGE — re-picking the same currency must not wipe the rate.
   */
  /**
   * Changing the company CLEARS the contact, and hands the field back to the
   * primary-contact seed.
   *
   * ⚠️ **The server will not catch this one.** `quotes.service.ts` checks a
   * contact through `contactsExist` — does the row exist — and never against the
   * company; the pair that `deliverables.service.ts` refuses is accepted here
   * with a `200`. So a contact left behind by a company change is not a 422
   * somebody reports, it is a stored, plausible, wrong person on a document that
   * reaches a customer, and this function is the only thing standing in front of
   * it.
   *
   * Resetting `contactTouched` is the second half and is what makes the seed
   * correct on an EDIT: the previous choice was a choice about the previous
   * company, so it stops counting the moment that company goes. Without it the
   * field would be cleared and then left empty, which is worse than either end
   * — the person loses a value AND is offered nothing in its place.
   *
   * ⚠️ Only on an actual CHANGE. Re-picking the same company must not discard a
   * contact somebody deliberately chose, the same way `onCurrencyChange` below
   * only clears the rate when the code really moved.
   */
  function onCompanyChange(next: string) {
    if (next === values.companyId) return;
    contactTouched.current = false;
    // `seededContact` is NOT cleared here: it carries the company it was read
    // for, so it simply stops resolving. One writer, no second place to forget.
    setValues((prev) => ({ ...prev, companyId: next, contactId: '' }));
    setClientErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
    setServerErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
  }

  /**
   * The ONLY writer of `contactTouched` besides `onCompanyChange` above.
   *
   * Set even when the box is CLEARED, because clearing is a decision: without
   * that, emptying the contact would hand the field straight back to the seeder,
   * which refills it on the next company change and reads as the control
   * fighting the user. Same rule as `currencyTouched` and `subjectTouched`.
   */
  function onContactChange(next: string) {
    contactTouched.current = true;
    setField('contactId', next);
  }

  function onCurrencyChange(next: string) {
    /*
     * The ONE writer of `currencyTouched` — see the company seed above. It is
     * set even when the code is unchanged and even when it is cleared to '':
     * both are decisions, and a seed that overwrote either would be choosing a
     * currency on somebody's behalf on a document a customer is billed in.
     */
    currencyTouched.current = true;
    const code = next.toUpperCase();
    const changed = code !== values.currency;
    setField('currency', code);
    if (changed && values.currencyRate.trim() !== '') setField('currencyRate', '');
  }

  /**
   * The issue date moved, and Valid till follows it while it is still ours.
   *
   * ⚠️ **{@link validTillFollowsIssue} is the whole of the rule, and one
   * control turns it off.** The default is issue date + {@link
   * DEFAULT_VALID_FOR_DAYS}, which is only a useful default if it TRACKS the
   * date it is derived from — someone backdating a quote to last Monday means a
   * quote valid for thirty days from last Monday, not one that quietly expired
   * sooner than the box says. The instant they set a validity date themselves,
   * it is theirs and nothing here moves it again.
   *
   * ⚠️ **Inferring "untouched" from the value would be wrong in both
   * directions**, which is why it is tracked explicitly: a blank Valid till is
   * a real, deliberate state (an open-ended quote), and a value that merely
   * happens to equal issue date + 30 may have been typed on purpose.
   *
   * ⚠️ **CREATE ONLY**, because the flag starts false on an edit. Moving a
   * stored validity date because an issue date was corrected is exactly the
   * silent rewrite every other seed in this form refuses.
   *
   * A half-typed or cleared issue date changes nothing: with no real day to
   * derive from there is no follow-on to compute, and blanking a validity date
   * the person can still see would look like the form eating a field.
   * `isRealDate` is the same test `validate` uses, so the two cannot disagree
   * about what a day is.
   */
  function onIssueDateChange(next: string) {
    setField('issueDate', next);
    if (!validTillFollowsIssue) return;
    if (!isRealDate(next)) return;
    const follows = addDaysIso(next, DEFAULT_VALID_FOR_DAYS);
    setValues((prev) => (prev.validTill === follows ? prev : { ...prev, validTill: follows }));
    setClientErrors((prev) => ('validTill' in prev ? without(prev, 'validTill') : prev));
    setServerErrors((prev) => ('validTill' in prev ? without(prev, 'validTill') : prev));
  }


  /*
   * The response carries resolved objects (`stage`, `owner`, `deal`) where the
   * write takes ids, plus read-only audit columns, so a loaded quote can never
   * be PATCHed back wholesale — every schema is `.strict()`, and an unknown key
   * is a 422 rather than a silent strip. Only changed keys go.
   *
   * The acting user is never sent: the server takes it from the bearer token,
   * and any actor key is now a 422.
   */
  /**
   * The PATCH body for a given set of changed keys.
   *
   * ⚠️ **Split out so the CONVERT follow-up cannot word a write differently from
   * an ordinary edit.** Both go to `PATCH /quotes/{id}` and both have to obey
   * the same three rules that are easy to get subtly wrong: `''` on an id key is
   * an explicit `null` that CLEARS, the two NOT NULL totals are omitted rather
   * than nulled, and the two dates travel together or not at all. A second
   * implementation of this for the convert path would agree on the day it was
   * written and drift on the first change to any of the three.
   *
   * The keys are passed in rather than read off `changedKeys` because the
   * convert path sends a NARROWER set — bounded by
   * {@link CONVERT_FOLLOW_UP_PATCH_KEYS} — and the narrowing is the caller's
   * decision.
   */
  function buildUpdatePayload(keys: readonly (keyof FormValues)[]): QuoteUpdateInput {
      const payload: QuoteUpdateInput = {};
      for (const key of keys) {
        const next = values[key].trim();
        if (key === 'subject') {
          // Not nullable server-side: `subject: null` is a 422.
          payload.subject = next;
        } else if ((ID_KEYS as readonly string[]).includes(key)) {
          payload[key as (typeof ID_KEYS)[number]] = next === '' ? null : Number(next);
        } else if (key === 'currency') {
          // Uppercased on the way out as well as on the way in: a code pinned
          // back from an older record could still be stored in lower case.
          payload.currency = next === '' ? null : next.toUpperCase();
        } else if (key === 'currencyRate') {
          /*
           * ⚠️ An explicit `null` CLEARS the pin, and that is the intended
           * meaning of an emptied rate — the "Remove" control writes '' here,
           * and clearing hands the document back to Xero's own daily rate.
           * That is safe in a way the two NOT NULL totals are not: `currency_rate`
           * is genuinely nullable, so `null` is stored as null rather than
           * silently taking a column default.
           *
           * It reaches this branch by name rather than through `TEXT_KEYS`
           * because it is not text: it must not be length-checked against
           * `MAX_LENGTHS`, and it is validated for decimal shape instead.
           *
           * Never sent when unchanged — the diff is what stops a quote that
           * carries a pinned rate losing it because somebody edited its
           * subject.
           */
          payload.currencyRate = next === '' ? null : next;
        } else if ((TOTAL_KEYS as readonly string[]).includes(key)) {
          const totalKey = key as (typeof TOTAL_KEYS)[number];
          /*
           * ⚠️ Guard two of three: the header NEVER writes a total the LINES
           * own.
           *
           * Neither the inputs nor `changedKeys` can reach here while
           * `totalsLocked`, so nothing should — but this is the one place in
           * the form where a bug costs real money rather than a re-save. A
           * header PATCH writes literally what it is sent and never
           * re-derives; the figures it replaces are kept nowhere; and
           * `convert` copies all four onto the invoice verbatim, so a header
           * that disagrees with its lines is inherited by a document the
           * customer is billed against. The guard is therefore structural: no
           * path through this function can put a totals key in a PATCH while
           * the lines are in charge.
           */
          if (totalsLocked) continue;
          // An emptied NOT NULL total is caught by `validate` before we get
          // here; omitting it is the belt to that pair of braces, so a bug
          // upstream can never turn into a silent zero on a financial record.
          if (next === '' && UNCLEARABLE_TOTALS.includes(totalKey)) continue;
          payload[totalKey] = next === '' ? null : next;
        } else if ((DATE_KEYS as readonly string[]).includes(key)) {
          // Handled once, just below: both dates travel together or not at all.
          continue;
        } else if (key === 'xeroLineAmountType') {
          /*
           * Narrowed rather than cast. The control can only produce the three
           * DOCUMENT members, but the baseline comes from the server, and a
           * value outside the union must never be smuggled into a write
           * claiming to be one. Anything unrecognised becomes `null`, which
           * says "fall back to the company's Xero sales default" — the honest
           * reading of an empty control, and never "no tax".
           */
          payload.xeroLineAmountType = toDocumentLineAmountType(next);
        } else {
          payload[key as (typeof TEXT_KEYS)[number]] = next === '' ? null : next;
        }
      }

      // Either date changing sends both, so the server has the pair to compare.
      // See the note on DATE_KEYS: a one-sided PATCH skips the check entirely.
      if (DATE_KEYS.some((key) => keys.includes(key))) {
        payload.issueDate = values.issueDate.trim() === '' ? null : values.issueDate.trim();
        payload.validTill = values.validTill.trim() === '' ? null : values.validTill.trim();
      }

      return payload;
  }

  /**
   * The follow-up write a CONVERT needs, and nothing more.
   *
   * Empty is the ORDINARY case on a form nobody edited beyond what
   * {@link convertValuesFromDeal} seeds — and even then it is not empty, because
   * those three seeds differ from the baseline by design. Whatever it holds,
   * nothing outside {@link CONVERT_FOLLOW_UP_PATCH_KEYS} can get into it.
   */
  function buildConvertFollowUpPatch(): QuoteUpdateInput {
    const allowed = CONVERT_FOLLOW_UP_PATCH_KEYS as readonly string[];
    return buildUpdatePayload(changedKeys.filter((key) => allowed.includes(key)));
  }

  function buildPayload(): QuoteCreateInput | QuoteUpdateInput {
    if (quote) return buildUpdatePayload(changedKeys);

    const payload: QuoteCreateInput = { subject: values.subject.trim() };
    for (const key of ID_KEYS) {
      if (values[key] !== '') payload[key] = Number(values[key]);
    }
    for (const key of DATE_KEYS) {
      if (values[key] !== '') payload[key] = values[key];
    }
    if (values.currency.trim() !== '') payload.currency = values.currency.trim().toUpperCase();
    /*
     * Omitted when unset, never sent as an explicit `null`. On a create the two
     * read the same to the server — no pin either way — and omission is what
     * this app sends for every unset field. ⚠️ It is emphatically not defaulted
     * to `"1"`: a pin of 1 is a rate somebody negotiated, and inventing one on
     * every create would put a fabricated parity on documents whose currencies
     * are nowhere near it.
     */
    if (values.currencyRate.trim() !== '') payload.currencyRate = values.currencyRate.trim();
    /*
     * ⚠️ Guard three of three, on the CREATE branch.
     *
     * Same rule as the PATCH: the header only carries totals when the lines
     * are not the owner. A create can only be `totalsLocked` when the caller
     * could not tell — an unknown line count fails safe — and a quote created
     * from this form has no lines at the instant of the POST, so the four
     * boxes are genuinely writable up to that point and are locked the moment
     * it returns.
     */
    if (!totalsLocked) {
      for (const key of TOTAL_KEYS) {
        const next = values[key].trim();
        if (next !== '') payload[key] = next;
      }
    }
    for (const key of TEXT_KEYS) {
      /*
       * ⚠️ **Kept over an EMPTY list, on purpose** — see
       * {@link CREATE_SUPPRESSED_KEYS}. It skips nothing today, and it is one of
       * that constant's three enforcement sites: dropping the `continue` because
       * the list is empty is how the next suppressed key gets wired in two
       * places out of three, which is a silent "one field needs attention" over
       * nothing highlighted.
       *
       * ⚠️ **`quoteNumber` is not covered by this and must not be added back
       * to it.** It is not a `TEXT_KEYS` member and not a `FormValues` member,
       * so this loop cannot reach it on either branch — which is the stronger
       * closure, and the one the read-only server now requires. A key skipped
       * here is still a key the edit branch would send.
       */
      if (CREATE_SUPPRESSED_KEYS.includes(key)) continue;
      const next = values[key].trim();
      if (next !== '') payload[key] = next;
    }
    /*
     * Omitted when unset, never sent as an explicit `null`. On a create the two
     * read the same to the server, and omission is what this app sends for
     * every unset field — but on the two tracking keys the distinction is worth
     * keeping straight in one's head: the stored `null` they land on means
     * "derive from the project", not "no value".
     */
    const lineAmountType = toDocumentLineAmountType(values.xeroLineAmountType.trim());
    if (lineAmountType) payload.xeroLineAmountType = lineAmountType;
    return payload;
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    const subject = values.subject.trim();
    if (subject === '') found.subject = 'Subject is required.';
    else if (subject.length > MAX_LENGTHS.subject) {
      found.subject = 'Must be 255 characters or fewer.';
    }

    /*
     * ── A NEW QUOTE NEEDS A COMPANY ─ and that half really is ours ──────────
     *
     * ⚠️ **APPROVAL IS NO LONGER CHECKED HERE — corrected 2026-08-31.** This
     * block used to refuse an unapproved company in sentences of our own, on the
     * stated premise that the server did not. It does: `assertCompanyApproved`
     * (migration 099) runs inside `createQuote` and answers a **409** whose
     * message is better than either sentence we had. It is read by
     * `billingGateRefusalOf` and bannered by `reportFailure`.
     *
     * What remains is genuinely this form's own: `companyId` is nullable on the
     * wire and **the guard skips a document naming no company**, so without this
     * line a company-less quote saves.
     *
     * ⚠️ **CREATE ONLY, and both exclusions are deliberate.**
     * - An EDIT of a quote with no company has to keep working. Refusing there
     *   would strand a real document nobody can correct.
     * - A CONVERT is excluded because the owner ruled explicitly that a deal
     *   with no company stays convertible — 24 of 40 deals have none — **and the
     *   backend agrees**: the guard is called with the deal's company and skips
     *   when there is none. The company on that path is read-only in any case,
     *   so the refusal would name a control the reader cannot reach. **Do not
     *   let this rule reach the convert path.**
     */
    if (!isEdit && !isConverting) {
      const refusal = newQuoteCompanyError(values.companyId);
      if (refusal !== null) found.companyId = refusal;
    }

    for (const key of TEXT_KEYS) {
      if (values[key].trim().length > MAX_LENGTHS[key]) {
        found[key] = `Must be ${MAX_LENGTHS[key].toLocaleString()} characters or fewer.`;
      }
    }

    const currency = values.currency.trim().toUpperCase();
    if (currency !== '' && !CURRENCY_PATTERN.test(currency)) {
      found.currency = 'Use a three-letter ISO code, e.g. USD.';
    }

    /*
     * ⚠️ Kept even though the dialog will not let an invalid rate out of
     * itself, and the reason is the second writer: the SERVER can still name
     * `currencyRate` on a 422 — a rate with seven decimal places from an older
     * record, or a schema that tightens later — and `knownWriteKeys` claims
     * this form can mark it. That claim is only true because the rate note
     * under the Currency picker renders `errors.currencyRate`. Marking a key
     * with nowhere on screen to show it is the "one field needs attention,
     * highlighted below" lie with nothing highlighted; the two halves have to
     * stay together.
     */
    const currencyRate = values.currencyRate.trim();
    if (currencyRate !== '' && !isValidCurrencyRate(currencyRate)) {
      /*
       * "Above zero" is in the sentence because zero is the refusal a reader
       * would not otherwise predict: the SERVER accepts `0` — its rule is
       * non-negative — and `isValidCurrencyRate` is deliberately stricter,
       * because a zero rate divides every base-currency total to `0.00` beside
       * a real document total. A message naming only the decimal places would
       * leave someone re-typing `0.000000` at a control that keeps refusing it.
       */
      found.currencyRate = `Use a rate above zero with at most ${CURRENCY_RATE_MAX_DP} decimal places, e.g. 1.89207.`;
    }

    // Both the shape and the existence of the day, exactly as the server checks.
    for (const key of DATE_KEYS) {
      const value = values[key].trim();
      if (value !== '' && !isRealDate(value)) {
        found[key] = 'Must be a real date in YYYY-MM-DD form.';
      }
    }

    /*
     * Compared as strings, never as Dates. ISO dates sort lexicographically so
     * the comparison is exact, whereas parsing 'YYYY-MM-DD' through `new Date()`
     * reintroduces the timezone off-by-one this format exists to avoid.
     * Reported at `validTill`, which is where the server puts it.
     */
    const issueDate = values.issueDate.trim();
    const validTill = values.validTill.trim();
    if (
      found.issueDate === undefined &&
      found.validTill === undefined &&
      issueDate !== '' &&
      validTill !== '' &&
      issueDate > validTill
    ) {
      found.validTill = 'Cannot be before the issue date.';
    }

    // Nothing to check once the lines own the totals: the inputs are not
    // rendered, and nothing money-shaped is in the payload either.
    if (!totalsLocked) {
      for (const key of TOTAL_KEYS) {
        const next = values[key].trim();
        if (next !== '' && !AMOUNT_PATTERN.test(next)) {
          found[key] = 'Use up to 12 digits and at most 2 decimal places, e.g. 1800.00.';
        } else if (
          next === '' &&
          isEdit &&
          UNCLEARABLE_TOTALS.includes(key) &&
          baseline[key].trim() !== ''
        ) {
          // The "the column stores 0.00, never nothing" half is dropped: it explains
          // the refusal rather than changing what to do about it.
          found[key] = `${TOTAL_LABELS[key]} cannot be emptied — enter a figure, or put the previous one back.`;
        }
      }
    }

    return found;
  }

  /**
   * Everything a failed save does, minus the acceptance 409, which both callers
   * handle before they get here.
   *
   * One copy, because the ordinary save and the acknowledged retry must report
   * an identical failure identically — two hand-written copies of this drift,
   * and the half that drifts is always the rarer path.
   */
  function reportFailure(caught: unknown) {
    if (caught instanceof ApiError) {
      /*
       * ── THE COMPANY-APPROVAL GATE ─ checked FIRST, and bannered verbatim ──
       *
       * `assertCompanyApproved` (migration 099) refuses `createQuote` against a
       * company that is `pending`, `rejected`, or carrying a status the server
       * cannot read. Three codes, matched through `billingGateRefusalOf`.
       *
       * ⚠️ **The server's sentence, and nothing of ours in front of it.** It
       * names the company, says an approver has been emailed a link, and says
       * everything else on that company still works — all three of which this
       * form would have to invent, and two of which it cannot know.
       *
       * ⚠️ **FORM LEVEL, never `serverErrors.companyId`.** The 409 carries no
       * `details` on any call site, deliberately: half of them reach the gate
       * with a company the caller never sent. Keying it to a field would make
       * `fieldErrors` non-empty and produce "one field needs attention,
       * highlighted below" with nothing highlighted.
       *
       * ⚠️ **Explicit rather than left to the fall-through below.** A
       * details-less 409 already lands on `setFormError(caught.message)` by way
       * of an empty `fields` and an empty `unroutable` — correct today, but
       * correct by accident of the envelope's shape. The day the server attaches
       * a `path` to this refusal, the fall-through would silently start routing
       * it to a control. This branch is what makes the banner a property of the
       * code.
       *
       * An unrecognised code returns `null` here and drops through to the
       * server's own message anyway: newer than this branch, not "not a refusal".
       */
      const gate = billingGateRefusalOf(caught);
      if (gate !== null) {
        setServerErrors({});
        setFormError(gate.message);
        return;
      }
      /*
       * ⚠️ There is no `quoteNumber` collision handler here — it is allocated
       * by the server and refused on both create and PATCH, so this form
       * never sends it and no collision on it can be attributed to anything a
       * person typed. Nothing this form sends carries a UNIQUE index: neither
       * `xeroQuoteNumber` nor `xeroQuoteId` is a key here regardless.
       *
       * So every 409 falls through to the general path below and speaks in
       * the server's own words — a create or an update raises no 409 this
       * form can attribute to a control, and the resource's other one, a
       * repeat `convert`, comes from `api.convertQuote` and never reaches this
       * handler.
       */
      /*
       * Split against this form's OWN field list, never against the shape of
       * the path. `fields` is safe to mark AND to count; `unroutable` is
       * everything no input here can carry — the EMPTY-path `.strict()` and
       * "at least one field" refusals, plus any path naming a key with no
       * control on this form. Rendering it at form level is the only way it
       * is seen at all, and it is never counted as highlighted.
       *
       * ⚠️ A 422 naming `quoteNumber` lands in `unroutable` and is BANNERED
       * rather than silently dropped. That is the honest outcome for a key this
       * form cannot mark, and it is how a regression that started sending the
       * key again would announce itself instead of highlighting nothing.
       */
      const { fields, unroutable } = caught.routeFieldErrors(knownWriteKeys(isEdit));
      setServerErrors(fields);
      if (unroutable.length > 0) setFormError(unroutable.join(' '));
      else if (Object.keys(fields).length === 0) setFormError(caught.message);
    } else {
      setFormError('Could not reach the server. Check your connection and try again.');
    }
  }

  /**
   * Writes the draft lines against a quote that now exists.
   *
   * ⚠️ **One request per line, run one at a time, and it does NOT stop at the
   * first failure.** There is no bulk endpoint and no transaction across them,
   * so a partial result is an ordinary outcome rather than an edge case. Two
   * decisions inside that are worth stating:
   *
   * - **Sequential, not `Promise.all`.** The office shares a per-IP rate
   *   limit, `sortOrder` is what the customer's document prints in, and a
   *   `(product, project)` clash between two rows must be reported against the
   *   second one rather than raced. Concurrency would buy nothing here.
   *   ⚠️ *SUPERSEDED: "a 120-per-60-seconds rate limit."* It is **12,000/60s**
   *   (corrected 2026-09-17), so the limiter is no longer a reason for any
   *   basket this form can build. ⛔ **The other two still are** — the order is
   *   what the customer's document prints in, and a raced clash is reported
   *   against the wrong row. **Do not read the corrected figure as licence to
   *   parallelise.**
   * - **A refused line does not abandon the rest.** The lines are independent;
   *   stopping would lose four good rows to one bad one, and the person would
   *   have to work out which of them had landed.
   *
   * Nothing is rolled back and nothing pretends to be. The quote exists from
   * the moment the create returned, and deleting it to "undo" a refused line
   * would destroy the part that worked.
   */
  async function writeDraftLines(
    quoteId: number,
    lines: QuoteProductCreateInput[],
  ): Promise<DraftLineOutcome> {
    const failures: DraftLineOutcome['failures'] = [];
    let saved = 0;
    /*
     * ⚠️ Overwritten by every success, so what survives is the LAST one — see
     * {@link DraftLineOutcome.lastXeroPush}. A refused line leaves it alone
     * rather than clearing it: the previous line's push is still the most recent
     * true statement about the document, and blanking it would report silence
     * about a push that did happen.
     */
    let lastXeroPush: XeroPushReport | undefined;

    for (const [index, line] of lines.entries()) {
      try {
        const written = await api.createQuoteProduct(quoteId, line);
        saved += 1;
        if (written.xeroPush !== undefined) lastXeroPush = written.xeroPush;
      } catch (caught) {
        failures.push({ position: index + 1, message: lineFailureMessage(caught) });
      }
    }

    return { attempted: lines.length, saved, failures, lastXeroPush };
  }

  /**
   * Write the note again, against the quote that now exists.
   *
   * ⚠️ **Not a rollback and not a repair of the save** — the quote was created
   * and stays created either way. This is the plain `POST /notes` a person would
   * otherwise make from the quote's own Tasks & notes drawer, offered here only
   * because the text they typed is already on screen and retyping it is the one
   * thing they should not have to do.
   *
   * Reachable only after `created` is set, so `quote` is a real record; the
   * guard is a ref rather than the `savingNote` state because a second click
   * arrives before a re-render and would otherwise write the note twice.
   */
  async function retryDraftNote() {
    if (noteLock.current) return;
    const target = quote;
    if (!target) return;

    noteLock.current = true;
    setSavingNote(true);
    try {
      const result = await writeDraftNote({ kind: 'quote', id: target.id }, noteDraft);
      setNoteOutcome(result);
      /*
       * Cleared only when it landed, exactly as the create path does: a refused
       * note exists nowhere but this box.
       */
      if (result?.kind === 'saved') setNoteDraft('');
    } finally {
      noteLock.current = false;
      setSavingNote(false);
    }
  }


  /*
   * ⚠️ **`pushCreatedToXero` IS GONE. THE SERVER PUSHES ON EVERY SAVE.**
   *
   * *SUPERSEDED, and it was ~110 lines: "Send a quote this form has just CREATED
   * to Xero, and report what happened without ever implying the save did not …
   * **This is the LAST push this app makes on a save, and it is deliberate.**
   * The edit branch's `carryOnToXero` was deleted on 2026-08-30 when the server
   * took the push over; this one stays, because the server's push never CREATES
   * and this is the create. The owner's ruling is that “Create quote” is the
   * button that puts a document into Xero for the first time."*
   *
   * **The premise died: the server's push CREATES now.** With it gone, the
   * owner's recorded decision finally holds — saving pushes as a property of the
   * SYSTEM rather than of one screen, so it is true of a line-item edit, an
   * importer, a script and any second client, none of which go through this
   * form.
   *
   * ⚠️ **LEFT IN PLACE IT WAS NOT A DUPLICATE DOCUMENT — IT WAS A DOUBLE
   * SPEND.** The server's own document lock stops two Xero quotes; what it does
   * not stop is two round trips per save against a tenant budget of 60/minute
   * and 5,000/day shared by the whole office, and two outcomes reported for one
   * action. That is the reason this is a deletion rather than a tidy-up.
   *
   * ⚠️ **THE COOLDOWN PRE-CHECK WENT WITH IT, and that is a real loss worth
   * naming.** This function read `xeroCooldownSecondsRemaining()` and refused to
   * spend a call while a session-wide rate-limit deadline was live, and it
   * called `noteXeroRateLimit` to RECORD a limit learned from a push so the
   * reference-data reads would stop asking too. Neither can be done from here
   * any more: the push happens inside the save, and refusing to save because
   * Xero is rate-limited would be refusing to store the person's work over
   * somebody else's quota. `xero-cooldown.ts` keeps working for the reads and
   * for the manual push on the quote's own page.
   *
   * What reports the push now: the `xeroPush` sibling of `data` on the save
   * response — see {@link createPushFromServer}.
   */

  /**
   * ── SAVE, on the convert path: two writes, and only the first is a convert ──
   *
   * ⚠️ **`POST /deals/{id}/convert` takes NO HEADER fields**, so the header
   * edits on this form cannot ride along with it. The conversion runs FIRST,
   * copying every line item with its project, cities and campaign inclusions in
   * one server-side transaction; the edits follow as `PATCH /quotes/{newId}`,
   * bounded by {@link CONVERT_FOLLOW_UP_PATCH_KEYS}.
   *
   * ⚠️ **CORRECTED 2026-08-30.** *SUPERSEDED: "takes NO fields — its body schema
   * is `z.object({}).strict()`".* It takes a LINE key, and that is the one key
   * this function DOES send with the conversion. Everything else about the
   * two-write shape is unchanged.
   *
   * ⚠️ **CORRECTED AGAIN — THE KEY IS `lineSet`, AND IT IS A DIFFERENT
   * INSTRUCTION.** *SUPERSEDED: "It takes **`lines`** … the per-line figure
   * overrides, built once above so the array index a 422 names can be turned
   * back into a row."* `lines` respells rows the server copies anyway; `lineSet`
   * IS the list of lines the quote will have, so a row missing from it is a line
   * the quote will not have. Sending both is a 422.
   *
   * ⚠️ **THAT CHANGES WHAT THE SECOND WRITE IS FOR, AND ONE WHOLE PHASE IS
   * GONE.** *SUPERSEDED, from the body of this function: "── PHASE TWO: the
   * ADDED lines, and they run BEFORE the header PATCH ── ⚠️ **A NEW line cannot
   * ride on the conversion, and no arrangement of this form can change that.**
   * Every entry in `lines` is keyed by `dealLineId` … The only route it has is
   * the ordinary one: `POST /quotes/{id}/products`, against the quote the
   * conversion has just created … **The cost, stated rather than hidden:** the
   * conversion is atomic and this is not."* A new line rides on the conversion
   * now, as a `lineSet` entry with no `dealLineId`, so there are no
   * `POST /quotes/{id}/products` calls on this path at all and **the
   * non-atomicity that phase introduced is gone with it.** The two writes are
   * once again the conversion and the header PATCH.
   *
   * ⚠️ **THERE IS NO TRANSACTION ACROSS THE TWO, AND NO ROLLBACK. THESE ARE TWO
   * REQUESTS.** If the convert lands and the PATCH is refused, the quote EXISTS
   * — real, with the deal's line items copied onto it — carrying the DEAL'S
   * ORIGINAL HEADER and none of the corrections made above. Reporting that as a
   * failed save is the specific harm: the person closes the modal believing
   * nothing happened and converts again, and `POST /deals/{id}/convert` answers
   * no 409, so the second attempt succeeds and the deal now has two quotes.
   *
   * So the failure is SURFACED rather than swallowed: it names the quote, links
   * to it, and offers the PATCH again — never the conversion.
   */
  async function runConvert(dealId: number, preview: boolean) {
    submitLock.current = true;
    setSubmitting(true);
    setConvertError(null);
    setConvertPush(null);
    setConvertHeaderRefused(false);
    setLineServerErrors({});

    /*
     * ⚠️ Already-converted means RETRY THE PATCH, never convert again. This is
     * the state after a refused follow-up, and the one thing that must not
     * happen from here is a second `POST /deals/{id}/convert`.
     */
    let target = convertedQuote;
    /*
     * ⚠️ **Declared OUTSIDE the conversion branch, because a retry does not
     * convert.** Coming back through here with `convertedQuote` already set, no
     * `POST /deals/{id}/convert` is made and there is no new report to describe
     * — `undefined` is then the honest answer, and the PATCH's own report below
     * is the only one there is. Declaring it inside would simply not compile;
     * defaulting it to the previous attempt's report would re-assert a refusal
     * about a push that this attempt never made.
     */
    let convertPushed: XeroPushReport | undefined;
    if (target === null) {
      /*
       * ⚠️ **Read into a local ONCE, because these two arrays are the only thing
       * that can turn `lineSet.2.quantity` back into a row**: the index is a
       * position in WHAT WAS SENT, and `rowKeys` is the grid's own answer to
       * which row that position came from. Reading the state again inside the
       * catch would work today and would stop working the moment a keystroke
       * landed between the two.
       *
       * ⚠️ **THE KEY IS OMITTED ON A PRISTINE SET, AND NEVER SENT AS `[]`.** An
       * absent key is today's proven behaviour — the deal's lines cross verbatim
       * — where `lineSet: []` is **a quote with no lines at all**. That is the
       * one mistake on this endpoint that produces a real, empty, customer-facing
       * document with a 201 and nothing to notice, so the test is on the grid's
       * own `pristine` verdict rather than on a length: `entries.length === 0`
       * is a legitimate answer here (somebody deleted every row) and reading it
       * as "nothing changed" would silently restore them all.
       *
       * ⚠️ **`null` is the unread basket and it also omits the key.** A
       * conversion fired before the read lands copies the deal's lines verbatim,
       * which is exactly what it did before any of this existed.
       */
      const sent = convertLineSet;
      const entries = sent?.entries ?? [];
      const rowKeys = sent?.rowKeys ?? [];
      const body: DealConvertInput =
        sent === null || sent.pristine ? {} : { lineSet: entries };
      try {
        /*
         * ⚠️ **THE WHOLE ENVELOPE, NEVER `(await …).data`.** This call answers
         * `{ data, xeroPush }`, and the `.data` that stood here discarded the
         * second half at the expression — which `tsc` cannot see, because
         * reading one member of a returned object is not an error.
         *
         * ⚠️ **`xeroPush` DESCRIBES THE QUOTE, NOT THE DEAL IN THE URL.**
         * `deals.controller` calls `autoPushAndRefresh('quotes', quote.id, …)`
         * on the document it just created, so the report below is built with
         * `'quotes'`. Passing the URL's entity would label a refusal about a
         * quote as one about a deal — and there is no deal entity in the Xero
         * integration at all, so it would name a record that can never be there.
         */
        const raised = await api.convertDeal(dealId, body);
        target = raised.data;
        convertPushed = raised.xeroPush;
      } catch (caught) {
        /*
         * ⚠️ A per-line 422 is MARKED ON THE BOX as well as bannered. Left as a
         * banner alone it names a path — `lineSet.2.discountAmount` — that no
         * reader can map onto a row, and the form refuses to convert while
         * pointing at nothing.
         */
        if (caught instanceof ApiError) {
          const routed = convertLineSetFieldErrors(caught.fieldErrors, rowKeys);
          setLineServerErrors(routed.rows);
          /*
           * ⚠️ **Ordered by SPECIFICITY, never by which shape arrived first** —
           * the house rule, and the one `CompaniesPage` was silently broken by
           * when a better-shaped 422 landed on an endpoint it already handled.
           *
           * `unrouted` first: it carries a refusal on `dealLineId` or on
           * `sortOrder` — both ours to get right, with no control anywhere — and
           * anything else this branch has not been taught about. Dropping it is a
           * form that refuses to convert and explains itself nowhere visible.
           *
           * ⚠️ Then, when rows WERE marked and nothing is unrouted, a sentence
           * pointing AT THE TABLE rather than the server's own. A 422's
           * `message` is only "Validation failed", and the marks are inside a
           * table that may be scrolled off — so silence, which is what the
           * header path correctly does when its own fields are marked, would
           * read here as a button that did nothing.
           */
          if (routed.unrouted.length > 0) setConvertError(routed.unrouted.join(' '));
          else if (Object.keys(routed.rows).length > 0) {
            setConvertError('Some line items were refused — they are marked below.');
          } else setConvertError(convertRefusal(caught));
        } else {
          setConvertError(convertRefusal(caught));
        }
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
    }

    /*
     * ── PHASE TWO IS GONE, AND ITS ABSENCE IS THE CHANGE ────────────────────
     *
     * *SUPERSEDED, the whole block that stood here: "── PHASE TWO: the ADDED
     * lines, and they run BEFORE the header PATCH ── ⚠️ **A NEW line cannot ride
     * on the conversion, and no arrangement of this form can change that.**
     * Every entry in `lines` is keyed by `dealLineId` — it is an OVERRIDE of a
     * row the deal already has — so a line that was never on the deal has
     * nothing to key to and no way to be expressed in that body. The only route
     * it has is the ordinary one: `POST /quotes/{id}/products` … **The cost,
     * stated rather than hidden:** the conversion is atomic and this is not. A
     * convert that lands followed by a refused added line leaves a REAL quote
     * carrying the deal's own lines and missing one somebody typed … ⚠️
     * **Guarded by `addedLinesWritten`**, so a retry of a refused PATCH does not
     * append the same rows a second time."*
     *
     * **Its premise was true and is not any more.** A `lineSet` entry with no
     * `dealLineId` IS an added line, so an added line rides on the conversion,
     * inside the one transaction that creates the quote. With it go the
     * `POST /quotes/{id}/products` calls, the per-line partial-failure report,
     * the `addedLinesWritten` re-entry guard and — the part worth saying out
     * loud — **the non-atomic window that block existed to apologise for.** A
     * conversion is now all-or-nothing again: either the quote exists with every
     * line on it, or nothing was created at all.
     *
     * ⚠️ **`writeDraftLines` and `draftLines` still exist and are still used**,
     * by the CREATE path — a quote made from a blank form has no conversion to
     * ride on and still writes its lines one at a time after the create. Do not
     * read this removal as a statement about that path.
     */

    const patch = buildConvertFollowUpPatch();
    if (Object.keys(patch).length === 0) {
      /*
       * Nothing to correct. `onSaved` is what takes the caller to the quote —
       * the response is a QUOTE, never a deal, and the caller navigates to it.
       *
       * ⚠️ **UNLESS XERO DID NOT TAKE IT.** `onSaved` navigates
       * (`DealDetailPage` pushes `/quotes/{id}`), so a band set here would be
       * unmounted before anybody read it — and the quote's own page cannot show
       * the failure either, because the server wrote nothing and re-read nothing
       * on that branch, leaving it indistinguishable from a quote nobody has
       * pushed. The modal is the only surface that outlives the request, so it
       * STAYS OPEN to say so, exactly as `InvoiceFormModal` does on the other
       * three endpoints. `convertedQuote` is what stops the next Save converting
       * a second time.
       */
      const pushReport = conversionPushReport('quotes', target.subject, convertPushed);
      if (pushReport !== null) {
        setConvertedQuote(target);
        setConvertPush(pushReport);
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
      submitLock.current = false;
      if (preview && onCreatedPreview) onCreatedPreview(target);
      else onSaved(target);
      onClose();
      return;
    }

    try {
      const after = await api.updateQuote(target.id, patch);
      /*
       * ⚠️ NO `pushCreatedToXero` on this path, and the absence is deliberate
       * rather than unfinished. Xero refuses a quote with no lines and this one
       * is a second old; more to the point the owner's rule is that nothing
       * writes a NEW document into SEED's accounting system on somebody's
       * behalf. The quote's own page is where it is sent.
       *
       * ⚠️ **CORRECTED 2026-08-30.** This also said "NO `carryOnToXero`". That
       * function is deleted: the server pushes inside the PATCH now, so this
       * line has ALREADY been to Xero — and it still cannot create, because the
       * server refuses to create on a save for the same reason stated above.
       * The absence that remains is only the absence of a REPORT, which this
       * conversion path has nowhere to render.
       */
      /*
       * ⚠️ **THE HEADER PATCH'S REPORT WINS, BECAUSE IT IS THE LAST WRITE.** It
       * pushed too — the server pushes inside every save — so it describes the
       * quote as it finally stands, where the conversion's own report describes
       * the version that existed a second earlier, before these corrections.
       * The conversion's is the fallback for a server that sent none.
       *
       * ⚠️ **And it can CLEAR the conversion's failure rather than repeat it.**
       * A convert whose push was refused followed by a PATCH whose push landed
       * is a quote that IS in Xero, and re-reporting the first would be a false
       * alarm about a document in the accounts.
       */
      const pushReport = conversionPushReport(
        'quotes',
        after.data.subject,
        after.xeroPush ?? convertPushed,
      );
      /*
       * ⚠️ Same rule as above, one step later: the header landed, and a refused
       * push still must not be reported by closing the form. "Everything saved"
       * and "saved, and Xero has not got it" must never look alike.
       *
       * ⚠️ **`convertHeaderRefused` is NOT set here.** The header was accepted;
       * only the push was refused. The band below reads that flag rather than
       * its own presence, so this branch renders "was created" without the
       * "your header changes were refused" sentence that would send somebody to
       * correct a field that is not marked and was never wrong.
       */
      if (pushReport !== null) {
        setConvertedQuote(after.data);
        setConvertPush(pushReport);
        setValues(toFormValues(after.data));
        setSubmitting(false);
        submitLock.current = false;
        return;
      }
      if (preview && onCreatedPreview) onCreatedPreview(after.data);
      else onSaved(after.data);
      onClose();
    } catch (caught) {
      /*
       * ⚠️ Recorded BEFORE anything is rendered, because it is what turns the
       * form into "the quote exists, its edits did not land" rather than leaving
       * Convert live over a deal that has already been converted.
       */
      setConvertedQuote(target);
      /*
       * ⚠️ The ONE place this is set: the header PATCH was refused. The band
       * reads it rather than inferring the cause from `convertedQuote`, which
       * now has two causes and cannot tell them apart.
       */
      setConvertHeaderRefused(true);
      reportFailure(caught);
      setSubmitting(false);
    } finally {
      submitLock.current = false;
    }
  }

  /**
   * Why a conversion was refused, in one sentence.
   *
   * ⚠️ **Nothing of ours is put in front of the server's own message**, and
   * there is deliberately no default destination: an unrecognised refusal means
   * "newer than this branch", never "not a refusal". The one exception is a
   * `403`, which carries no `details` at all and can only ever be a banner — and
   * is reachable although the button rendered, because a role can change under a
   * live access token.
   *
   * ⚠️ **THE COMPANY-APPROVAL GATE RUNS ON THIS PATH TOO.** `deal-convert.ts`
   * calls `assertCompanyApproved` on the DEAL's company for both conversions, so
   * all three billing-gate codes arrive here. They are matched rather than left
   * to the final `return caught.message` — which would render them identically
   * today — so that this branch cannot start swallowing them the day the server
   * attaches `details`, and so the reason it is a banner is written down.
   *
   * ⚠️ **A deal naming NO company is skipped by the guard, not refused**, which
   * is the owner's ruling and the backend's behaviour agreeing. Nothing here
   * blocks a company-less conversion.
   */
  function convertRefusal(caught: unknown): string {
    if (!(caught instanceof ApiError)) {
      return 'Could not reach the server, so nothing was converted.';
    }
    if (caught.status === 403) {
      return (
        denialReason(ability, 'create', 'quotes') ??
        'Your access changed since this page loaded, so nothing was converted.'
      );
    }
    const gate = billingGateRefusalOf(caught);
    if (gate !== null) return gate.message;
    return caught.message;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    /*
     * Contained, per the house rule on nested modals: React propagates by its
     * own tree rather than the DOM one, so a form rendered inside another
     * form's subtree submits both. This form is currently the outer one, and
     * the guard costs nothing while making that a property of the code rather
     * than of where it happens to be mounted.
     */
    event.stopPropagation();

    /*
     * ⚠️ Read and written SYNCHRONOUSLY, before anything awaits. See the note
     * on `submitLock`: on a create, a second click that gets past here is a
     * second quote, not a retry.
     */
    if (submitLock.current) return;

    /*
     * Which submit was pressed. Read from the native `submitter` rather than a
     * click-set ref, so Enter (implicit submission → the default "Create quote"
     * button) can never inherit an earlier "Create and preview" click. Only a
     * CREATE can preview — `quote` is set from the moment a create lands.
     */
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const preview =
      onCreatedPreview !== undefined &&
      quote === undefined &&
      submitter instanceof HTMLElement &&
      submitter.dataset.intent === 'preview';

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    /*
     * ⚠️ AFTER `validate`, and that ordering is the whole safety of seeding
     * `validTill` from the deal's closing date: a closing date already in the
     * past is refused HERE, on a form where nothing exists yet, rather than as a
     * 422 against a quote the conversion has already created.
     */
    if (convertDeal !== undefined) {
      await runConvert(convertDeal.id, preview);
      return;
    }

    const payload = buildPayload();
    /*
     * ⚠️ **"Nothing has changed" IS A CLAIM ABOUT THE WHOLE FORM, so it may not
     * be made from the header payload alone.** An edit that touched only line
     * items builds an empty payload and is not an empty edit; saying otherwise
     * here is the same falsehood the disabled button used to tell, one layer
     * down, and it would survive every gate above it.
     */
    const linesToSave = quote !== null && lineEdits.dirty > 0;
    if (Object.keys(payload).length === 0 && !linesToSave) {
      setFormError('Nothing has changed yet.');
      return;
    }

    submitLock.current = true;
    setSubmitting(true);
    /*
     * ⚠️ Hoisted OUT of the `try` on purpose: it is how the catch below can
     * tell "the create failed" from "the create succeeded and something after
     * it did not".
     *
     * Nothing between the POST returning and `setCreated` can throw today —
     * `writeDraftLines` catches per line and the re-read is guarded — but if
     * anything ever does, reporting it as a failed create would leave the form
     * still offering "Create quote" over a quote that already exists, and the
     * next click would make a second one. That is the single most expensive bug
     * this form can have, so it is closed structurally rather than by the
     * absence of a `throw`.
     */
    let createdQuote: Quote | null = null;
    try {
      if (quote) {
        /*
         * ⚠️ **LINES FIRST, HEADER SECOND, and the order is arithmetic rather
         * than taste.** Every line write rewrites the quote's four header
         * totals inside its own transaction; a header PATCH writes exactly what
         * it is given and never re-derives. Header-first would therefore let a
         * line save overwrite figures the person had just typed, with a 200 and
         * nothing on screen to say so.
         *
         * ⚠️ **The grid still reports its own run, row by row, and this must
         * not restate it.** What is added here is only the ONE thing the grid
         * cannot know: whether the form may close.
         */
        const linesLanded = linesToSave ? ((await saveLinesRef.current?.()) ?? false) : true;
        if (Object.keys(payload).length === 0) {
          /*
           * Line items only. `onSaved` is the callers' "reload and close"
           * signal and they re-read the record, so handing up the header as it
           * stands is right — the totals it carries were rewritten by the
           * writes that just ran, and this form is not the place they are read
           * back from.
           */
          if (!linesLanded) {
            /*
             * ⚠️ **`setSubmitting(false)` OR THE WHOLE FORM FREEZES.** It is
             * cleared in the `catch` and at the end of the create branch, and
             * every other non-throwing path here ends in `onClose()` — so a
             * stuck flag has never been reachable before. These two returns are
             * the first that report something and STAY OPEN, and while
             * `submitting` is true Save renders `loading` (which `Button` makes
             * disabled) and `finish()` returns early, so Cancel is dead too.
             * That is a form frozen behind a message telling somebody to go and
             * fix a row: the same defect this change exists to remove, one
             * screen further in.
             */
            setSubmitting(false);
            setFormError(LINES_NOT_SAVED);
            return;
          }
          onSaved(quote);
          onClose();
          return;
        }
        const result = await api.updateQuote(quote.id, payload);
        /*
         * ⚠️ **CORRECTED 2026-08-30. Nothing is sent to Xero from here any
         * more — the PATCH did it.**
         *
         * This read `await carryOnToXero(result.data);`, an awaited second
         * request whose doc explained at length that it *"cannot unmake the
         * save"*, that its `catch` was the guarantee rather than tidiness, and
         * that it was awaited so the form would not close under its own report.
         * `carryOnToXero` is deleted with it.
         *
         * ⚠️ **The reasoning it carried has not gone away — it has moved into
         * the server, and the shape it warned about is now IN THIS REQUEST.**
         * The save and the push are still two acts with no transaction across
         * them; the difference is that one response now reports both. So the
         * old rule stands with one word changed: nothing about `xeroPush` may
         * be worded as the save having failed. The quote is stored either way,
         * and this line has already proved it.
         *
         * Handing the report up is synchronous and cannot throw, so there is
         * nothing left to await and nothing left to swallow.
         */
        onXeroPush?.(result.xeroPush);
        /*
         * ⚠️ **A REFUSED LINE MUST NOT CLOSE A FORM THAT SAVED THE HEADER — and
         * `onSaved` IS a close.** The callers read it as "reload and close"
         * (`handleSaved` on `/quotes` calls `closeForm()` first thing), so
         * calling it here would unmount this form and take the grid's per-row
         * report with it: the person would be back at the list, told nothing,
         * with the header saved and their line edits gone from the screen.
         *
         * `setCreated` instead. It is what `refreshQuote` already does with a
         * re-read of an existing quote, so it is the established way to hold a
         * fresher copy — and `finish()` hands `created` to `onSaved` when the
         * person does close, so the list still reloads. Nothing is lost; the
         * signal is deferred to the moment they are actually finished.
         */
        if (!linesLanded) {
          setCreated(result.data);
          // See the note on the other new return: without this the form freezes.
          setSubmitting(false);
          setFormError(HEADER_SAVED_LINES_NOT);
          return;
        }
        onSaved(result.data);
        onClose();
      } else {
        /*
         * ⚠️ A CREATE DOES NOT CLOSE THE FORM, and does not call `onSaved`.
         *
         * A line item is `POST /quotes/{id}/products` — nested, no bulk
         * endpoint — so no line can exist until the quote does. Closing here
         * would send someone back to the list to reopen the record they were
         * halfway through, and `onSaved` is the callers' signal to close, so
         * it is held back until this form is genuinely finished.
         *
         * What happens instead: the form becomes an EDIT of the record that
         * just came back. `created` feeds `quote`, so the baseline, the diff,
         * the pins and the PATCH all follow it without a second code path —
         * and the line-items table below mounts against a real id.
         */
        const result = await api.createQuote(payload as QuoteCreateInput);
        createdQuote = result.data;

        /*
         * ⚠️ PHASE TWO, and it runs BEFORE `setCreated`.
         *
         * The order is deliberate. `setCreated` flips the grid below from draft
         * mode to saved mode, and saved mode's first act is to list the quote's
         * lines — which, at this instant, is an empty list. Writing the lines
         * first means the one read it does is the read that already has them,
         * instead of an empty grid that fills in afterwards and looks for a
         * moment as though the drafts were thrown away.
         *
         * The outcome is captured either way. A refused line does not stop the
         * quote existing, and this form must say so rather than leaving someone
         * to conclude nothing was saved and press Create again.
         */
        const outcome =
          draftLines.length > 0 ? await writeDraftLines(result.data.id, draftLines) : null;
        /*
         * ⚠️ The LAST line write's report, and it is what the push note renders
         * — see the phase-three block below. `undefined` when no line was
         * written or when the server sent no report, and both fall back to the
         * create's own.
         */
        const lineXeroPush = outcome?.lastXeroPush;

        /*
         * ⚠️ PHASE TWO ALSO — a third request, and a third separate fact.
         *
         * A note is `POST /notes` with a `quoteId`, so like a line it cannot
         * exist before the quote does. It is written here, after the lines and
         * before the re-read, so everything the person pressed Save for has
         * been attempted by the time the form reports on it.
         *
         * ⚠️ It cannot fail this create. `writeDraftNote` catches everything and
         * returns the reason — letting a rejection escape would land in the
         * catch below, which reports a FAILED create over a quote that plainly
         * exists, and the next click would raise a second one. An empty box
         * returns `null` having sent nothing: `body: ''` is a 422, and "no note
         * was typed" must not be reported as "a note was refused".
         */
        const noteResult = await writeDraftNote({ kind: 'quote', id: result.data.id }, noteDraft);

        /*
         * Re-read when anything landed. ⚠️ Every line write rewrites the four
         * header totals in the same transaction and **returns none of them**,
         * so the record the create handed back is stale in exactly the four
         * places that matter. Silent on failure, like `refreshQuote`: the
         * outcome report has already told the truth about the lines, and a red
         * block here would read as the lines having failed.
         */
        let saved = result.data;
        if (outcome !== null && outcome.saved > 0) {
          try {
            saved = (await api.getQuote(saved.id)).data;
          } catch {
            /* a stale header beats an error about a refresh */
          }
        }

        setCreated(saved);
        setJustCreated(true);
        setLineOutcome(outcome);
        setNoteOutcome(noteResult);
        /*
         * ⚠️ Cleared ONLY when it actually landed. A refused note keeps its text
         * on screen, because that text is the only copy of it anywhere — the
         * request failed, so nothing was stored — and clearing the box to tidy
         * up would destroy what the person wrote while telling them to try
         * again.
         */
        if (noteResult?.kind === 'saved') setNoteDraft('');
        /*
         * The drafts are no longer drafts. Whatever landed is now the grid's
         * own saved state, and whatever did not is named by position in the
         * report — holding on to the array would write it twice on the next
         * save.
         */
        setDraftLines([]);
        setBlockedDraftRows(0);
        /*
         * Re-seeded from the RECORD, not left as typed. The server normalises
         * (a currency is stored upper-case, an omitted total lands on its
         * column default), and a baseline that disagrees with the values would
         * light up Save over changes nobody made.
         */
        setValues(toFormValues(saved));
        /*
         * ⚠️ **PHASE THREE IS A READ OF WHAT THE SERVER ALREADY DID, NOT A
         * PUSH.** *SUPERSEDED: "⚠️ PHASE THREE, and it runs LAST — after the
         * quote exists and after its draft lines have been written. Xero refuses
         * a quote with no lines, so the order is not a preference: pushing any
         * earlier would refuse on every create that has line items … ⚠️ **This
         * is the LAST push this app makes on a save, and it is deliberate.** The
         * edit branch's `carryOnToXero` was deleted on 2026-08-30 when the
         * server took the push over; this one stays, because the server's push
         * never CREATES and this is the create."* The server's push creates now,
         * so `await pushCreatedToXero(saved)` is gone and **this app makes no
         * Xero call on any save.**
         *
         * ⚠️ **THE ORDERING ARGUMENT SURVIVES AND IS WHY THE LAST LINE'S REPORT
         * WINS.** The create response is a quote with NO LINES — the lines are
         * written afterwards — so its own `xeroPush` is a `skipped`, and
         * rendering it would tell somebody their brand new quote was not sent
         * when the line writes then sent it. Each line write pushes again and
         * reports again, so the LAST report is the only one describing the
         * document as it finally stands. `lineXeroPush` is that; the create's
         * own report is the fallback for a quote with no lines at all, where it
         * is both the only report and the true one.
         */
        const pushOutcome = createPushFromServer(lineXeroPush ?? result.xeroPush, saved);
        /*
         * "Create and preview": only a FULLY successful create leaves for the
         * preview — every draft line landed, the note (if any) landed, and Xero
         * either took the quote or skipped it as an ordinary draft (the same
         * line `conversionPushReport` draws for the convert path). Anything
         * less falls through to the ordinary create below, which stays open
         * and reports it; navigating away would unmount that report.
         */
        if (
          preview &&
          onCreatedPreview &&
          (outcome === null || outcome.failures.length === 0) &&
          (noteResult === null || noteResult.kind === 'saved') &&
          (pushOutcome === null || pushOutcome.kind === 'sent' || pushOutcome.kind === 'not-sent-yet')
        ) {
          setSubmitting(false);
          submitLock.current = false;
          onCreatedPreview(saved);
          onClose();
          return;
        }
        setCreatePush(pushOutcome);
        setSubmitting(false);
      }
    } catch (caught) {
      reportFailure(caught);
      /*
       * ⚠️ The quote may exist even though we are in the failure path — see the
       * note on `createdQuote`. Switching to edit mode is what stops the next
       * click creating a second one, and it is the truth: a record was made,
       * and this form is now editing it. The error above still stands and still
       * says what went wrong.
       */
      if (createdQuote !== null && created === null) {
        setCreated(createdQuote);
        setJustCreated(true);
        setValues(toFormValues(createdQuote));
        setDraftLines([]);
        setBlockedDraftRows(0);
      }
      setSubmitting(false);
    } finally {
      /*
       * Released on every path, including the successful create.
       *
       * That is safe rather than sloppy: by the time this runs `created` is
       * set, so the form is an EDIT of a real record and the next submit can
       * only PATCH it. The one thing the lock exists to prevent — a second
       * `POST /quotes` — is unreachable from here on. Holding it would instead
       * freeze Save on a form the user still has to finish.
       */
      submitLock.current = false;
    }
  }

  /*
   * One controller for the header re-reads, aborted on unmount.
   *
   * The re-read is fired from the line table's callback rather than from an
   * effect, so there is no cleanup function to hang it on — a ref is what
   * makes "the modal closed while this was in flight" a no-op rather than a
   * setState on an unmounted form.
   */
  const refreshRef = useRef<AbortController | null>(null);
  useEffect(() => () => refreshRef.current?.abort(), []);

  /**
   * Re-reads the quote after a line write.
   *
   * ⚠️ **Every line write rewrites the four header totals in the same
   * transaction and returns none of them**, so the record this form is holding
   * is stale in exactly the four places that matter the moment a line saves.
   * The read-only totals block renders from the record, so without this it
   * would keep showing the figures from before the line landed.
   *
   * Deliberately silent on failure. The table has already reported the line
   * write truthfully; a red block here would be about a refresh, and would read
   * as the line having failed. The figures simply stay as they were until the
   * quote is opened again.
   */
  function refreshQuote() {
    const target = quote;
    if (!target) return;
    refreshRef.current?.abort();
    const controller = new AbortController();
    refreshRef.current = controller;
    api
      .getQuote(target.id, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCreated(result.data);
      })
      .catch(() => {
        /* see above — a stale header is better than a misleading error */
      });
  }

  /**
   * Closes a form that created something.
   *
   * `onSaved` rather than `onClose`, because the callers treat `onSaved` as
   * "there is a new or changed record — reload and close" and `onClose` as
   * "nothing happened". A quote was created; the list has to hear about it
   * even though the last thing the user did was add a line.
   *
   * ⚠️ **Refused outright while a save is in flight**, which Escape and the
   * close button can otherwise reach — the footer buttons are disabled, they
   * are not. A create with line items is several requests deep by then: closing
   * mid-run would unmount this form while the quote already exists and the
   * remaining lines are still being written, and the caller would never be told
   * to reload. The person would be looking at a list with no quote in it and a
   * quote in the database.
   */
  function finish() {
    if (submitting) return;
    /*
     * ⚠️ **THE PUSH-RETRY GUARD IS GONE WITH THE RETRY.** *SUPERSEDED: "A RETRY
     * push is not covered by `submitting` — it runs from the report band,
     * outside any save — and closing under it would unmount this form while an
     * inline write to SEED's accounting system is still on the wire."* There is
     * no retry and no inline write: the push happens inside the save, so
     * `submitting` covers the whole of it. The NOTE retry below is unaffected
     * and still needs its own guard for exactly the reason this one gave.
     */
    /*
     * ⚠️ Nor is a NOTE retry, for the same reason: it runs from the report band
     * outside any save, and unmounting under it would leave the person never
     * learning whether the second attempt worked either — with the only copy of
     * what they typed going with the form.
     *
     * ⚠️ A CONVERTED quote counts as "something was saved" even when the
     * follow-up PATCH was refused, and this is the branch that stops somebody
     * walking away from a real record without being taken to it. It exists, the
     * deal's lines are on it, and the callers treat `onSaved` as "there is a new
     * record — reload and close".
     */
    if (savingNote) return;
    if (convertedQuote) onSaved(convertedQuote);
    else if (created) onSaved(created);
    else onClose();
  }

  const stageOptions = useMemo(
    () => selectableStages(stages, quote?.stage ?? null),
    [stages, quote],
  );

  /*
   * A SEEDED currency is injected back for the same reason a stored one is: the
   * master list is the currencies still offered, and a deal priced in one that
   * has since been retired would otherwise render blank — reading as "no
   * currency" over a form that is carrying the code, and one save from
   * dropping it.
   *
   * ⚠️ A currency inherited from the COMPANY needs the same injection, and it
   * needs it MORE: a company's `currency.id` is nullable precisely because the
   * legal set is Xero's rather than ours, so a company can perfectly well
   * default to a code `/currencies` has never carried. Offered only while it is
   * still what the field holds — once somebody picks something else it is a
   * code nothing refers to. Same shape as `storedThemeId` below.
   */
  const currencyChoices = useMemo(
    () =>
      currencyOptions(
        currencies,
        quote?.currency ??
          (seededCurrency !== null && seededCurrency === values.currency ? seededCurrency : null) ??
          prefill?.currency?.toUpperCase() ??
          null,
      ),
    [currencies, quote, prefill, seededCurrency, values.currency],
  );

  /*
   * ── The base currency, and what may be converted into it ─────────────────
   *
   * Xero states a rate the way an accountant reads one: `1 GBP = 1.89207 AUD`,
   * base first. So the two codes are not interchangeable and the pair has to be
   * carried together — which is what `ExchangeRate` is for, and why nothing
   * here passes a bare rate string around.
   *
   * ⚠️ **The base currency comes from the TENANT and is never assumed.** It is
   * `GBP` on this organisation today; hardcoding that would print a GBP figure
   * against a Xero organisation that files in something else, which is a wrong
   * number on a financial screen rather than a wrong label. A disconnected Xero
   * therefore means the base currency is genuinely UNKNOWN — a real state with
   * its own sentence below, not a reason to fall back to a guess.
   */
  const documentCurrency = values.currency.trim().toUpperCase() || null;
  const baseCurrency = xero.connection.connected ? xero.connection.baseCurrency : null;

  /**
   * A usable rate, or null — and null covers five quite different situations.
   *
   * Every one of them means the same thing to a RENDERER (there is no converted
   * figure to show), which is why they collapse here; they mean five different
   * things to a READER, which is why `rateState` below keeps them apart. Do not
   * merge the two.
   */
  const exchangeRate = useMemo<ExchangeRate | null>(
    () =>
      /*
       * ⚠️ `exchangeRateOf` and not a hand-rolled check here. Which
       * combinations count as "a real conversion" — same code both sides, an
       * unparseable rate, a rate of zero — is one rule, and this form is the
       * third consumer of it. A second copy would agree until one of them was
       * edited, and the half that drifts is always the one nobody is looking
       * at. The `rateState` below is a separate question (what the READER is
       * told) and is deliberately not folded into it.
       */
      exchangeRateOf({
        baseCurrency,
        documentCurrency,
        currencyRate: values.currencyRate,
      }),
    [documentCurrency, baseCurrency, values.currencyRate],
  );

  /**
   * Which sentence the reader gets under the Currency picker.
   *
   * ⚠️ **These are not variants of one message.** "Xero is not connected" and
   * "no rate is recorded" send someone to two different screens, and
   * "this quote is already in the base currency" is not a gap at all. Collapsing
   * any two of them into "no conversion available" would be the one thing that
   * reads as a fault in the case where nothing is wrong.
   */
  const rateState: 'no-currency' | 'checking' | 'base-unknown' | 'same' | 'unset' | 'set' =
    documentCurrency === null
      ? 'no-currency'
      : xero.loading
        ? 'checking'
        : baseCurrency === null
          ? 'base-unknown'
          : documentCurrency === baseCurrency
            ? 'same'
            : values.currencyRate.trim() === ''
              ? 'unset'
              : 'set';

  /*
   * The project this quote is ALREADY stored against, read straight off the
   * record. Not a selection — there is nothing to select — and deliberately
   * not mirrored into `values`, which is what keeps it out of every payload.
   *
   * ⚠️ `null` here does not prove the quote has no project: the lookup the
   * revenue modules run swallows its own failures. Nothing below asserts it
   * has none.
   */
  /**
   * Hoisted rather than read inline in the JSX, so the narrowing survives the
   * render callback the `Field` primitive takes. Same shape as
   * `DealFormModal`'s `existingProjectName`, for the same reason.
   */
  const headerProjectName = quote?.project?.name ?? null;

  /*
   * 🔴 **THE WHOLE DERIVED-TRACKING READOUT IS GONE — 2026-08-31.** It resolved
   * the header project to a full record and turned its region and intake into
   * the two placeholders and hints under the Location / Tour Type override
   * boxes. With those boxes removed there is nothing left to describe, so
   * `headerProject`, `headerProjectId`, `describeDerived`, `derivedLocation`,
   * `derivedTourType`, `derivedPlaceholder`, `locationHint`, `tourTypeHint` and
   * the whole `useProjectOptions` fetch behind them went with it.
   *
   * ⚠️ **The DERIVATION ITSELF is untouched and still runs.** It is server-side
   * and per line — `resolveLineTracking` prefers the LINE's project and falls
   * back to the document's — and a stored `null` on either column still means
   * *derive*, exactly as it did. What was removed is this form's readout of it
   * and its ability to override it, not the behaviour.
   *
   * ⚠️ **`headerProjectName` above survives and is NOT part of this.** It comes
   * straight off `quote.project.name` and feeds the read-only Project box; it
   * never needed the fetched list.
   *
   * ⚠️ There is no document-level project requirement any more — a document that
   * derives no tracking simply pushes untracked — so there is nothing for a
   * `hasTrackingOverride` flag to be an escape hatch from, and no override left
   * to be one. Do not reinstate the flag, the advice, or the boxes.
   */

  /*
   * ⚠️ **`userOptions` and `staffFootnote` are GONE with the Owner picker.**
   *
   * They built the staff directory the Owner Combobox read from — an option
   * list, a pin for an owner outside the fetched page, and a footnote joining
   * "the list failed" to "the list is truncated". The owner asked for ACCOUNT
   * LEAD in that place, and an account lead is derived through the company on
   * the server: there is no directory to search, no option to pin, and no page
   * to run past, so all three states describe a request this form no longer
   * makes. Do not resurrect them to label the account lead — a name is not a
   * picker.
   */

  /**
   * The account lead's name, or `null` when there is nothing to show.
   *
   * ⚠️ Hoisted out of the JSX rather than narrowed inline, because the field
   * renders through a child-function and TypeScript drops a property narrowing
   * across that boundary. A `!` there would compile, and would be the only
   * thing standing between a `null` lead and a crash.
   *
   * `null` covers both states that must render NOTHING: a create, where there
   * is no record for the server to have resolved one from, and a company with
   * no lead set. Neither is an empty field — an empty labelled field is a
   * claim that the account has no lead.
   */
  const accountLeadName = quote?.accountLead?.name ?? null;

  /*
   * ── THE THREE LOCKED CONVERT-MODE READOUTS ──────────────────────
   *
   * Hoisted out of the JSX rather than narrowed inline, for the reason
   * {@link accountLeadName} above is: `Field` takes a child FUNCTION and a
   * property narrowing does not survive that boundary. A `!` there would
   * compile, and would be the only thing standing between a deal that names no
   * company and a crash.
   *
   * ⚠️ **Each is `null` for "there is nothing to show", never `''`.** The render
   * turns that into {@link NOT_SET}; an empty string would render an empty box,
   * which is a different claim entirely — see that constant.
   *
   * Computed unconditionally because none of them costs anything, and because a
   * value guarded on `isConverting` would be a fourth place this form has to
   * remember which mode it is in.
   */
  const convertCompanyName = convertDeal?.company?.name ?? null;
  /*
   * The currency THAT WILL BE STORED, read from `values` and never from
   * `convertDeal.currency`.
   *
   * They hold the same code today — {@link convertBaselineFromDeal} seeds it
   * from the deal, uppercased — and only one of them stays true if that seed
   * ever changes. A readout of what the form is about to write is the honest
   * one; reading the deal's own column beside it would be a second answer to a
   * question that has one.
   */
  const convertCurrencyCode = values.currency.trim() === '' ? null : values.currency.trim();
  /*
   * The tax treatment's LABEL, taken from the ONE shared vocabulary and never
   * re-worded here — see the import note on `DOCUMENT_LINE_AMOUNT_OPTIONS`,
   * whose two copies once diverged across the halves of a single conversion.
   *
   * ⚠️ **`''` renders as {@link USE_COMPANY_DEFAULT}, NOT as {@link NOT_SET}.**
   * The empty value is a real, choosable answer — "use the company's Xero
   * default" — and calling it "Not set" would report an absence where somebody
   * has an answer. The raw value is the last fallback so a member this branch
   * has not been taught about shows itself rather than vanishing.
   */
  const convertTaxLabel =
    values.xeroLineAmountType === ''
      ? USE_COMPANY_DEFAULT
      : (DOCUMENT_LINE_AMOUNT_OPTIONS.find(
          (option) => option.value === values.xeroLineAmountType,
        )?.label ?? values.xeroLineAmountType);

  /**
   * Whether the converted rows are still live.
   *
   * 🔴 **THEY ARE NOT ONCE `convertedQuote` IS SET, AND THIS IS THE ONE PLACE
   * AN EDITABLE TABLE COULD HAVE LIED.** That state means the conversion LANDED
   * and only the follow-up header PATCH was refused — so the quote exists, its
   * lines are already written from whatever overrides were sent, and
   * `runConvert` deliberately never calls `POST /deals/{id}/convert` a second
   * time. A box left live there is a control somebody types a new price into,
   * presses Save, and watches succeed with the old price on the quote: no
   * error, nothing in a log, and a customer document that disagrees with what
   * was on screen. Editing a line from that point on is the QUOTE's own line
   * grid, which the alert above the table links to.
   *
   * It is handed to the grid as `convertLocked`, which locks every control on
   * every row — including Revert, which would otherwise read as a way to undo a
   * quote that already exists.
   */
  const convertLinesLocked = convertedQuote !== null || submitting;

  /**
   * Whether this form is holding a quote because an ADDED line was refused,
   * rather than because the header PATCH was.
   *
   * ⚠️ **PERMANENTLY `false` ON THE CONVERT PATH NOW, and it is kept for the
   * CREATE path.** *SUPERSEDED: "A refused added line stops phase two BEFORE the
   * PATCH is attempted, so the header edits have not been refused — they have
   * not been sent … this file already carries one such ref
   * (`addedLinesWritten`) that must not be confused with it."* There is no phase
   * two and no `addedLinesWritten`: an added line rides on the conversion, so a
   * refused one means no quote at all and this form never reaches the state this
   * flag discriminates.
   *
   * ⚠️ **Read off `lineOutcome`, not off a second flag** — still the rule, and
   * still live wherever `lineOutcome` is written, which today is the CREATE
   * path alone.
   */
  const addedLinesFailed = lineOutcome !== null && lineOutcome.failures.length > 0;

  /**
   * One row's SERVER errors, dropped as soon as that row is touched.
   *
   * ⚠️ A 422 describes the request that was SENT; the moment somebody edits the
   * row it describes nothing on screen, and leaving it would mark a box that is
   * now correct. The grid's own validator re-runs on every keystroke and fills
   * the gap.
   *
   * ⚠️ **It is driven off the ENTRIES the grid emits rather than off a
   * keystroke**, because the grid owns the boxes now: a row whose entry has
   * changed — or which has been DELETED, so the array no longer holds it at all
   * — is a row whose refusal is stale. Rows whose entry is byte-for-byte what
   * was sent keep theirs: the server's word about an unchanged request is still
   * the current answer.
   *
   * ⚠️ **The lookup is by ROW KEY through the parallel `rowKeys` array**, never
   * by `dealLineId` — see {@link entryFingerprint}. A row somebody ADDED here
   * has no `dealLineId`, and it is the likeliest row of all to be carrying a
   * refusal.
   *
   * ⚠️ **`lastConvertEntries` and `lastConvertRowKeys` are REFS, not the
   * state.** This runs from the grid's own emit effect, so reading the state
   * here would read the value from the render that scheduled it and prune
   * against the wrong baseline.
   */
  function onConvertLinesChange(
    next: { entries: DealConvertLineSetEntry[]; rowKeys: string[]; pristine: boolean } | null,
  ) {
    /*
     * ⚠️ **`null` is "the deal's lines are not read yet" and it CLEARS**, rather
     * than being ignored. Leaving the previous value standing would hold a
     * `lineSet` built from a basket this form is no longer showing.
     */
    if (next === null) {
      if (lastConvertEntries.current.length === 0 && lastConvertRowKeys.current.length === 0) return;
      lastConvertEntries.current = [];
      lastConvertRowKeys.current = [];
      setConvertLineSet(null);
      setLineServerErrors({});
      return;
    }
    const previous = lastConvertEntries.current;
    const previousKeys = lastConvertRowKeys.current;
    if (sameEntries(previous, next.entries)) return;
    lastConvertEntries.current = next.entries;
    lastConvertRowKeys.current = next.rowKeys;
    setConvertLineSet(next);
    setLineServerErrors((current) => {
      const rowKeys = Object.keys(current);
      if (rowKeys.length === 0) return current;
      const kept: Record<string, Record<string, string>> = {};
      for (const rowKey of rowKeys) {
        const row = current[rowKey];
        if (row === undefined) continue;
        if (
          entryFingerprint(previous, previousKeys, rowKey) ===
          entryFingerprint(next.entries, next.rowKeys, rowKey)
        ) {
          kept[rowKey] = row;
        }
      }
      return kept;
    });
  }

  const fieldErrorCount = Object.keys(errors).length;
  /*
   * ⚠️ A blocked draft row disables Save on a create.
   *
   * The grid withholds any row that would be refused — no product, a bad
   * percentage, a city its project does not declare, a `(product, project)`
   * pair another row already claims — and reports the count rather than sending
   * it. Letting Save through anyway would create the quote and drop that row on
   * the floor: the drafts are cleared, saved mode refetches, and the row is
   * simply not there, with nothing on screen to say it ever was.
   * `draftLines.length` alone cannot see this, which is why the count exists.
   *
   * ⚠️ **ONE ROW SET ON THE CONVERT PATH AGAIN, AND THE SUM IS GONE.**
   * *SUPERSEDED, and it had already been corrected once: "The convert path
   * counts BOTH ROW SETS, and it did not until 2026-08-30 … The draft grid IS
   * rendered there now — a conversion may add a line the deal never had … a
   * blocked ADDED row is silently DROPPED — the grid withholds it — so the quote
   * is created and a line the person typed is simply not there … Summing them is
   * right because the sentence beside Save reports a count of rows to correct,
   * and both kinds are rows on this screen."* Before that it read: "The two
   * counts are separate because the two row sets are: the grid is not rendered
   * on the convert path and `blockedDraftRows` is permanently 0 there."
   *
   * **The second grid is gone and the added line is now a row of the FIRST
   * one**, so `blockedDraftRows` is once again permanently 0 on this path — for
   * a different reason than the original sentence gave, which is why that
   * sentence is not simply restored.
   *
   * ⚠️ **The two failure modes it distinguished have COLLAPSED INTO ONE, and
   * this is the improvement worth recording.** A blocked added row used to be
   * *silently DROPPED* — the quote created, the line missing. Every row now
   * travels in `lineSet`, and a refused entry takes the whole conversion with
   * it: no quote at all rather than a quote missing a line somebody typed.
   */
  /*
   * ⚠️ **Zero once the conversion has landed, and that is not leniency.** With
   * `convertedQuote` set the table is locked (see `convertLinesLocked`) and Save
   * re-sends the HEADER PATCH alone — no `lineSet` is built and no line is
   * written a second time. Counting a blocked row there would disable the only
   * control that can finish the job, over boxes the person is no longer allowed
   * to correct.
   */
  const blockedRows = isConverting
    ? convertedQuote === null
      ? convertBlockedRows
      : 0
    : blockedDraftRows;
  /*
   * ⚠️ **`isDirty` IS THE HEADER, AND SAVE IS THE WHOLE FORM.** The two were
   * the same thing until line items became editable inside this modal; treating
   * them as the same afterwards is what produced a Save that refused to save.
   * An edit is now offerable when EITHER half has changed, and the line half is
   * counted through `lineEdits`, which the grid reports.
   *
   * ⚠️ **A blocked row still disables it**, exactly as on a create, and for the
   * same reason: the grid withholds a row it knows would be refused, so letting
   * Save through would close this form over work that was never sent. The
   * sentence in the footer names the count — a bare disabled control here would
   * be the defect this change exists to remove, in a new place.
   */
  const saveDisabled = isEdit
    ? (!isDirty && lineEdits.dirty === 0) || lineEdits.blocked > 0
    : values.subject.trim() === '' || blockedRows > 0;

  /*
   * Locked, or picked. The locked variant is a read-only Input rather than a
   * disabled Combobox: a disabled control reads as "temporarily unavailable"
   * and hides its value behind a muted trigger, where a read-only field states
   * the company plainly and still exposes the name to a screen reader.
   *
   * `values.companyId` carries the id either way, so the payload is identical
   * whichever branch rendered — the lock is presentation, not a second path.
   *
   * ⚠️ **THREE branches now, and the convert one is FIRST deliberately.** A
   * conversion opened from a company page would satisfy `lockedCompany` too, and
   * the two locks are not interchangeable: one says "the page you came from", the
   * other says "the deal that is about to be copied". Ordered the other way the
   * convert hint would never render on exactly the route where it matters most.
   */
  const companyField = isConverting ? (
    /*
     * ⚠️ CONVERT MODE — READ-ONLY. Owner's decision, 2026-08-30.
     *
     * The company is the DEAL'S, copied by `POST /deals/{id}/convert` inside the
     * transaction that creates the quote. Changing it here would put the
     * follow-up header PATCH into a fight with that copy: every LINE would have
     * crossed under one company and the header would name another, with nothing
     * reconciling the two and both documents reading as correct.
     *
     * A read-only Input rather than a disabled Combobox, for the reason the
     * locked branch below already gives — plus a hint, because a sunken box on a
     * control that is a live picker everywhere else in this app reads as a fault
     * rather than as a decision.
     *
     * ⚠️ `error` is still passed. `companyId` is a key this form claims it can
     * mark, and the server can name it on a 422; a marked key with nowhere on
     * screen to show it is the "one field needs attention" lie with nothing
     * highlighted. The hint yields to the error — `Field` renders one or the
     * other — which is the right precedence when something is actually wrong.
     */
    <Field label="Company" error={errors.companyId}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          value={convertCompanyName ?? NOT_SET}
          readOnly
          aria-readonly="true"
          className="bg-surface-sunken text-fg-secondary"
          aria-describedby={describedBy}
        />
      )}
    </Field>
  ) : lockedCompany ? (
    <Field label="Company">
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          value={lockedCompany.name}
          readOnly
          aria-readonly="true"
          // The sunken read-only box already says the company is not editable
          // here, so the reason lives on a title rather than a visible hint.
          title="Locked to the company you opened this form from."
          className="bg-surface-sunken text-fg-secondary"
          aria-describedby={describedBy}
        />
      )}
    </Field>
  ) : (
    <Field label="Company" error={errors.companyId}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          value={values.companyId}
          // Not `setField` — a company change clears the contact with it, and
          // the two have to be one state update. See `onCompanyChange`.
          onChange={onCompanyChange}
          options={companies.options}
          onSearch={companies.setTerm}
          loading={companies.loading}
          emptyOptionLabel="Unassigned"
          searchPlaceholder="Search companies…"
          footnote={companies.footnote}
          invalid={Boolean(errors.companyId)}
          aria-describedby={describedBy}
        />
      )}
    </Field>
  );

  // A failed save must not leave its error hidden behind a collapsed section.
  const totalsErrorCount = TOTAL_KEYS.filter((key) => Boolean(errors[key])).length;
  const totalsExpanded = totalsOpen || totalsErrorCount > 0;
  /*
   * ⚠️ `documentErrorCount` / `documentExpanded` / `DOCUMENT_KEYS` went with the
   * "Xero tracking and references" disclosure on 2026-08-31. They existed to
   * force that section open when a save failed behind it; with no section there
   * is nothing to force open, and `DOCUMENT_KEYS` held exactly the two tracking
   * keys that no longer exist. Totals keep theirs — that disclosure is still
   * here.
   */

  /*
   * The tenant's themes, plus the stored one when the live list no longer
   * carries it. No cached name exists on a quote — there is no
   * `xeroBrandingThemeName` column — so a theme an accountant has since deleted
   * is labelled with its GUID. That reads badly and is still better than
   * dropping it, which renders the control blank, reads as "use the default",
   * and is one stray save away from silently changing the letterhead a
   * customer's PDF is printed on.
   */
  /*
   * ⚠️ A SEEDED id needs exactly the same injection as a stored one. On a
   * create there is no record, so `quote?.xeroBrandingThemeId` is null — and a
   * company default the live list does not carry would leave the control
   * rendering blank, which reads as "organisation default" and is one save
   * away from the silent letterhead change this injection exists to stop. It
   * is offered only while it is still what the field holds; once someone picks
   * something else it is an id nothing refers to.
   */
  const storedThemeId =
    quote?.xeroBrandingThemeId ??
    (seededThemeId !== null && seededThemeId === values.xeroBrandingThemeId ? seededThemeId : null);

  const brandingChoices = useMemo<ComboboxOption[]>(
    () => brandingThemeOptions(brandingThemes.themes, storedThemeId, null),
    [brandingThemes.themes, storedThemeId],
  );

  const xeroUnreachable =
    xero.loading || !xero.connection.connected || brandingThemes.error !== null;
  /*
   * Unreachable does not mean frozen — CLEARING still works. The server
   * validates this field for GUID shape alone and never asks Xero about it, so
   * locking the control whenever the tenant is unreachable would trap a quote
   * on a letterhead it could not be released from, for a request the server
   * would have accepted. Inert only when there is nothing to choose from AND
   * nothing to clear.
   */
  const themeCanOnlyClear = xeroUnreachable && values.xeroBrandingThemeId !== '';

  /**
   * What the one Save actually did, said above the grid it changed.
   *
   * ⚠️ **The whole point is that "the quote was created" and "the lines were
   * created" are separate facts.** They are separate requests with no
   * transaction across them, so a report that collapsed them into one "Saved"
   * would be wrong in the case that matters: the quote exists, a line does not,
   * and the person is about to close the form believing nothing happened and
   * press New quote again.
   *
   * Nothing here offers to undo anything, because nothing can. Deleting the
   * quote to "roll back" a refused line would destroy the part that worked, and
   * calling it a rollback when it is a second destructive write is worse than
   * saying plainly what stands.
   *
   * ⚠️ Never `Quote ${quote.quoteNumber}` unguarded. **A numberless quote is
   * now the ordinary result of a create, and this is the ONE place in this file
   * that interpolates the number**, so the guard is the whole defence. Nothing
   * here can set the field — it is the server's, and read-only on create and
   * PATCH alike — and the server allocates none until the ledger ships, so
   * `null` is not an edge case to be tidied away with a `??`: it is what every
   * quote raised from this form carries. The id is the fallback, and the column
   * was nullable long before any of that anyway.
   */
  const createdNote: ReactNode = !justCreated ? undefined : (
    <div className="grid gap-2">
      <Notice tone="success" role="status" className="break-words">
        <strong className="font-semibold">
          {quote?.quoteNumber ? `Quote ${quote.quoteNumber}` : `Quote #${quote?.id}`} was created
          and is saved.
        </strong>{' '}
        {lineOutcome === null
          ? 'No line items were entered — add them below.'
          : lineOutcome.failures.length === 0
            ? savedLinesSentence(lineOutcome.attempted)
            : `${lineOutcome.saved} of ${lineOutcome.attempted} line items were saved with it.`}
      </Notice>

      {/*
        What the push did, said as a SEPARATE fact from the save above it.

        ⚠️ It is deliberately its own band rather than a clause on the green
        paragraph: "the quote was created" and "Xero took it" are two outcomes of
        two requests with no transaction across them, and the case that matters
        is the one where the first is true and the second is not. A person who
        reads a single collapsed sentence as a failure closes this form and
        presses New quote again.
      */}
      {createPush !== null && (
        /*
          ⚠️ **NO RETRY BUTTON ANY MORE, and `undefined` is the honest value.**
          *SUPERSEDED: `onRetry={quote ? () => void pushCreatedToXero(quote) :
          undefined}`.* There is nothing this form can retry — the push is part
          of the save, so the only way to send it again is to save again, and a
          button here would either re-save silently or call an endpoint this
          form no longer talks to. The quote's own Xero panel is the permanent
          control, which is what the sentences already point at.
        */
        <CreatePushNote outcome={createPush} busy={false} onRetry={undefined} />
      )}

      {lineOutcome !== null && lineOutcome.failures.length > 0 && (
        <Notice tone="danger" role="alert" className="break-words">
          <p>
            <strong className="font-semibold">
              {lineOutcome.failures.length === 1
                ? 'One line was refused, and the quote was still created.'
                : `${lineOutcome.failures.length} lines were refused, and the quote was still created.`}
            </strong>{' '}
            The refused {lineOutcome.failures.length === 1 ? 'line is' : 'lines are'} not in the
            grid below — add {lineOutcome.failures.length === 1 ? 'it' : 'them'} again.
          </p>
          {/*
            Numbered by the position each line held in the grid as it was
            SUBMITTED — which is not its position now, because the rows that
            landed have closed up around it. Said plainly above rather than
            silently letting someone count down the grid to row 3.
          */}
          <ul className="mt-2 grid gap-1 pl-6">
            {lineOutcome.failures.map((failure) => (
              <li key={failure.position} className="list-disc">
                <strong className="font-semibold">
                  Line {failure.position} of {lineOutcome.attempted}
                </strong>{' '}
                — {failure.message}
              </li>
            ))}
          </ul>
        </Notice>
      )}
    </div>
  );

  return (
    <Modal
      open
      /*
       * `finish`, not `onClose`. Escape and the close button have to reload the
       * list when this form created a quote — otherwise a record that plainly
       * exists is missing from the list behind it.
       */
      onClose={finish}
      /*
       * The widest step there is, and the reason the layout below works at all.
       * A quote header is fourteen fields; at `lg` (48rem) they can only stack
       * two abreast, which is the single column of boxes this form was rebuilt
       * to stop being. `xl` seats three from `lg` up and four from `xl`, which
       * is the shape Xero's own quote screen uses. Below `sm` every Modal size
       * is the same full-bleed sheet, so nothing here costs a phone anything —
       * the grid simply collapses to one column.
       */
      size="xl"
      dismissOnBackdrop={false}
      initialFocusRef={subjectRef}
      title={
        quote
          ? justCreated
            ? `Quote created — ${quote.subject}`
            : `Edit ${quote.subject}`
          : convertDeal
            ? /*
               * Named as a CONVERSION rather than as a new quote, because the
               * two behave differently in the one way that matters: the line
               * items are the deal's and are copied by the server, not entered
               * here.
               */
              `Convert ${convertDeal.dealName} to a quote`
            : 'New quote'
      }
      /*
       * No description. Xero's own quote screen carries none, and the
       * two-phase create is reported where it happens — the line-items band,
       * and `createdNote` afterwards — which is the only place it has to be
       * right.
       */
      /*
       * 🔴 **THE HEADER, NOT THE FOOTER — owner's decision, 2026-09-14.** Cancel
       * and Save sit beside the title on all three document forms: this one is a
       * screen tall and its line grid carries a Save of its own partway down.
       * **The pair moved together** — splitting them across the two ends of a
       * scrolling dialog leaves a reader hunting the other half.
       *
       * ⚠️ **The sentence below travels WITH Save and that is the point of it.**
       * Its whole job is to say why the button beside it is off, so leaving it
       * in a footer while Save moved would strand an explanation a screen away
       * from the thing it explains. `sm:mr-auto` still pushes the buttons to the
       * end of the row — the header's action slot is `justify-end` from `sm`,
       * exactly as the footer was.
       */
      headerActions={
        <>
          {/*
            Why Save is off, said next to Save — and ONLY for the reason nobody
            can see. A disabled Save on an unedited form explains itself; a
            disabled Save because of a row further down the form does not.
          */}
          {/*
            ⚠️ **ONE count, TWO sentences, and the second half is why.** The
            draft grid's rows can be REMOVED and a converted row cannot — there
            is no delete control on that table and the line belongs to the deal —
            so telling somebody to "fix or remove it" on a conversion is advice
            they cannot follow, which is the class of defect that already cost
            this repo a duplicate-key message naming a control that had stopped
            existing. The count is shared because it is the same fact.
          */}
          {/*
            ⚠️ **The edit's own version of the same sentence, and it is the only
            reason Save may be disabled here that a reader cannot see.** An
            unedited form explains its own greyed-out Save; a row further down
            that the grid is withholding does not, and this is exactly the state
            that used to leave somebody pressing a dead button. It names the
            grid's own control rather than a count alone, because that control
            is what the row is waiting for.
          */}
          {isEdit && lineEdits.blocked > 0 && (
            <p className="self-center text-xs break-words text-danger sm:mr-auto">
              {lineEdits.blocked === 1
                ? 'One line item below cannot be saved yet — fix or remove it.'
                : `${lineEdits.blocked} line items below cannot be saved yet — fix or remove them.`}
            </p>
          )}
          {!isEdit && blockedRows > 0 && (
            <p className="self-center text-xs break-words text-danger sm:mr-auto">
              {isConverting
                ? blockedRows === 1
                  ? 'One line item below cannot be converted yet — correct it.'
                  : `${blockedRows} line items below cannot be converted yet — correct them.`
                : blockedRows === 1
                  ? 'One line item below cannot be saved yet — fix or remove it.'
                  : `${blockedRows} line items below cannot be saved yet — fix or remove them.`}
            </p>
          )}
          <Button variant="secondary" onClick={finish} disabled={submitting}>
            {/*
              Something was saved, so this is not a "Cancel" any more — and on
              the convert path a refused follow-up leaves a REAL quote behind,
              which is exactly the moment somebody must not read this control as
              "undo".
            */}
            {created || convertedQuote ? 'Done' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {/*
              ⚠️ **THE "Sending to Xero…" PHASE LABEL IS GONE — there is no
              second phase left to name.** *SUPERSEDED: "The Xero phase is NAMED
              rather than hidden behind the same spinner. The quote is already
              saved by the time this label changes — the POST returned — and what
              is still running is an inline call to Xero that can take a few
              seconds."* The push is inside the save now, on the create exactly
              as on the edit, so there is no window in which the record is stored
              and something else is still on the wire: `submitting` spans the
              whole of it.

              ⚠️ **The reason the label existed has NOT gone away — it moved.**
              A save still waits on a Xero round trip, so it can take a few
              seconds, and "the reading that gets someone clicking again" is the
              hazard the `loading` spinner and `saveDisabled` now carry alone. If
              saves start looking hung, this is the sentence to re-read.

              ⚠️ **CORRECTED 2026-08-30. This was a nested ternary reading
              `justCreated ? 'Sending to Xero…' : 'Updating Xero…'`**, whose
              second arm named the EDIT save's own push. That push was gone —
              the server did it inside the PATCH — so the arm was unreachable. A
              label nothing can render is the "value nothing renders" shape this
              repo keeps paying for, so it was deleted rather than left looking
              like a live case. The remaining arm has now gone the same way for
              the same reason.

              An edit save still waits on Xero; it waits INSIDE the PATCH, so
              `submitting` alone covers it and there is no second phase to name.

              ⚠️ *SUPERSEDED: "`submitting &&` stays, so a RETRY pressed from
              the report below does not relabel a Save button that is idle and
              enabled."* There is no retry in the report band any more.
            */}
            {isEdit
                ? 'Save changes'
                : convertedQuote
                  ? /*
                     * ⚠️ The conversion is DONE. Only the header edits are being
                     * re-sent, and the label has to say so — "Convert to quote"
                     * over a quote that exists reads as an offer to make a second
                     * one, and the endpoint would happily oblige.
                     */
                    'Save these changes again'
                  : isConverting
                    ? 'Convert to quote'
                    : 'Create quote'}
          </Button>
          {/*
            The same submit, flagged by `data-intent` — `handleSubmit` reads the
            native submitter, so this runs the identical save path. Create only,
            and hidden once a conversion exists (`convertedQuote`): from there
            the primary button re-sends header edits and "Create" would be false.
          */}
          {onCreatedPreview && !isEdit && !convertedQuote && (
            <Button
              type="submit"
              form={formId}
              variant="secondary"
              data-intent="preview"
              disabled={saveDisabled || submitting}
            >
              {CREATE_AND_PREVIEW_LABEL}
            </Button>
          )}
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        {/*
          ⚠️ THE ONE SENTENCE THIS SCREEN CANNOT DO WITHOUT.
          Nothing exists until Convert is pressed — that is the whole change the
          owner asked for. It is invisible from the controls: the form looks
          exactly like the one that creates a quote from scratch, where Save
          writes what is in it row by row.

          ⚠️ **CORRECTED 2026-08-30 — the second fact this used to carry is now
          only HALF true, which is the worse kind.** *SUPERSEDED: "…and the line
          items are the SERVER's copy rather than rows anybody is authoring
          here. BOTH facts are invisible from the controls…"* The sentence on
          screen says only the part that is still wholly true, because a sentence
          that is right about one clause and wrong about the next reads as
          authoritative to everybody who skims it.

          ⚠️ **AND THE CORRECTION ITSELF IS NOW STALE IN ITS TURN.**
          *SUPERSEDED: "They are still the server's copy and they are no longer
          un-authored: the figures on them are boxes, and the conversion carries
          a `lines` override."* They are not the server's copy at all any more —
          the conversion sends **`lineSet`**, the complete final list, so the
          rows on screen ARE the quote's lines: their product, project, cities,
          regions and inclusions are all authored here, rows can be added and
          removed, and only the tax comes from elsewhere. "Nothing is created
          until you press Convert" survives all of it unchanged, which is why the
          rendered sentence needed no edit — the third time this passage has been
          corrected and the first time the screen did not have to move.
        */}
        {isConverting && convertedQuote === null && (
          /*
            ⚠️ **SHORTENED 2026-08-30 — owner's copy pass.** *SUPERSEDED: "…
            {convertLinesNote} The header above is applied to the new quote by a
            SECOND request straight afterwards, so if that one is refused the
            quote still exists — carrying the deal's own header — and this form
            will say so rather than report a failed save."*

            Two reasons the cut is safe, and neither is "it was long":

            - `convertLinesNote` was DUPLICATED here. It rendered again, in this
              same view, over the copied-lines table below. ⚠️ **That constant no
              longer exists**: `QuoteLineItemsTable`'s convert mode says each of
              its three states itself — skeletons while the read is in flight,
              an error naming that every line crosses anyway, and an empty state
              for a deal with genuinely no products. Do not re-add a sentence
              here that the grid below is already rendering.
            - the second-request warning described a HYPOTHETICAL, and the
              actual outcome it warned about has its own alert
              (`convertedQuote !== null`) which leads with "was created" and
              says converting again raises a second quote. That alert is the
              safeguard; this was a rehearsal of it. **If that alert is ever
              removed, this sentence has to come back.**
          */
          <Notice tone="info" className="break-words">
            Nothing is created until you press Convert.
          </Notice>
        )}

        {convertError !== null && (
          <Notice tone="danger" role="alert" className="break-words">
            {convertError}
          </Notice>
        )}

        {/*
          ⚠️ **THE QUOTE EXISTS.** Convert landed and the follow-up PATCH did
          not, and there is no rollback and no compensating endpoint safe to fire
          on somebody's behalf — so the only truthful report is the one that says
          what stands, names it, and links to it. "Nothing was saved" here is the
          sentence that produces a second quote: the person closes the modal and
          converts again, and the convert endpoint answers no 409.

          ⚠️ The heading leads with what was CREATED, not with what failed.

          ⚠️ **AND IT NOW SAYS THE LINE BOXES ARE SPENT**, because from this
          point they are locked (`convertLinesLocked`) and a disabled control
          with no sentence beside it is indistinguishable from a broken one.
          The lines were written by the conversion that landed; Save from here
          re-sends the HEADER only, so a price typed into the table would do
          nothing and report success. "Why a control is disabled" is one of the
          four kinds of sentence the no-helper-text rule keeps, and this is one.

          ⚠️ **BACK TO ONE WAY TO REACH THIS STATE — a refused header PATCH.**
          *SUPERSEDED: "TWO WAYS TO REACH THIS STATE SINCE ADDED LINES SHIPPED,
          and they must not be worded alike. A refused header PATCH, and a
          refused ADDED line — which stops before the PATCH is even attempted …
          `addedLinesFailed` is the discriminator."* An added line now rides on
          the conversion, so a refused one means **no quote at all** and this
          block never renders for it. `addedLinesFailed` is kept because
          `lineOutcome` is still written on the CREATE path, and the branch below
          is the only thing that would say so if that ever changed — but on the
          convert path it is now permanently `false`.
        */}
        {convertedQuote !== null && (
          <Notice tone="warning" role="alert" className="break-words">
            <p className="m-0">
              <strong className="font-semibold">
                Quote #{convertedQuote.id} was created from this deal, with its line items.
              </strong>{' '}
              {/*
                ⚠️ **Read off `convertHeaderRefused`, NOT off the presence of
                `convertedQuote`.** That state has a SECOND cause since this
                block also renders when the header was perfectly fine and the
                conversion's XERO PUSH was refused — and telling that reader
                their header changes were refused sends them to correct a field
                that is not marked and was never wrong. `InvoiceFormModal`
                records the same correction against its own twin of this band.

                ⚠️ **Silence is the third branch, and it is deliberate.** On the
                push-only path there is nothing to say about the header, and the
                Xero band below is already saying the thing that happened. A
                sentence here would be a second account of one fact.
              */}
              {addedLinesFailed
                ? 'Your header changes have not been applied yet — press Save again to apply them. '
                : convertHeaderRefused
                  ? 'Your header changes were refused. '
                  : ''}
              Converting again would raise a second quote, and the line items below can only be
              edited on the quote now.
            </p>
            {/*
              ⚠️ **The added lines report per line, and it leads with the quote
              having been created.** A refused added line does not un-make the
              quote and does not un-write the ones that landed — and this is the
              only account of it anywhere, because the form does not navigate on
              a partial result.

              ⚠️ **Numbered by the position each row held as it was SUBMITTED**,
              which is not its position now: the grid still holds every row,
              including the ones already written. Said plainly rather than
              letting somebody count down the grid.

              ⚠️ **UNREACHABLE ON THIS PATH NOW, and kept rather than deleted.**
              `lineOutcome` is never written by `runConvert` any more — added
              lines ride on the conversion — so this branch renders for nothing
              today. It stays because `lineOutcome` is real state written by the
              CREATE path, and a block that would report a partial line write is
              cheaper to keep than to rediscover. *SUPERSEDED, the instruction it
              carried: "**\"Add them on the quote\", never \"press Save again\".**
              The rows that landed are already there, and `addedLinesWritten`
              deliberately stops a retry re-sending the whole set."*
              `addedLinesWritten` no longer exists.
            */}
            {lineOutcome !== null && lineOutcome.failures.length > 0 && (
              <div className="mt-2">
                <p className="m-0">
                  <strong className="font-semibold">
                    {lineOutcome.failures.length === 1
                      ? 'One added line was refused.'
                      : `${lineOutcome.failures.length} added lines were refused.`}
                  </strong>{' '}
                  {lineOutcome.saved} of {lineOutcome.attempted} were written onto the quote. Add
                  the refused {lineOutcome.failures.length === 1 ? 'one' : 'ones'} on the quote
                  itself — saving again here will not send{' '}
                  {lineOutcome.failures.length === 1 ? 'it' : 'them'} a second time.
                </p>
                <ul className="mt-2 grid gap-1 pl-6">
                  {lineOutcome.failures.map((failure) => (
                    <li key={failure.position} className="list-disc">
                      <strong className="font-semibold">
                        Added line {failure.position} of {lineOutcome.attempted}
                      </strong>{' '}
                      — {failure.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="m-0 mt-2">
              {/*
                ⚠️ **The instruction has to follow the cause too.** With nothing
                marked — the push-only path, where the header was accepted —
                "correct what is marked" is an instruction that cannot be carried
                out, and it sends somebody hunting a field that was never wrong.
                Same three-way split, off the same flag, as the sentence above.
              */}
              {addedLinesFailed
                ? 'Save again, or '
                : convertHeaderRefused
                  ? 'Correct what is marked and save again, or '
                  : 'You can '}
              {/*
                `finish` rather than a bare route push: the caller is what
                navigates, and it also has a list to reload.
              */}
              <button
                type="button"
                onClick={finish}
                className="cursor-pointer rounded-sm underline underline-offset-2 hover:no-underline"
              >
                open the quote
              </button>{' '}
              and edit it there.
            </p>
          </Notice>
        )}

        {/*
          ⚠️ **XERO DID NOT TAKE THE QUOTE THIS CONVERSION CREATED.**

          Its own block, beside the band above and never instead of it: the
          conversion landing, the header landing and the push being refused are
          three separate facts from separate requests, and each changes what the
          reader does next.

          ⚠️ **THIS IS THE ONLY ARTEFACT ANYWHERE THAT CARRIES IT.** The server
          swallows a failed push so `POST /deals/{id}/convert` still answers
          `201`, and it does not re-read the record on that branch because
          nothing was written — so the quote arrives with no Xero link, and its
          own page cannot tell that state from a quote nobody has pushed yet.
          Drop this and the person is told the conversion worked, which it did,
          and never learns the customer's document is not in the accounts.

          ⚠️ **`pushed` and `skipped` never reach here, deliberately.**
          `conversionPushReport` answers `null` for both — see it for why a
          warning on the commonest outcome in the system would be worse than
          none. `skipped` in particular is the server's answer for a quote with
          no lines and for a company nobody has linked to a Xero contact, which
          is most of them.

          ⚠️ Every string is `rowPushReport`'s or the SERVER's own, rendered by
          the component the list rows use. Only the heading is replaced, and it
          leads with the quote having been created, because reading a refused
          push as a failed conversion is what produces a second quote —
          `POST /deals/{id}/convert` answers no 409.
        */}
        {convertPush !== null && (
          <div className="overflow-hidden rounded-md border border-border">
            <XeroPushReportBanner report={convertPush} onDismiss={() => setConvertPush(null)} />
          </div>
        )}

        {/*
          THE HEADER BAND — the whole quote in one view.

          Modelled on Xero's own quote screen, which puts the customer and the
          document's title on the left and a block of short facts — dates,
          numbers, currency, letterhead, tax treatment — to their right, rather
          than stacking twelve full-width boxes down a page. One flat grid does
          that better than nested ones: every field is a sibling, so the columns
          stay aligned across rows instead of each nested block choosing its own
          gutter.

          One column below `sm`, two from `sm`, three from `lg`, four from `xl`.
          A multi-column grid COLLAPSES on a phone; it does not shrink, which is
          what keeps this readable at 320px where every cell is full width.

          `[&>*]:min-w-0` is load-bearing rather than decoration. A grid item
          defaults to `min-width: auto`, so one wide child — a long option label
          in a Combobox, a hint that will not wrap — widens its whole column and
          pushes the form past the viewport. That has broken a page in this repo
          before. Applying it to every direct child is one utility instead of
          fourteen wrappers that someone will forget on the fifteenth.
        */}
        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 [&>*]:min-w-0">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              subjectRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label="Subject" required error={errors.subject}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.subject}
                  maxLength={MAX_LENGTHS.subject}
                  placeholder="Riverdale Academy — Summer 2027 recruitment"
                  invalid={Boolean(errors.subject)}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    /*
                     * The ONLY writer of `subjectTouched` — see the company
                     * seed. Set on every keystroke including the one that
                     * empties the box, because a cleared subject is a decision
                     * and the next company change must not re-fill it.
                     */
                    subjectTouched.current = true;
                    setField('subject', event.target.value);
                  }}
                />
              )}
            </Field>
          </div>

          {/*
           * The CRM's own reference. Deliberately not beside the two Xero
           * references below — those are the finance system's separately
           * populated numbers, and conflating them is the mistake this layout
           * avoids.
           *
           * ⚠️ **READ-ONLY ON BOTH BRANCHES.** `quoteNumber` is allocated by
           * the server and is a 422 on create AND on PATCH — the field is not
           * sent from anywhere in this form; see `FormValues`, which has no
           * such key. It is DISPLAYED rather than dropped: a quote that has a
           * number still has the reference people read out, and a field that
           * vanishes reads as the number being gone rather than as it not
           * being ours to set.
           *
           * ⚠️ **A quote created here carries NO number at all** until the
           * backend builds the numbering ledger — a known gap, not a bug. Do
           * not "fix" it by allocating one client-side: that is a
           * read-then-write race the UNIQUE index turns into a 409 on a field
           * the losing user never touched, the exact hazard the old free-text
           * box demonstrated (`QU-BSF-1787705864873`, `TEST-1787704936649`).
           * Nothing rendered here may promise the ledger while it does not
           * exist.
           *
           * A read-only `Input` rather than a paragraph, for the same reason
           * the locked company below is one: `Field` renders a real
           * `<label for>`, so the content has to be a labelable control. Not
           * `disabled`, which reads as "temporarily unavailable" rather than
           * "not yours to fill in".
           *
           * `src/lib/types.ts` and `InlineQuoteFields` carry the same
           * statement; keep the three in step.
           */}
          {/*
            The hint "Set by the server." is GONE (2026-09-05, owner's stricter
            pass). It was a few words telling "you may not" from "this is
            broken", and nothing refuses a read the reader cannot make — so it
            is not one of the five sentences that now survive. The value shown
            ("Not allocated") is what carries the state.
          */}
          <Field label="Quote number">
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                value={quote?.quoteNumber ?? 'Not allocated'}
                readOnly
                aria-readonly="true"
                className="bg-surface-sunken text-fg-secondary tabular-nums"
                aria-describedby={describedBy}
              />
            )}
          </Field>

          <Field
            label="Stage"
            error={errors.stageId}
            /*
             * ⚠️ **No hint at all now.** There was never an acceptance one —
             * accepting moves the stage and does nothing else, so saying so
             * would restate the control — and the LOAD FAILURE that used to sit
             * here ("The stage list could not be loaded — try signing in
             * again.") went in the owner's stricter pass, 2026-09-05: a fetch
             * that failed is not a server refusal of anything the reader asked
             * for, so it is not one of the five sentences that survive.
             *
             * ⚠️ **The list can still be empty for that reason**, and the
             * picker now says nothing about why. `useQuoteStageOptions` still
             * returns `failed`; it is simply no longer destructured here.
             *
             * ⚠️ Staged billing itself is untouched: Convert is still refused
             * on a staged quote (`409 QUOTE_IS_STAGED`), and "Bill line items"
             * on `QuoteDetailPage` is still how one is billed. Nothing here may
             * be reworded to say otherwise.
             */
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={values.stageId}
                onChange={(next) => {
                  /*
                   * The ONLY writer of `stageTouched`. Set even when the box is
                   * cleared back to "No stage": that is a choice, and the Draft
                   * seed lands after the stage list arrives — which can easily
                   * be after somebody has already made it.
                   */
                  stageTouched.current = true;
                  setField('stageId', next);
                }}
                // Filtered locally: the whole master arrives in one request.
                // There is deliberately no "add new" here — quote stages are an
                // admin-managed master, and `convert` resolves one of them by
                // name, so this picker only ever reads them.
                options={stageOptions}
                loading={stagesLoading}
                emptyOptionLabel="No stage"
                searchPlaceholder="Search stages…"
                invalid={Boolean(errors.stageId)}
                aria-describedby={describedBy}
              />
            )}
          </Field>

          {companyField}

          <ContactPickerField
            value={values.contactId}
            onChange={onContactChange}
            options={contactOptions}
            onSearch={contacts.setTerm}
            loading={contacts.loading}
            footnote={contacts.footnote}
            error={errors.contactId}
            scope={contactScope}
            company={contactCompany}
          />

          <Field label="Deal" error={errors.dealId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                value={values.dealId}
                onChange={(next) => setField('dealId', next)}
                options={deals.options}
                onSearch={deals.setTerm}
                loading={deals.loading}
                emptyOptionLabel="Unassigned"
                searchPlaceholder="Search deals…"
                footnote={deals.footnote}
                invalid={Boolean(errors.dealId)}
                aria-describedby={describedBy}
              />
            )}
          </Field>

          {/*
            ⚠️ **ACCOUNT LEAD, READ-ONLY — and this is NOT the Owner field
            renamed.**

            The owner asked to "show account lead instead of owner, pulled from
            the company". The server already pulls it: every read resolves the
            quote's own `account_lead_id` where it has one, otherwise
            `company_id -> d_companies.account_lead_id`, so reassigning an
            account re-points its un-overridden quotes immediately. There is
            nothing here to seed and nothing to send, because **this form
            deliberately writes no lead** — which is why it is not a
            `FormValues` key, not in `ID_KEYS`, and not in `KNOWN_WRITE_KEYS`. A
            key no payload can express beats a guard somebody can forget, and
            that guard is worth keeping on its own merits.

            ⚠️ **CORRECTION (2026-08-30): this block asserted "there is no
            `account_lead_id` column on `d_quotes` at all" and "`accountLeadId`
            in a write body is a **422**". BOTH ARE FALSE.** Migration 082 added
            the column and the key is on the quote write schema as
            `optionalUserId()` (`quotes.schema.ts:211`, verified from backend
            source); `null` there means DERIVE — it clears the override and
            returns the quote to following its company — never "no lead". The
            key is genuinely refused only on a DELIVERABLE, which has no such
            column. Both sentences were true when written and were falsified by
            a migration in another repo, with nothing here failing.

            **The read-only rendering is unaffected and stays**: it is the
            owner's instruction, not a limit of the API. Anyone later asked to
            override the lead on a single quote should read this as unbuilt
            frontend work, not as a backend gap to file.

            ⚠️ **They are DIFFERENT PEOPLE and the copy must never suggest
            otherwise.** Measured live: quote 9's owner is Paras Fatnani while
            its account lead is Daryna Kodinets. Anyone reading this as a rename
            would conclude the quote had been reassigned. The label says
            "Account lead", the sentence says where the value comes from, and
            neither mentions an owner — a "formerly Owner" note would invite
            exactly the wrong reading.

            ⚠️ **Nothing is rendered on a CREATE, and nothing is rendered for a
            company that has no lead.** There is no quote yet, so the server has
            resolved nothing; an empty labelled row would read as "no account
            lead" over a fact that has not been asked for. The field appears the
            moment the record does — which, after a create, is the moment this
            form becomes an edit of it.

            The read-only Input matches the Project field beside it: a disabled
            control reads as "temporarily unavailable" and hides its value
            behind a muted trigger, where a read-only field states the name
            plainly and still exposes it to a screen reader.
          */}
          {accountLeadName !== null && (
            <Field label="Account lead">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={accountLeadName}
                  readOnly
                  aria-readonly="true"
                  className="bg-surface-sunken text-fg-secondary"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          )}

          {/*
            ⚠️ PROJECT IS READ-ONLY HERE, and only when the record already has
            one. There is no picker on create and none on edit — the owner
            moved the choice onto the line items, so a control here would be a
            second answer to a question asked in the grid below.

            Shown rather than hidden because a value the record holds must not
            read as an omission: someone opening a quote that IS on a project
            and seeing no project at all would reasonably conclude it had none.
            The sunken read-only box makes the absence of a control read as
            deliberate rather than broken; the reason is now a `title`.

            No `error` slot, and no `errors.projectId` anywhere: this form
            never sends `projectId`, so a 422 can never name it.

            ⚠️ There is no document-level project requirement left at any
            level to surface — a quote whose lines name no project pushes
            untracked, not refused, so no warning belongs in this slot either.
          */}
          {headerProjectName !== null && (
            <Field label="Project">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={headerProjectName}
                  readOnly
                  aria-readonly="true"
                  // The Xero fallback half is dropped — it changes nothing anybody
                  // does from this screen. The destination survives.
                  title="Chosen on each line item now."
                  className="bg-surface-sunken text-fg-secondary"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          )}

          <Field label="Issue date" error={errors.issueDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                // A native date input emits YYYY-MM-DD from local parts, which
                // is the wire format — no Date, no timezone drift.
                type="date"
                value={values.issueDate}
                invalid={Boolean(errors.issueDate)}
                aria-describedby={describedBy}
                onChange={(event) => onIssueDateChange(event.target.value)}
              />
            )}
          </Field>

          {/*
            Blank is a real state, not a missing one: a quote with no
            `validTill` is open-ended, which is not expired and not expiring.
            That is what an empty date box already says, so it is not spelled
            out under the control.

            ⚠️ On a NEW quote this starts at issue date + 30 and FOLLOWS the
            issue date until it is edited here — see `onIssueDateChange`.

            🔴 **The hint that said so is GONE (2026-09-05, owner's stricter
            pass).** It read `Issue date + 30 days` and it was the one thing
            telling a reader that this box MOVES on its own when a different
            control changes. Nothing refuses that and no money turns on it, so
            it is not one of the five sentences kept — but the behaviour is
            unchanged, so a box silently re-dating itself now reads as a bug.
            If that gets reported, this is the sentence that was removed, and
            `validTillFollowsIssue` is still the state that drove it.
          */}
          <Field label="Valid till" error={errors.validTill}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                value={values.validTill}
                invalid={Boolean(errors.validTill)}
                aria-describedby={describedBy}
                onChange={(event) => {
                  /*
                   * The ONLY thing that stops Valid till following the issue
                   * date. Set even when the box is emptied: an open-ended quote
                   * is a deliberate state, and the issue date must not refill
                   * it.
                   */
                  setValidTillFollowsIssue(false);
                  setField('validTill', event.target.value);
                }}
              />
            )}
          </Field>

          {/*
            THREE references live on this quote and conflating any two loses a
            real one: `quoteNumber` is ours, `xeroQuoteNumber` is the finance
            system's, and this one is the CUSTOMER's. It is what their finance
            team matches the payment against, so it is labelled by whose number
            it is rather than as a bare "Reference".

            ⚠️ It is also the ONLY one of the three anybody may type. The other
            two are written by their own systems and are displayed read-only —
            so a control here is not evidence that its neighbours take one.
          */}
          <Field label="Customer PO number" error={errors.reference}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                value={values.reference}
                maxLength={MAX_LENGTHS.reference}
                placeholder="PO MBAMK10654"
                invalid={Boolean(errors.reference)}
                aria-describedby={describedBy}
                onChange={(event) => setField('reference', event.target.value)}
              />
            )}
          </Field>

          {/*
            The currency and the rate it is pinned at are ONE cell, exactly as
            they are on Xero's own document header — the rate line sits directly
            under the picker it qualifies. They are two fields on the wire and
            one fact to a reader, and separating them would leave the rate
            floating with no currency beside it to make sense of.
          */}
          <div className="space-y-2">
            {isConverting ? (
              /*
               * ⚠️ CONVERT MODE — READ-ONLY. Owner's decision, 2026-08-30.
               *
               * The currency crosses with the conversion and every LINE it copies
               * is priced in it. Re-picking here would leave the header naming one
               * currency over line figures entered in another — an error that
               * reaches a customer as a number, on a document nothing else in this
               * system compares.
               *
               * ⚠️ **The exchange-rate note below is DELIBERATELY NOT LOCKED.** The
               * pinned rate is a different column, a different decision and a
               * different control, and nothing about it was put to the owner.
               * Freezing it alongside the currency because the two share one cell
               * would be this form deciding something on its own — and that cell is
               * a grouping for a READER, which is not a reason to treat two fields
               * as one.
               *
               * `error` is still passed, for the reason the company branch gives:
               * `currency` is validated here AND nameable by the server, so it must
               * have somewhere to render even when the box cannot be typed in.
               */
              <Field label="Currency" error={errors.currency}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={convertCurrencyCode ?? NOT_SET}
                    readOnly
                    aria-readonly="true"
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            ) : (
              /*
               * The load-failure hint ("The currency list could not be loaded.")
               * went in the owner's stricter pass, 2026-09-05 — a failed fetch
               * is not a refusal, so it is not one of the five sentences kept.
               */
              <Field label="Currency" error={errors.currency}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    /*
                     * The value here is the three-letter CODE, not a currency id. A
                     * quote stores `currency` as a CHAR(3) with no foreign key to
                     * /currencies, so an id would either be rejected or — worse —
                     * coerced into a nonsense code.
                     */
                    value={values.currency}
                    onChange={(next) => onCurrencyChange(next)}
                    options={currencyChoices}
                    loading={currenciesLoading}
                    emptyOptionLabel={NOT_SET}
                    searchPlaceholder="Search currencies…"
                    invalid={Boolean(errors.currency)}
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            )}

            {/*
              `documentCurrency` is no longer passed: its only reader was the
              `same` branch's sentence, removed 2026-09-05.
            */}
            <ExchangeRateNote
              state={rateState}
              baseCurrency={baseCurrency}
              rate={values.currencyRate.trim()}
              exchangeRate={exchangeRate}
              error={errors.currencyRate}
              onSet={() => setRateDialogOpen(true)}
              /*
               * ⚠️ Clearing writes '' and nothing else. It must NOT also clear
               * the currency, and it must not write '1': both would be this form
               * deciding something on the user's behalf that changes what a
               * customer is billed. '' becomes an explicit `null` on the PATCH,
               * which is the API's own way of saying "no pin — use Xero's daily
               * rate".
               */
              onRemove={() => setField('currencyRate', '')}
            />
          </div>

          {/*
            Tax treatment sits in the header band rather than behind the section
            below, exactly as it does on Xero's own screen: it is one of the
            things that decide what the customer's PDF looks like. Letterhead
            sat beside it until it was taken off the CREATE form.

            ⚠️ **Letterhead is EDIT-ONLY, on the owner's instruction.** Reading
            the tenant's themes costs a Xero call against a shared rate limit,
            and when that limit is spent the field renders a warning block on a
            form where nothing is wrong — which is what it was doing on every
            new quote.

            What did NOT change is the WRITE. A company's own letterhead is
            still seeded onto a new quote and still sent, so a quote raised for
            a company that names one still gets it; there is simply no control
            to change it until the quote exists. That is why this is a render
            branch and NOT an entry in `CREATE_SUPPRESSED_KEYS` — suppressing
            the key would silently drop the company's letterhead instead of
            merely hiding the picker, and the two look identical from here.
            (Today every company stores `null`, so nothing is seeded either
            way; that is the state of the data, not a reason to break the path.)
          */}
          {isEdit && <div className="space-y-2">
            <Field
              /*
               * ⚠️ **"Branding theme", not "Letterhead" — owner's decision,
               * 2026-08-31**, to match the wording on Xero's own screen, which
               * is the screen this is reconciled against.
               *
               * ⚠️ **The LABEL moved and NOTHING ELSE did.** The key is still
               * `xeroBrandingThemeId`, the payload is unchanged, and the
               * comments around here still say "letterhead" because they
               * describe the concept rather than quote the UI. Do not rename
               * the key to follow the label, and do not go looking for this
               * control by grepping the old word.
               */
              /*
                🔴 **`brandingHint(...)` IS GONE — owner's stricter pass,
                2026-09-05**, and the function with it. It reported the state of
                the Xero read behind this picker: checking the connection, not
                connected, the app not installed, the themes call failed, the
                shared Xero call budget cooled down, or the organisation default
                in use. None of those is a server refusal of something the
                reader asked for and none is a money warning, so none survives
                the narrowed bar.

                ⚠️ **What the reader loses: an EMPTY picker now looks the same
                whatever caused it.** "Xero is disconnected", "the themes read
                failed" and "this organisation has no themes" were three
                different sentences and are now one silence. The states are all
                still computed — `xeroUnreachable`, `themeCanOnlyClear` and
                `brandingThemes.error` still drive `disabled` and the option
                list — so restoring a sentence needs no new plumbing.
              */
              label="Branding theme"
              error={errors.xeroBrandingThemeId}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={values.xeroBrandingThemeId}
                  options={brandingChoices}
                  loading={brandingThemes.loading}
                  disabled={xeroUnreachable && !themeCanOnlyClear}
                  placeholder={
                    brandingThemes.loading ? 'Reading themes from Xero…' : 'Organisation default'
                  }
                  emptyOptionLabel="Organisation default"
                  searchPlaceholder="Search branding themes…"
                  invalid={Boolean(errors.xeroBrandingThemeId)}
                  aria-describedby={describedBy}
                  onChange={(next) => {
                    /*
                     * The ONLY writer of `themeTouched`, and the reason the
                     * company seed can never overwrite a decision — including
                     * the decision to clear the field back to ''.
                     */
                    themeTouched.current = true;
                    setField('xeroBrandingThemeId', next);
                  }}
                />
              )}
            </Field>

            {/*
              The disconnected case is stated once, in the field's own hint —
              `brandingHint` says which of the two it is. The link is what the
              hint cannot carry, so it is all that is left here.
            */}
            {!xero.loading && !xero.connection.connected && !xero.notInstalled && (
              <p className="text-xs">
                <TextLink to="/integrations" className="font-medium">
                  Set up the Xero connection
                </TextLink>
              </p>
            )}

            {xero.connection.connected && brandingThemes.error && (
              <Notice
                role="alert"
                className="break-words"
                /*
                 * A cooldown is a calm state of a HEALTHY connection, so it
                 * takes the warning band rather than the danger one. Red
                 * would say something is wrong, and nothing is.
                 */
                tone={
                  brandingThemes.error.kind === 'retry' ||
                  brandingThemes.error.kind === 'missing' ||
                  brandingThemes.error.kind === 'cooldown'
                    ? 'warning'
                    : 'danger'
                }
                action={
                  brandingThemes.error.kind !== 'missing' ? (
                    <Button variant="secondary" size="sm" onClick={brandingThemes.reload}>
                      Try again
                    </Button>
                  ) : undefined
                }
              >
                {brandingThemes.error.message}{' '}
                {brandingThemes.error.kind === 'reconnect' && (
                  <Link to="/integrations" className="font-medium underline">
                    Re-authorise Xero
                  </Link>
                )}
              </Notice>
            )}
          </div>}

          {/*
            The blank option is LABELLED `USE_COMPANY_DEFAULT` — "Use the
            company's Xero default" — so the one thing a sentence here had to
            prevent, reading blank as "no tax", is said by the option itself.
            `NOTAX` is a separate choice in the same list.

            ⚠️ No hint states that this control changes how XERO PRINTS THE
            CUSTOMER'S DOCUMENT while EMERGE's own figures do not follow it.
            That is an accepted cost: every quote and invoice defaults to
            `EXCLUSIVE`, so someone choosing `Tax Inclusive` and expecting the
            totals here to move is a deliberate departure, not the state
            everyone lands in. `InvoiceFormModal` agrees about this enum.
          */}
          {isConverting ? (
            /*
             * ⚠️ CONVERT MODE — READ-ONLY. Owner's decision, 2026-08-30.
             *
             * ⚠️ **THIS IS THE DOCUMENT'S `xeroLineAmountType`, NOT A LINE'S
             * `taxRate`, AND THE DISTINCTION IS LOAD-BEARING.** This control
             * decides how XERO PRINTS the customer's document, it is one value for
             * the whole quote, and it is what was locked. A line's rate is a
             * different column under a separately-taken decision, and nothing here
             * is evidence about it.
             *
             * ⚠️ **CORRECTED 2026-08-30** — *SUPERSEDED: "The owner REVERSED the
             * read-only LINE tax ruling on 2026-08-30, and the contract says in as
             * many words not to branch a `readOnly` prop on `taxRateSource`
             * again."* That is one of three positions taken the same day and not
             * the live one; the rate percentage IS locked where the company states
             * a rate and a figure is present, with the tax CODE left editable. See
             * the note where `CONVERT_FIELD_FROM_DEAL` used to be declared, and
             * measure it in
             * `QuoteLineItemsTable.tsx` rather than from either paragraph.
             *
             * ⚠️ **There is no hint at all now** (removed 2026-09-05). When there
             * was one it deliberately did NOT say "from the deal": `d_deals` has no
             * such column, so the conversion writes none and this form asserts
             * `EXCLUSIVE` on top of it — see {@link convertValuesFromDeal}. Naming
             * the deal as the source would be a claim about a column that does not
             * exist, and that is still true of anything written here later.
             *
             * The value shown is the one that will be WRITTEN, labelled from the
             * shared vocabulary rather than re-worded here — see
             * {@link convertTaxLabel}.
             */
            <Field label="Tax" error={errors.xeroLineAmountType}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={convertTaxLabel}
                  readOnly
                  aria-readonly="true"
                  className="bg-surface-sunken text-fg-secondary"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          ) : (
            <Field label="Tax" error={errors.xeroLineAmountType}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Select
                  id={id}
                  value={values.xeroLineAmountType}
                  invalid={Boolean(errors.xeroLineAmountType)}
                  aria-describedby={describedBy}
                  onChange={(event) => {
                    /*
                     * The ONLY writer of `lineAmountTouched` — see the company
                     * seed. Set even when the blank option is chosen: '' is the
                     * real, choosable value "use the company's Xero default", not
                     * an empty control, and a seed that read it as untouched
                     * would undo somebody deliberately picking it.
                     */
                    lineAmountTouched.current = true;
                    setField('xeroLineAmountType', event.target.value);
                  }}
                >
                  <option value="">{USE_COMPANY_DEFAULT}</option>
                  {DOCUMENT_LINE_AMOUNT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {/*
            ⚠️ **The company's Xero sales defaults are not shown here, and must
            not come back as a CONTROL either.** `xeroSalesTaxType` and
            `xeroSalesLineAmountType` are the COMPANY's fields;
            `XeroSalesDefaultsDialog` writes them, to the COMPANY, at push time
            with the person pushing in front of it — a second copy on the quote
            would be a second answer to one question. There is no
            `xeroSalesTaxType` on `Quote` or `QuoteCreateInput` in any case, and
            the write schemas are `.strict()`, so the key would be a 422.

            A company recording none is refused loudly by `XERO_NO_TAX_TYPE` at
            push time — that refusal is the guarantee.
          */}
        </div>


        {/*
          THE LINE ITEMS BAND — the document's middle, between its header and
          its foot, exactly where Xero's own quote screen puts it.

          ⚠️ **The Enter guard is load-bearing, not tidiness.** A line control
          is a text input inside this `<form>`, and a form with a submit button
          submits on Enter from any of them — so typing a quantity and pressing
          Enter would PATCH the quote header. The guard cancels only the
          implicit submission, and only from a bare `<input>`: a Combobox that
          has already handled its own Enter arrives here `defaultPrevented`, and
          buttons and textareas are left alone. Same family of bug as the nested
          picker dialog that once created a half-filled deal — React and the DOM
          both route these upwards, and neither `tsc` nor oxlint sees it.
        */}
        <div
          className="border-t border-border pt-4"
          onKeyDown={(event) => {
            if (event.defaultPrevented) return;
            if (event.key !== 'Enter') return;
            if (event.target instanceof HTMLInputElement) event.preventDefault();
          }}
        >
          {/*
            ⚠️ **NOTHING IS SAID HERE ABOUT PROJECTS, DELIBERATELY** — see the
            comment above `lineItemState` for why. A projectless quote is
            completely ordinary, this form cannot tell whether a given line
            OUGHT to have a project, and the header picker a warning would send
            someone to no longer exists. The grid's own Project column shows
            the truth per row, and the per-line write guard refuses the rows
            that are genuinely wrong.
          */}
          {isConverting ? (
            /*
             * ⚠️ CONVERT MODE — **THE LINE GRID, in its third mode.**
             *
             * 🔴 *SUPERSEDED, and it was ~380 lines of hand-built table: "⚠️
             * CONVERT MODE — NOT THE LINE GRID, and deliberately so … `ConvertedLinesPreview`
             * is NOT reused, and could not be. It takes `QuoteProduct[]`; a deal line
             * is a different shape — `f_deal_products` has one generated column and no
             * `tax_rate` at all — so the columns here are the ones a deal line actually
             * has."* Every warning that paragraph carried is preserved — in
             * `QuoteLineItemsTable`'s own header and on `QuoteLineItemsConvertProps`,
             * where the person changing a cell will actually read them — and the
             * shape argument was answered by giving the grid a mode that takes a
             * `DealProduct` rather than by building a second table beside it.
             *
             * **The owner's instruction, 2026-08-30:** *"the way how new quote model
             * looks in when clicking on new quote from the quote section the same the
             * deal to quote model should exactly similar"*. Two near-identical tables
             * on one document is exactly the shape this codebase keeps paying for —
             * they agree the day they are written and diverge on the first edit, and
             * the one that drifts is always the one nobody was looking at.
             *
             * ⚠️ **THE TWO CLAUSES THAT STILL HOLD WORD FOR WORD, AND MUST:**
             * `draftLines` is never written from this branch, and there is no
             * `POST /quotes/{id}/products` anywhere on it. `mode="convert"` issues no
             * line request of any kind — it emits overrides for the copy the server is
             * about to make, inside the transaction that creates the quote. **A row
             * POSTed from here would be a SECOND line beside the one the server already
             * copied: a quote billing the customer twice for the same work.**
             *
             * ⚠️ **ITEM, PROJECT, CITIES, REGIONS AND INCLUSIONS STAY FACTS.** The
             * override carries none of them, so the grid renders every one of those
             * controls INERT rather than merely ignoring what they collect. Changing
             * one is the resulting quote's own line grid — which is the same component
             * in its saved mode.
             *
             * ⚠️ **THERE IS NO TAX CONTROL HERE AND THERE MUST NOT BE.** The owner's
             * instruction in as many words, and also the only shape the data allows:
             * `f_deal_products` has no tax column, so there is nothing to override, and
             * a converted line's rate comes from the COMPANY. The grid's Tax rate
             * column is a readout saying so — see `ConvertedTaxRateCell` there.
             *
             * ⚠️ **NO TOTALS ROW, and the grid suppresses its own footer for this
             * mode.** The four figures are already on screen in the Totals panel,
             * derived one way; a second sum here could disagree with it, and a
             * disagreement between two totals on one form is the failure this
             * document's whole totals note exists to prevent.
             */
            <div className="grid gap-2">
              <QuoteLineItemsTable
                mode="convert"
                dealLines={dealLines.lines}
                dealLinesLoading={dealLines.loading}
                dealLinesError={dealLines.error}
                convertLocked={convertLinesLocked}
                convertLineErrors={lineServerErrors}
                onConvertLinesChange={onConvertLinesChange}
                onConvertBlockedChange={setConvertBlockedRows}
                /*
                 * Passed on all three mounts, and INERT on this one: convert
                 * mode never writes a line — the conversion creates every one
                 * of them server-side — so the grid can never reach a fire
                 * site here. It is passed anyway to keep the three call sites
                 * identical rather than inviting a reader to work out why one
                 * differs, which is the reading that leads somebody to drop it
                 * from the mount that needs it.
                 */
                onRowsSaved={onLinesSaved}
                /*
                 * ⚠️ **THE FIGURE, not a promise of one.** The Tax rate column is
                 * a readout on this path — `f_deal_products` has no tax column
                 * and the override carries no `taxRate` key — so this is the
                 * only place the number can come from. It rides the company read
                 * this form already makes; nothing extra is fetched, and the rate
                 * is never sent back.
                 */
                companySalesTax={companySalesTax}
                /*
                 * ⚠️ **Empty, and structurally so rather than by omission.** This
                 * prop is the HEADER project's city set, and a quote made by
                 * conversion has no header project — this form does not send
                 * `projectId` at all. Each row draws its own cities from its own
                 * project, inside the grid, and every one of them is inert here
                 * anyway.
                 */
                cities={NO_HEADER_CITIES}
                hasProject={false}
                /*
                 * ⚠️ **THE DEAL'S OWN CURRENCY, not `values.currency`.** The figures
                 * on these rows are the deal's; an unsaved change in the header box
                 * would label every one of them with a code they are not in. Same
                 * rule the saved grid states for `quote.currency`.
                 */
                currency={convertDeal.currency}
                canEdit={ability.canCreate('quotes')}
                /*
                 * ⚠️ **CORRECTED — `true`, and it now changes what is on screen.**
                 * *SUPERSEDED: "`false`, and it changes nothing on screen. The grid
                 * renders no delete control at all in convert mode — an override
                 * names an EXISTING deal line and there is no key that removes one —
                 * so this is the honest value rather than a permission being
                 * granted."* `lineSet` is the complete final list, so a row left out
                 * of it is a line the quote will not have, and the trash can has a
                 * real effect again.
                 *
                 * ⚠️ **`true`, and NOT `ability.canDelete('quotes')`** — the same
                 * ruling the draft grid carries, for the same reason. Nothing here
                 * has been sent anywhere: removing a row issues no request and
                 * destroys no record on the deal OR on any quote, so gating it on
                 * the admin-tier delete ability would trap a `basic_user` with a
                 * line they do not want and no way to take it off the form.
                 */
                canDelete
              />
              {/*
                ⚠️ Stated because the stage move happens server-side after the
                quote is created (owner 2026-10-18): the deal moves to the
                quote-sent stage unless it is already further along, which the
                reader cannot see from this form.

                ⚠️ **The tax sentence is NOT here any more, and its own comment
                predicted the move.** `CONVERTED_LINE_TAX_RATE` said the
                read-only/editable split "belongs beside the controls it
                describes, in `QuoteLineItemsTable.tsx`, not in a preview panel
                that has no rate box on it" — and there is a Tax rate cell there
                now. Two copies of one sentence in two files is the drift this
                document keeps recording; the grid's is the one that survives.
              */}
              <p className="m-0 text-xs break-words text-fg-muted">
                {QUOTE_CONVERSION_MOVES_THE_DEAL}
              </p>

              {/*
                ── ADDING A LINE THE DEAL NEVER HAD ────────────────────────────

                🔴 **THE SECOND GRID IS GONE. ADDING A LINE IS THE GRID ABOVE
                NOW, AND IT RIDES ON THE CONVERSION.**

                *SUPERSEDED, and it was a whole mounted `<QuoteLineItemsTable
                mode="draft">` with its own `onDraftLinesChange`,
                `onDraftBlockedChange` and context note: "🔴 **THE DRAFT GRID IS
                MOUNTED HERE — owner's decision, 2026-08-30** … **The
                distinction is the seed** … **never pre-fill this grid from the
                deal's lines.** ⚠️ **A NEW LINE CANNOT RIDE ON THE CONVERSION.**
                The `lines` override is keyed by `dealLineId` — it respells a row
                the deal already has — so a line that was never on the deal has
                nothing to key to. It is written afterwards, one request per
                line, against the quote the conversion created."*

                **The sentence in bold was the whole reason that grid existed,
                and it is no longer true.** A `lineSet` entry with no
                `dealLineId` IS a new line, so Add row in the grid above puts one
                on the quote inside the conversion's own transaction.

                ⚠️ **REMOVED RATHER THAN LEFT STANDING, and the reason is that
                leaving it would have been silent.** Nothing on this path writes
                `draftLines` any more — phase two is deleted — so a row typed
                into that grid would have been collected, counted, rendered, and
                then simply never sent: a control whose action cannot be
                performed, reporting success, which is the exact shape this
                codebase catalogues at length. Two Add-row surfaces on one modal
                with different atomicity is the other half of the objection.
              */}

            </div>
          ) : quote ? (
            <QuoteLineItemsTable
              quoteId={quote.id}
              /*
               * ⚠️ The QUOTE's own derived cities — the HEADER project's set.
               *
               * A line carries its own `projectId` and its cities are checked
               * against THAT project. This array is the document-level default
               * — what a new line starts from — and nothing more. The line
               * table owns the correct per-line picker
               * (`GET /projects?productId=…` → that project's `cities`); it is
               * not this form's to build, and this prop is not a substitute
               * for it. Never `/cities` either way: the master offers rows the
               * write is guaranteed to refuse.
               */
              cities={quote.cities}
              hasProject={quote.project !== null}
              /*
               * The SAVED currency, not `values.currency`. An unsaved change in
               * the header box would otherwise print every line figure in a
               * code the stored lines are not in.
               */
              currency={quote.currency}
              /*
               * ⚠️ **DISPLAY ONLY, and it changes nothing about what is SENT.**
               * A row added to a saved quote is a POST, and the server gives a
               * blank percentage the company's rate — so the Tax rate control
               * says so and the Amount cell previews it. Existing rows are
               * untouched: a blank there means *leave the stored rate alone*,
               * which the grid tests for.
               */
              companySalesTax={companySalesTax}
              canEdit={ability.canUpdate('quotes')}
              canDelete={ability.canDelete('quotes')}
              deleteDenialReason={denialReason(ability, 'delete', 'quotes') ?? undefined}
              /*
               * ⚠️ Said where the lines are, not only in the title bar.
               * Someone who closes this form after a partial line failure
               * believing nothing was created will make a second quote.
               */
              contextNote={createdNote}
              onLinesChanged={refreshQuote}
              /*
               * ⚠️ The two halves of one fix: the grid tells this form it has
               * unsaved rows, and hands it the run that saves them. Without the
               * first, the footer Save asserts "nothing has changed" over a
               * grid full of edits; without the second, it can say so and still
               * do nothing about it. See `lineEdits`.
               */
              onUnsavedChange={setLineEdits}
              registerSave={registerLineSave}
              /*
               * ⚠️ **THE MOUNT THIS PROP EXISTS FOR.** The grid's own "Save N
               * rows" writes here without this form's footer Save being
               * pressed, and the person then leaves through Cancel — which
               * calls `onClose` and refetches nothing. `onLinesChanged` above
               * brings the header totals back into THIS form; this tells the
               * caller its list is stale. Both are needed and neither covers
               * the other.
               */
              onRowsSaved={onLinesSaved}
            />
          ) : (
            /*
             * ⚠️ CREATE MODE — the grid holds the rows and issues NO request.
             *
             * `POST /quotes/{id}/products` needs an id and there is no bulk
             * endpoint, so nothing here can be written until the quote exists.
             * Draft mode is what closes that gap without lying about it: the
             * rows are edited exactly as they are on a saved quote, handed up
             * as the very payloads the POST takes, and written by this form the
             * moment the create returns. One Save, two phases, and the second
             * phase is reported line by line rather than as one "Saved".
             */
            <QuoteLineItemsTable
              mode="draft"
              /*
               * ⚠️ **THE PLANNED BASKET, SEEDED ONCE AT MOUNT — and CREATE ONLY
               * by construction rather than by a flag.** This branch renders
               * only while `quote` is null, so an edit and a saved create both
               * mount the SAVED grid instead and never reach this prop. That is
               * the same guarantee `withPrefill` gets from being called in a
               * `useState` initialiser, obtained here from where the JSX sits.
               *
               * ⚠️ **The grid reads it in its own `useState` initialiser and
               * never again**, so this may be a fresh array on every render —
               * which it is: the page maps `plannedProductLines(row)` inline.
               * Nothing re-seeds, so a row deleted here stays deleted.
               *
               * ⚠️ **These become ordinary draft rows and are written by the
               * EXISTING two-phase create** — quote first, then each line
               * through `writeDraftLines`, then `lineOutcome` reports any that
               * were refused. There is no second write path and no way for a
               * seeded row to be sent twice: it reaches `draftLines` through
               * `onDraftLinesChange` like every other row.
               */
              initialDraftRows={prefill?.lines}
              /*
               * ⚠️ Empty, and structurally so rather than by omission: a quote
               * created from this form has NO header project, because this
               * form no longer sends `projectId` at all. There is nothing to
               * derive a header city set from, and inventing one — from the
               * `/cities` master, say — would offer rows every line write
               * refuses. Each draft row draws its own cities from its own
               * project, inside the grid.
               */
              cities={NO_HEADER_CITIES}
              hasProject={false}
              /*
               * The TYPED currency, not a saved one — there is no saved one
               * yet. Uppercased, because that is what will be stored and a
               * lowercase code is a 422 rather than a value to preview.
               */
              currency={values.currency.trim() === '' ? null : values.currency.trim().toUpperCase()}
              /*
               * ⚠️ **THE MOUNT THE OWNER'S REPORT WAS ABOUT.** Every row here is
               * a POST after the quote is created, so a blank percentage is
               * given the company's rate server-side — and until this prop
               * existed the Tax rate control read "Xero default" and the Amount
               * cell previewed Tax 0.00 over it.
               *
               * ⚠️ **DISPLAY ONLY. It reaches no payload**: `createPayload`
               * still omits `taxRate` when the box is blank, so the SERVER
               * decides at Save with the rate then in force rather than the one
               * that happened to be read when this modal opened.
               */
              companySalesTax={companySalesTax}
              canEdit={ability.canCreate('quotes')}
              /*
               * ⚠️ `true`, and NOT `ability.canDelete('quotes')`.
               *
               * On a saved quote, removing a line is
               * `DELETE /quotes/{id}/products/{lineId}` and is admin-tier. A
               * draft row has never been sent anywhere: removing it issues no
               * request and destroys no record. Gating it on the delete ability
               * would trap a `basic_user` with a row they added by mistake and
               * cannot take off the form — refusing an action the server is
               * never asked to perform.
               */
              canDelete
              onDraftLinesChange={setDraftLines}
              onDraftBlockedChange={setBlockedDraftRows}
              /*
               * Passed on all three mounts, and INERT on this one: draft mode
               * issues no line request at all — the Save control is not even
               * rendered — so the grid can never reach a fire site here. Kept
               * so the three call sites read alike; see the note on the convert
               * mount above.
               */
              onRowsSaved={onLinesSaved}
              /*
               * 🔴 **`contextNote` IS NO LONGER PASSED — owner's stricter pass,
               * 2026-09-05.** It rendered "Saved one at a time after the quote —
               * you are told which landed." above the draft line grid.
               *
               * ⚠️ **It was the one place the create's NON-ATOMICITY was stated
               * BEFORE it could bite**: the quote is written first and each line
               * after it, so a quote can exist while a line was refused. Under
               * the narrowed bar a warning about a write that has not happened
               * yet is not a refusal, so it goes.
               *
               * ⚠️ **The REPORT after the fact is untouched and is now the only
               * place this is said** — the grid still reports per row, and the
               * partial-save sentences ("the quote exists, this line did not
               * save") are explicitly kept. Never reword either into a promise
               * that nothing is saved unless everything is.
               */
            />
          )}
        </div>

        {/*
          THE FOOT — terms on the left, totals on the right.

          The same arrangement Xero's quote screen ends on, and it is not only
          imitation: the terms are prose the customer reads and want the width,
          while the totals are four short figures that do not. Two halves from
          `lg`, stacked below it.
        */}
        <div className="grid gap-4 border-t border-border pt-4 lg:grid-cols-2 [&>*]:min-w-0">
          <Field label="Terms" error={errors.terms}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Textarea
                id={id}
                rows={6}
                value={values.terms}
                maxLength={MAX_LENGTHS.terms}
                placeholder="Payable within 30 days of the invoice date…"
                invalid={Boolean(errors.terms)}
                aria-describedby={describedBy}
                onChange={(event) => {
                  /*
                   * ⚠️ **THE ONLY WRITER OF `termsTouched`, and it is set even
                   * when the box is EMPTIED.** Clearing the terms is a real
                   * choice — a quote sent with none — and the default lands
                   * after a fetch, which can easily be after somebody has
                   * already made it. Without this, deleting the wording would be
                   * silently undone by the next render that saw the seed.
                   *
                   * Same rule and same reason as `stageTouched` on the Stage
                   * Combobox, which is set when the box is cleared back to "No
                   * stage".
                   */
                  termsTouched.current = true;
                  setField('terms', event.target.value);
                }}
              />
            )}
          </Field>

          {/*
           * The four totals are the one genuinely dangerous part of this form —
           * the invoice form's problem, plus one more.
           *
           * They are STORED columns with two competing writers: a line-item
           * create, update or delete rewrites all four from the lines in the
           * same transaction, while a POST or PATCH on the quote writes
           * literally what it is sent and never re-derives anything. Nothing
           * reconciles the two, and the overwritten figures are not kept
           * anywhere. The extra edge is `convert`, which copies all four onto
           * the invoice exactly as they stand — so a header that disagrees with
           * its lines is inherited by the invoice rather than corrected on the
           * way across.
           *
           * Hence: still collapsed by default even here in the corner Xero puts
           * its own totals in, so it cannot be stumbled into; always mounted so
           * collapsing it does not throw typing away; and replaced by a
           * read-only note the moment `totalsLocked` says the lines are — or
           * may be — in charge. `'unknown'` locks: see the note on the prop.
           */}
          {totalsLocked ? (
            <FormSection
              title="Totals"
              summary={
                draftLinesPending
                  ? 'The line items below will own these figures'
                  : countUnknown
                    ? 'Line items may own these figures'
                    : 'The line items own these figures'
              }
              expanded
              onToggle={undefined}
              errorCount={0}
            >
              <p className="text-xs text-fg-muted">
                {/*
                  ⚠️ The create-with-draft-lines wording is a THIRD case, not a
                  variant of the other two, and it has to be present tense about
                  a future write: the boxes disappear the moment a line is
                  added, before either the quote or the line exists. Without
                  this sentence a typed figure appears to have been thrown away.
                */}
                {draftLinesPending
                  ? 'Worked out from the line items. Remove them all to enter figures by hand.'
                  : countUnknown
                    ? 'Line items may own these — anything typed here would be replaced.'
                    : 'The line items own these — anything typed here would be replaced.'}
              </p>
              {/*
                🔴 **"Converting copies these onto the invoice as they stand."
                IS GONE — owner's stricter pass, 2026-09-05.**

                ⚠️ **The fact it stated is still true and is still the extra
                edge a quote has over an invoice**, and it is the reason the
                unknown case above LOCKS rather than guesses: `convert` copies
                all four totals onto the invoice exactly as they stand, so a
                header that disagrees with its lines is not corrected on the way
                across — it is billed. The lock is what protects that now; the
                sentence no longer does.
              */}
              {/*
                Read from the RECORD, so there is nothing to read before one
                exists. Four dashes on a create would look like four figures the
                server had returned as empty rather than four that have not been
                computed yet.
              */}
              {isEdit && (
                <>
                  <dl className="mt-3 grid gap-2">
                    {TOTAL_KEYS.map((key) => {
                      const stored = quote ? quote[key] : null;
                      /*
                       * ⚠️ **THE GRAND TOTAL ONLY.** A base-currency figure used
                       * to sit under all four rows and the owner has cut it back
                       * to the one line anybody reads twice — the same figure the
                       * editable block below has always converted, and the same
                       * one Xero puts a base reading against.
                       *
                       * Eight numbers where four belong is the cost, and it is
                       * not only clutter: a converted SUB-TOTAL and a converted
                       * TAX are two figures nobody quotes, sitting in the same
                       * type and weight as the one figure somebody might, which
                       * is how a reader ends up reading the wrong line out to an
                       * accountant.
                       *
                       * ⚠️ Still per-row rather than derived once, and the
                       * `stored !== null` test stays: `grandTotal` is NOT NULL,
                       * but this reads from the record and a null must render
                       * NOTHING rather than a converted zero. `toBaseCurrency`
                       * remains the only arithmetic — never
                       * `Number(rate) * Number(total)`.
                       */
                      const inBase =
                        key === 'grandTotal' && exchangeRate !== null && stored !== null
                          ? toBaseCurrency(stored, exchangeRate)
                          : null;
                      return (
                        <div key={key} className="flex items-baseline justify-between gap-3">
                          <dt className="text-xs text-fg-secondary">{TOTAL_LABELS[key]}</dt>
                          <dd className="text-right text-sm text-fg">
                            <InvoiceMoney
                              value={stored}
                              currency={values.currency === '' ? null : values.currency}
                            />
                            {/*
                              The base figure goes SECOND and is visibly
                              secondary, as it is on Xero. The document
                              currency is what the customer is billed in and
                              what the stored column holds; the base figure is
                              a reading of it for the accounts. Leading with it
                              would invite someone to quote it back to a
                              customer.
                            */}
                            {inBase !== null && (
                              <span className="mt-0.5 block text-xs text-fg-muted">
                                <InvoiceMoney value={inBase} currency={exchangeRate?.baseCurrency} />
                              </span>
                            )}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                  {/*
                    ⚠️ A converted figure with no rate beside it is
                    unattributable — the reader cannot tell what it was worked
                    out from, and cannot tell a pinned rate from a live one. So
                    the rate line STAYS even though only the grand total is now
                    converted: it is what makes that one figure readable, not a
                    footnote to a block of four. Its condition is `exchangeRate
                    !== null`, which is exactly the condition the converted
                    figure renders under, so the two can never appear apart.
                  */}
                  {exchangeRate !== null && (
                    <p className="mt-2 text-xs text-fg-muted">
                      {formatRateLine(exchangeRate)} — the rate pinned to this quote.
                    </p>
                  )}
                </>
              )}
            </FormSection>
          ) : (
            <FormSection
              title="Totals"
              summary="Only for a quote with no line items"
              expanded={totalsExpanded}
              onToggle={() => setTotalsOpen((prev) => !prev)}
              errorCount={totalsErrorCount}
            >
              {/*
                ⚠️ Kept: it is the only warning that a figure typed here is
                DESTROYED, not merged, by the first line write — and the
                replaced figures are kept nowhere.
              */}
              <p className="text-xs text-fg-muted">
                Adding a line item overwrites these from the lines — the old figures are not kept.
              </p>
              <div className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2 [&>*]:min-w-0">
                {TOTAL_KEYS.map((key) => (
                  <Field
                    key={key}
                    label={TOTAL_LABELS[key]}
                    error={errors[key]}
                  >
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Input
                        id={id}
                        // Text with a numeric mode, not type="number": the value
                        // is an exact decimal string end to end, and a number
                        // input hands back a rounded or locale-formatted one.
                        inputMode="decimal"
                        value={values[key]}
                        placeholder="1800.00"
                        /*
                         * 🔴 **The null-versus-zero `title` is GONE — owner's
                         * stricter pass, 2026-09-05.** It read "Blank means not
                         * recorded, which is not the same as zero." on
                         * `discountTotal` and `taxTotal` only.
                         *
                         * ⚠️ **The COLUMNS still differ and nothing on screen
                         * says so.** Those two are NULLABLE, where `subTotal`
                         * and `grandTotal` are NOT NULL DEFAULT 0.00 — so an
                         * empty box means "not recorded" on half this grid and
                         * is refused on the other half. Same removal made on
                         * the invoice form's equivalent `hint`, which settles
                         * the two having drifted apart.
                         */
                        invalid={Boolean(errors[key])}
                        aria-describedby={describedBy}
                        onChange={(event) => setField(key, event.target.value)}
                      />
                    )}
                  </Field>
                ))}
              </div>
              {/*
                The grand total in the base currency, re-derived as it is typed
                — which is what Xero shows and is the only figure here anyone
                reads twice.

                ⚠️ Only the GRAND total, and only while it is a valid amount.
                Converting a half-typed `18` would show a base figure that is
                wrong and then silently correct itself, and converting all four
                would put eight numbers where the person is entering four. When
                the box is empty or mid-edit this renders NOTHING rather than a
                zero — a `0.00` here would read as a real converted total.
              */}
              {exchangeRate !== null && AMOUNT_PATTERN.test(values.grandTotal.trim()) && (
                <div className="mt-3 border-t border-border pt-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs text-fg-secondary">
                      Grand total in {exchangeRate.baseCurrency}
                    </span>
                    <span className="text-sm text-fg">
                      <InvoiceMoney
                        value={toBaseCurrency(values.grandTotal.trim(), exchangeRate)}
                        currency={exchangeRate.baseCurrency}
                      />
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-fg-muted">
                    {formatRateLine(exchangeRate)} — only {exchangeRate.documentCurrency} figures
                    are stored.
                  </p>
                </div>
              )}
            </FormSection>
          )}
        </div>

        {/*
          THE NOTE — last thing on the form, on the owner's instruction.

          ⚠️ **CREATE ONLY**, and `!isEdit || justCreated` is what says so: on a
          quote that already existed when this form opened, notes belong in the
          record's own Tasks & notes drawer, which lists them, edits them and
          deletes them. The `justCreated` half keeps the section alive across
          the moment this form turns into an edit of the quote it just made —
          without it the outcome of the note write would be unmounted in the
          same render that produced it, and a refusal would never be seen.

          ⚠️ It is NOT a field on the quote. It is a `d_notes` row written by a
          separate request, so it never enters `buildPayload`, never appears in
          `knownWriteKeys`, and its refusal is never marked on a header control.
        */}
        {(!isEdit || justCreated) && (
          <DraftNoteSection
            documentNoun="quote"
            value={noteDraft}
            onChange={setNoteDraft}
            disabledReason={noteDenial}
            outcome={noteOutcome}
            created={justCreated}
            onRetry={() => void retryDraftNote()}
            retrying={savingNote}
          />
        )}
      </form>

      {/*
        The exchange-rate dialog.

        A SIBLING of the `<form>`, and that placement is load-bearing: React
        propagates by its own tree rather than the DOM's, so a
        control inside the form's subtree can reach the form's `onSubmit` even
        when it is portalled elsewhere in the document — which is how a nested
        picker once created a half-filled deal in this repo. A sibling has no
        such path at all, which is a stronger guarantee than remembering to stop
        the event. The dialog also contains no `<form>` of its own.
      */}
      {rateDialogOpen && baseCurrency !== null && documentCurrency !== null && (
        <ExchangeRateDialog
          baseCurrency={baseCurrency}
          documentCurrency={documentCurrency}
          initialRate={values.currencyRate.trim()}
          onCancel={() => setRateDialogOpen(false)}
          onSave={(next) => {
            setField('currencyRate', next);
            setRateDialogOpen(false);
          }}
        />
      )}

    </Modal>
  );
}


/**
 * What the create's push to Xero did — one band, six outcomes, and **none of
 * them may read as a failed save**.
 *
 * ⚠️ **Every sentence here opens by restating that the quote exists**, on all
 * six. That is the load-bearing repetition: the paragraph above already says
 * the quote was created, but a red or amber block underneath it is read as a
 * correction of the thing above it, and the correction people supply for
 * themselves is "so it did not save after all". They then close the form and
 * create a second quote — which, once Xero is reachable again, is two documents
 * in SEED's accounting system for one piece of work.
 *
 * ⚠️ **Tone is decided per outcome, not by "did it work".** Four of the five
 * failures are amber rather than red, because none of them is a fault: a
 * cooldown is a healthy connection with a spent budget, and an unlinked company
 * is a setup step nobody has done yet — on a fresh dataset it is the ORDINARY
 * answer, not an exception. Red is reserved for a refusal we cannot name, which
 * is the only one where something may genuinely be wrong.
 *
 * ⚠️ **`Try again` is offered on three of them and withheld from two, and the
 * withholding is the considered half.** `XERO_QUOTE_REFUSALS` types `retryable`
 * as the literal `false` for every mapped refusal precisely because repeating
 * an identical request reproduces an identical answer while spending one of the
 * tenant's calls to do it. A cooldown is that case at its most certain — the
 * button would be swallowed by the pre-check and change nothing — so the two
 * cooldown outcomes name the quote's own page instead, which is where the
 * permanent control lives. The company case DOES get one, because its button is
 * pressed after a state change somewhere else (the company was linked), not to
 * ask the same question twice.
 */
function CreatePushNote({
  outcome,
  busy,
  onRetry,
}: {
  outcome: CreatePushOutcome;
  busy: boolean;
  onRetry: (() => void) | undefined;
}) {
  if (outcome.kind === 'sent') {
    return (
      <Notice tone="success" role="status" className="break-words">
        Sent to Xero, which now holds a draft copy.
      </Notice>
    );
  }

  /*
   * The two cooldown outcomes share one sentence because they are one fact
   * about one shared budget — the only difference is whether the local deadline
   * knew about it before the call or the server said so during it, and that
   * distinction is ours rather than the reader's. `xeroCooldownNotSentMessage`
   * is the agreed wording; nothing here paraphrases it.
   */
  if (outcome.kind === 'skipped-cooldown' || outcome.kind === 'rate-limited') {
    return (
      <Notice tone="warning" role="status" className="break-words">
        {xeroCooldownNotSentMessage('quote', outcome.seconds)}
      </Notice>
    );
  }

  /*
   * ⚠️ **THE SERVER DECLINED TO PUSH, AND THAT IS NOT A FAILURE.** A quote with
   * no lines yet, or one whose company nobody has linked, is an ordinary draft —
   * the server checks both BEFORE spending a call on the tenant's 60-a-minute
   * budget. Rendering it in the danger tone would put a red block in front of
   * every new quote in the system and in front of somebody who has done nothing
   * wrong, so it is `info` and it leads with the record being saved.
   *
   * ⚠️ **The server's own `reason` is shown verbatim** rather than paraphrased,
   * for the same reason a refusal's messages are: it names which of several
   * pre-flight conditions stopped the push, and no fixed sentence here can carry
   * that. It is not an error string.
   */
  if (outcome.kind === 'not-sent-yet') {
    return (
      <Notice tone="info" role="status" className="break-words">
        <strong className="font-semibold">This quote is saved.</strong> It has not been sent to
        Xero yet — {outcome.reason} It can be sent from the quote&rsquo;s own page once it is
        ready.
      </Notice>
    );
  }

  if (outcome.kind === 'company-not-linked') {
    return (
      <Notice
        tone="warning"
        role="status"
        className="break-words"
        action={
          outcome.companyId !== null || onRetry ? (
            <span className="flex flex-wrap items-center gap-2">
              {outcome.companyId !== null && (
                <Link
                  to={`/companies/${outcome.companyId}`}
                  className="font-medium underline"
                >
                  Open the company
                </Link>
              )}
              {onRetry && (
                <Button variant="secondary" size="sm" onClick={onRetry} loading={busy} disabled={busy}>
                  Try again
                </Button>
              )}
            </span>
          ) : undefined
        }
      >
        <span className="min-w-0">
          {/*
            ⚠️ A STATE TO FIX, never an error. On a fresh dataset most companies
            have no Xero contact yet, so this is what an ordinary first create
            answers — and wording it as a failure would teach people that
            creating quotes is broken. The company is NAMED because the reader
            may have several open and "the company" sends them to check all of
            them.
          */}
          {/*
            ⚠️ LEADS WITH "SAVED" AND MUST CONTINUE TO. Xero does not enforce
            document-number uniqueness, so anyone reading this as a failed SAVE and
            saving again can raise a second live document. Only the explanation was
            cut — "so Xero has nobody to bill it to", and the instruction to link the
            company, which the button beside this already is.
          */}
          This quote is saved, but not sent to Xero:{' '}
          <strong className="font-semibold">{outcome.companyName ?? 'its company'}</strong> is not
          linked to a Xero contact.
        </span>
      </Notice>
    );
  }

  if (outcome.kind === 'unreachable') {
    return (
      <Notice
        tone="warning"
        role="alert"
        className="break-words"
        action={
          onRetry ? (
            <Button variant="secondary" size="sm" onClick={onRetry} loading={busy} disabled={busy}>
              Try again
            </Button>
          ) : undefined
        }
      >
        <span className="min-w-0">
          {/*
            ⚠️ NOTHING may describe Xero's copy here. The request got no answer,
            so it may have landed and it may not have — and a second push is
            safe only because the SERVER decides create-vs-update from its own
            link rather than from what we believe. Said plainly, so a person who
            checks first is not surprised to find it already there.
          */}
          This quote is saved. Whether it reached Xero is not known — check the Xero panel on the
          quote&rsquo;s own page.
        </span>
      </Notice>
    );
  }

  return (
    <Notice tone="danger" role="alert" className="break-words">
      <p>
        <strong className="font-semibold">
          This quote is saved. Xero did not take it, and nothing was undone.
        </strong>{' '}
        It can be sent from the quote&rsquo;s own page.
      </p>
      {/*
        Xero's own words, one line each. Never paraphrased and never replaced by
        a sentence of ours: the useful half of a refusal is derived per tenant —
        which rate to pick instead, which tracking option is missing — and no
        fixed copy here can carry it.
      */}
      <ul className="mt-2 grid gap-1 pl-6">
        {outcome.messages.map((message) => (
          <li key={message} className="list-disc">
            {message}
          </li>
        ))}
      </ul>
      {onRetry && (
        <div className="mt-2">
          <Button variant="secondary" size="sm" onClick={onRetry} loading={busy} disabled={busy}>
            Try again
          </Button>
        </div>
      )}
    </Notice>
  );
}

/**
 * "Every line landed", said naturally at the counts that actually occur.
 *
 * `All 1 line item was saved` is what a count-first sentence reads like at one,
 * so one and two get their own wording rather than a number and a plural glued
 * together. Both word forms are written out here, as everywhere else in this
 * app — nothing is pluralised by appending a letter.
 *
 * Never called with 0: no outcome is recorded when no line was sent, and that
 * case says "No line items were entered" instead.
 */
function savedLinesSentence(attempted: number): string {
  if (attempted === 1) return 'Its line item was saved with it.';
  if (attempted === 2) return 'Both line items were saved with it.';
  return `All ${attempted} line items were saved with it.`;
}

/**
 * What is known about converting this quote into the organisation's base
 * currency, said under the Currency picker — the position Xero puts it in.
 *
 * ⚠️ **Six states, and each says a different thing to the reader.** The
 * temptation is one sentence covering "there is no converted figure", because
 * that is what they have in common to a renderer. It is the wrong sentence in
 * four of the six:
 *
 * - **`base-unknown`** is not a missing rate, it is a missing CONNECTION, and
 *   the fix is on `/integrations`. Wording it as a missing rate would send
 *   somebody to a dialog that cannot help them — the dialog needs a base
 *   currency to state a rate against, which is why it is not offered here.
 * - **`same`** is not a gap at all. A quote already in the base currency has
 *   nothing to convert, and copy implying something is missing would have
 *   people pinning rates of 1 to make a warning go away — which is exactly the
 *   fabricated parity every comment in this file warns about.
 * - **`checking`** is a moment, not a state of the data. Saying "not connected"
 *   during it would be a claim we cannot yet support, and it flickers.
 * - **`no-currency`** is answered by the picker sitting directly above, reading
 *   "Not set". A second sentence saying so is noise.
 *
 * ⚠️ **It renders `error` in every state, including the ones with no body.**
 * `currencyRate` is in this form's `knownWriteKeys`, so a 422 naming it is
 * marked rather than bannered — and a marked key with nowhere on screen to
 * appear is the "one field needs attention, highlighted below" failure with
 * nothing highlighted. This is that somewhere.
 */
// `documentCurrency` was a prop here and is gone: its only reader was the
// `same` branch's sentence, removed 2026-09-05.
function ExchangeRateNote({
  state,
  baseCurrency,
  rate,
  exchangeRate,
  error,
  onSet,
  onRemove,
}: {
  state: 'no-currency' | 'checking' | 'base-unknown' | 'same' | 'unset' | 'set';
  baseCurrency: string | null;
  rate: string;
  exchangeRate: ExchangeRate | null;
  error: string | undefined;
  onSet: () => void;
  onRemove: () => void;
}) {
  const body = (() => {
    switch (state) {
      case 'no-currency':
        return null;

      /*
       * Both silent since the owner's stricter pass, 2026-09-05. They rendered
       * "Checking the Xero connection…" and "Xero is not connected, so no
       * converted figure can be shown." — a loading state and an explanation of
       * an absent figure, neither of which is a refusal or a money warning.
       *
       * ⚠️ The reason there is no "Set exchange rate" button on `base-unknown`
       * is UNCHANGED and is not a copy decision: a rate is a pair of currencies
       * and one of them is missing, so the dialog could not even state the
       * question — `1 ??? = x AUD`. A control whose only outcome is a dead end
       * is absent rather than inert. Do not add one now that the sentence
       * explaining the emptiness has gone.
       */
      case 'checking':
      case 'base-unknown':
        return null;

      case 'same':
        /*
         * The ordinary case is silence: this quote is in the base currency and
         * there is nothing to convert.
         *
         * The exception is a rate that is still STORED — reachable by changing
         * the currency of a quote that had one. It is not cleared on the
         * currency's behalf, because silently discarding a recorded figure is
         * data loss dressed as tidying; it is stated, with the way to remove it.
         */
        /*
         * 🔴 The sentence went in the owner's stricter pass, 2026-09-05: "A rate
         * of {rate} is stored, but this quote is already in {currency} —
         * nothing is converted." The CONTROL stays, because a control is not
         * copy and this is the only way to remove such a rate.
         *
         * ⚠️ The rate is still not cleared on the currency's behalf — silently
         * discarding a recorded figure is data loss dressed as tidying — so the
         * button now appears with nothing saying why.
         */
        if (rate === '') return null;
        return (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-fg-muted">
            <Button variant="ghost" size="sm" onClick={onRemove}>
              Remove rate
            </Button>
          </div>
        );

      case 'unset':
        return (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-fg-muted">
            <span className="min-w-0">
              No rate set — Xero uses its daily rate on push.
            </span>
            <Button variant="secondary" size="sm" onClick={onSet}>
              Set exchange rate
            </Button>
          </div>
        );

      case 'set':
        /*
         * `exchangeRate` is null here only when the stored string will not
         * parse — an older record, or a rate with more than six decimal places.
         * It is reported rather than rendered as absent: a quote that silently
         * showed no conversion, with a rate sitting in the column, would look
         * like the feature was off.
         */
        if (exchangeRate === null) {
          return (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-warning">
              <span className="min-w-0">
                The stored rate <span className="tabular-nums">{rate}</span> cannot be read, so no{' '}
                {baseCurrency} figure is shown.
              </span>
              <Button variant="secondary" size="sm" onClick={onSet}>
                Replace it
              </Button>
            </div>
          );
        }
        return (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-fg-muted">
            {/*
              Xero prints `1 GBP = 1.89207 AUD (Source: XE, 27 Aug 2026)`. The
              parenthetical names where the rate came from, and ours came from a
              person — so it says so. ⚠️ Do not borrow Xero's provider wording:
              claiming a mid-market source for a hand-typed figure would put a
              false provenance on a document a customer is invoiced against, and
              it is the one part of this line nothing would ever check.

              ⚠️ **The invoice form's copy of this line no longer says
              "(entered by hand)" and this one still does. That is correct, not
              drift.** An invoice push stores the rate Xero applied, so over
              there the figure may have come from either side and the form
              cannot tell which; here nothing writes the column but a person, so
              the provenance is still knowable and still worth stating.
            */}
            <span className="min-w-0 tabular-nums text-fg-secondary">
              {formatRateLine(exchangeRate)}
            </span>
            <span className="min-w-0">(entered by hand)</span>
            <Button variant="ghost" size="sm" onClick={onSet}>
              Change
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemove}>
              Remove
            </Button>
          </div>
        );
    }
  })();

  if (body === null && error === undefined) return null;

  return (
    <div className="space-y-1">
      {body}
      {error !== undefined && (
        <p role="alert" className="text-xs break-words text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Enter a custom exchange rate, pinned to this document.
 *
 * ⚠️ **Custom only, and the automatic rate is NOT offered as a disabled
 * control.** Xero's own dialog gives two choices — its provider's mid-market
 * rate, or a rate you type. Ours gives one, because the provider half needs an
 * API this app cannot reach: the currency service the owner supplied speaks
 * plain HTTP on a non-TLS port and sends no CORS header at all, so a browser on
 * an HTTPS origin is blocked twice over. A backend proxy has been asked for and
 * does not exist. Until it does, a "Use today's rate" button could only ever
 * fail, and this repo's standing rule is that such an affordance is absent
 * rather than inert. **The seam is here**: when the proxy lands, this dialog
 * gains a second choice and `ratePerBase` starts having a source other than a
 * person — which is also when `ExchangeRateNote`'s "(entered by hand)" stops
 * being true of every rate and has to become conditional.
 *
 * ⚠️ **The draft lives here and nowhere else.** It reaches the form's values on
 * Save only, so cancelling cannot leave a half-typed rate in a payload — and
 * because the form's own diff drives the PATCH, an unchanged rate is not sent
 * at all.
 *
 * There is no `<form>` element inside: the dialog is portalled but React
 * propagates by its own tree, so a submit here could otherwise reach the quote
 * form and PATCH the header. A button and an onClick cannot.
 */
function ExchangeRateDialog({
  baseCurrency,
  documentCurrency,
  initialRate,
  onCancel,
  onSave,
}: {
  baseCurrency: string;
  documentCurrency: string;
  initialRate: string;
  onCancel: () => void;
  onSave: (rate: string) => void;
}) {
  const [draft, setDraft] = useState(initialRate);
  const trimmed = draft.trim();
  const valid = isValidCurrencyRate(trimmed);
  /*
   * An empty box is not an error, it is an unfinished one — the box opens empty
   * on a quote with no rate, and reddening it before a keystroke would be
   * scolding someone for not having typed yet. Save is simply unavailable.
   * Removing a rate is `Remove` on the note, not an emptied box here, so there
   * is no clearing intent this refusal could be blocking.
   */
  const error = trimmed !== '' && !valid;

  return (
    <Modal
      open
      onClose={onCancel}
      title="Set exchange rate"
      /*
       * The DIRECTION is the whole content of this sentence, and getting it
       * backwards is a silent factor-squared error rather than a visible one:
       * `1 GBP = 1.89 AUD` and `1 AUD = 0.53 GBP` are both plausible-looking
       * numbers. Xero states it base-first, so this does too, and the field
       * label repeats it rather than relying on the reader holding it.
       */
      description={`How many ${documentCurrency} one ${baseCurrency} buys.`}
      size="sm"
      // Losing a typed rate to a clipped backdrop is losing real work.
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            onClick={() => {
              if (!valid) return;
              onSave(trimmed);
            }}
          >
            Save rate
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label={`Exchange rate — 1 ${baseCurrency} in ${documentCurrency}`}
          error={
            error
              ? `Use a rate above zero with at most ${CURRENCY_RATE_MAX_DP} decimal places.`
              : undefined
          }
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-sm text-fg-secondary">1 {baseCurrency} =</span>
              <Input
                id={id}
                /*
                 * Text with a numeric mode, never `type="number"`. The value is
                 * an exact decimal string end to end — a number input hands
                 * back a rounded or locale-formatted one, and a rate is the
                 * value in this form where six decimal places actually matter.
                 */
                inputMode="decimal"
                value={draft}
                placeholder="1.89207"
                invalid={error}
                aria-describedby={describedBy}
                onChange={(event) => setDraft(event.target.value)}
              />
              <span className="shrink-0 text-sm text-fg-secondary">{documentCurrency}</span>
            </div>
          )}
        </Field>

        {/*
          ⚠️ Kept under the copy rule that a sentence stays when it prevents a
          specific wrong conclusion. Anyone who has used Xero's own dialog will
          look for the provider rate and, not finding it, conclude the control
          is broken or that they have missed a setting. This says why the other
          option is not here.
        */}
        {/*
          🔴 "Xero's automatic rate is not available here yet." went in the
          owner's stricter pass, 2026-09-05 — it explained a missing affordance
          rather than warning about money or reporting a refusal.

          ⚠️ The wrong conclusion it prevented is back: anyone who has used
          Xero's own dialog will look for the provider rate and, not finding it,
          decide the control is broken or that they missed a setting. The reason
          it is genuinely absent is recorded on this component's own doc comment
          — the currency service speaks plain HTTP with no CORS header, so a
          browser on an HTTPS origin is blocked twice over, and a backend proxy
          has been asked for and does not exist.
        */}

        {/*
          What a pinned rate DOES, said once. Without it the choice reads as a
          display preference, which is the wrong conclusion in the direction
          that costs money: it is written to the document and travels to Xero.

          ⚠️ **`InvoiceFormModal`'s copy of this paragraph carries a third
          sentence — "Either way, pushing replaces this with the rate Xero
          applied" — and it is ABSENT HERE ON PURPOSE. Do not add it.**

          A quote and an invoice genuinely differ on this. An invoice push reads
          `CurrencyRate` back off the write response and stores it, so a typed
          rate does not survive one. A quote push writes back only the quote
          number: there is no quote equivalent of `recordXeroCurrencyRate`, so
          the rate a person pins here stays pinned and this dialog's two
          sentences are the whole truth about it.

          The asymmetry reads as an oversight, which is why it is written down.
          Copying the invoice's sentence across for symmetry would state a
          replacement that does not happen to a quote — a wrong claim about a
          figure on a customer-facing document, and one nothing typechecks.

          What convert does is NOT a reason to add it either: convert copies
          this rate onto the new invoice, and it is THAT invoice's push that
          replaces THAT invoice's rate. Nothing touches the quote's own.
        */}
        <p className="text-xs text-fg-muted">
          Used when this quote is pushed; unset, Xero uses its daily rate.
        </p>
      </div>
    </Modal>
  );
}

/**
 * A disclosure inside the form — the totals, and the Xero document fields.
 *
 * Built from a button and a `hidden` region rather than `<details>`, so a failed
 * save can force it open without fighting the element's own state and so the
 * collapsed content stays mounted: typing in a section then closing it must not
 * throw the input away. Pass `onToggle: undefined` for a read-only variant,
 * which has nothing to hide.
 */
function FormSection({
  title,
  summary,
  expanded,
  onToggle,
  errorCount,
  children,
}: {
  title: string;
  summary: string;
  expanded: boolean;
  onToggle: (() => void) | undefined;
  errorCount: number;
  children: ReactNode;
}) {
  const uid = useId();
  const panelId = `${uid}-section`;
  const heading = (
    <>
      <span className="text-sm font-medium text-fg">{title}</span>
      <span className="hidden text-xs text-fg-muted sm:inline">{summary}</span>
      {errorCount > 0 && (
        <span className="ml-auto text-2xs font-semibold text-danger">
          {errorCount === 1 ? '1 problem' : `${errorCount} problems`}
        </span>
      )}
    </>
  );

  return (
    <section className="rounded-lg border border-border">
      <h3>
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={panelId}
            className={cx(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left',
              'transition-colors duration-150 ease-out motion-reduce:transition-none',
              'hover:bg-surface-hover',
              expanded && 'rounded-b-none',
            )}
          >
            <ChevronIcon open={expanded} />
            {heading}
          </button>
        ) : (
          <div className="flex w-full items-center gap-2 px-3 py-2.5">{heading}</div>
        )}
      </h3>
      <div id={panelId} hidden={!expanded} className="border-t border-border px-3 py-3">
        {children}
      </div>
    </section>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      {open ? <path d="m6 9 6 6 6-6" /> : <path d="m9 18 6-6-6-6" />}
    </svg>
  );
}

/*
 * 🔴 **`brandingHint()` WAS DECLARED HERE AND IS DELETED — owner's stricter
 * copy pass, 2026-09-05.** It returned the sentence under the branding-theme
 * picker, one per unreachable state and none at all for a working one.
 *
 * The seven strings it could return, recorded because the STATES still exist
 * and are still distinguishable if anybody is ever asked to bring them back:
 * "Checking the Xero connection…"; "Xero is not set up, so branding themes
 * cannot be listed."; "Xero is not connected, so branding themes cannot be
 * listed."; "Xero's rate limit is reached — the chosen branding theme stands,
 * and can be cleared."; "Xero's rate limit is reached — nothing is broken.";
 * "The branding theme list could not be read — the chosen one can still be
 * cleared."; "The branding theme list could not be read."; and "Reading
 * branding themes from Xero…".
 *
 * ⚠️ **The failure it existed to prevent is now live again, and that is the
 * accepted cost rather than an oversight**: a silently empty dropdown reads as
 * "this organisation has no branding themes", which is indistinguishable from
 * a real answer, so somebody can save a blank believing they chose it. The
 * rate-limit case is the sharpest — its sentence was deliberately worded to
 * stop a retry, and a retry is what emptied the shared Xero budget in the first
 * place.
 *
 * ⚠️ **Do not reintroduce it as a `disabled` alone.** A bare disabled control
 * makes an unreachable Xero, a failed read and an ordinary empty list
 * identical, which is strictly worse than the silence there is now.
 */

/**
 * Server-side search for the company, contact and deal pickers.
 *
 * Unchanged from the invoice form's copy. All three directories are larger than
 * one page, so filtering a fetched page locally would only ever search the
 * first 100 records. The Combobox debounces the term itself, so there is no
 * second debounce here.
 *
 * The record's existing link is pinned into the list: a search that does not
 * happen to return it must not make the field look unassigned, because an
 * unassigned-looking field saves as a `null` that quietly breaks the link.
 *
 * The deal branch differs twice over — its label comes from `dealName` rather
 * than `name`, and `/deals` takes no sort parameter at all.
 *
 * ⚠️ **The contacts branch is SCOPED to one company** (`scopeCompanyId`), which
 * is a different thing from the `enabled` flag below it and behaves differently
 * in the one case that matters: a disabled picker keeps whatever it last read,
 * while an unscoped one is EMPTY on purpose. See `src/lib/contact-scope.ts` for
 * the whole rule, the wording, and the measurement that the API does not check
 * the pair itself.
 */
function useLookupSearch(
  resource: 'companies' | 'contacts' | 'deals',
  pinned: { id: number; label: string } | null,
  /**
   * False when the picker this feeds is not rendered — a locked company, say.
   * The hook still runs (hooks always do) but issues no request, so a field
   * nobody can open costs nothing against the shared rate limit. The pinned
   * option survives regardless, so the value is still expressible.
   */
  enabled = true,
  /**
   * The company the contact list is scoped to. `undefined` on the two unscoped
   * pickers; `null` means "this IS a scoped picker and no company is chosen",
   * which fetches nothing and offers nothing — see `CONTACT_NEEDS_COMPANY`.
   */
  scopeCompanyId?: number | null,
  /**
   * COMPANIES ONLY — withhold every company that is not `approved`, and report
   * how many were withheld.
   *
   * ⚠️ **The optimisation, never the guarantee.** A company's approval can move
   * between this list being read and Save being pressed, so `validate()` refuses
   * again from the company's own record. Narrowing here only keeps a choice
   * whose one outcome is that refusal off the list.
   *
   * ⚠️ **A withheld company must not vanish silently.** One that is plainly on
   * `/companies` and absent here reads as a broken fetch, so the count goes into
   * the Combobox FOOTNOTE — the same exception to the no-helper-text rule as
   * "Showing the first 100 matches", and for the same reason: it is a fact about
   * the list, not advice about how to use it.
   *
   * ⚠️ **The PIN is never withheld**, in any mode. A quote already on a company
   * since rejected must still render its name, or the field reads "Unassigned"
   * over a record that holds one and the next save writes that emptiness back.
   * That is why the withholding happens on the fetched rows alone.
   */
  withholdUnapproved = false,
) {
  const [term, setTerm] = useState('');
  /*
   * Rows and the scope they ANSWER FOR, in one state, so "which company is this
   * list for" and "which company is selected" can never disagree for a frame —
   * the frame in which somebody could otherwise pick the previous company's
   * contact. Derived rather than cleared in a second effect, exactly as
   * `ProjectFormModal` tags its intakes with their cycle.
   *
   * ⚠️ That frame is worth closing here even though the server would accept the
   * write: it accepts it SILENTLY, so there is no 422 to fall back on.
   */
  const [result, setResult] = useState<{
    scope: number | null | 'unscoped';
    rows: { id: number; label: string }[];
    truncated: boolean;
    failed: boolean;
    /**
     * How many rows this page dropped for not being approved.
     *
     * ⚠️ **0 and "we did not look" are different facts and must not merge.** It
     * is 0 on every unfiltered list — contacts, deals, and companies on an edit
     * — which is true of those lists rather than a claim that nothing was
     * withheld from them. It is only ever read beside `withholdUnapproved`.
     */
    withheld: number;
  } | null>(null);
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);

  const scope: number | null | 'unscoped' = resource === 'contacts' ? scopeCompanyId ?? null : 'unscoped';

  useEffect(() => {
    if (!enabled) return;
    // No company, nothing to fetch. The empty answer is DERIVED below rather
    // than written here: a `setState` in this branch would be a render's worth
    // of cascade to reach a value already known during render.
    if (scope === null) return;

    const controller = new AbortController();
    const named = { search: term, perPage: 100, sort: 'name', order: 'asc' } as const;

    const request: Promise<{
      rows: { id: number; label: string }[];
      totalPages: number;
      withheld: number;
    }> =
      resource === 'companies'
        ? api.listCompanies(named, controller.signal).then((result) => {
            /*
             * ⚠️ **Filtered on the ENUM, never on a name.** `approvalStatus` is a
             * literal union — `'pending' | 'approved' | 'rejected'` — and
             * `types.ts` says why it is safe to match where a stage or status
             * NAME is not: those three values ARE the flow, so an admin adding a
             * fourth would be changing the workflow rather than adding a row.
             *
             * ⚠️ **`=== 'approved'`, written out, rather than excluding the two
             * negatives.** A fourth member added upstream would then be offered
             * by default; this way it is withheld and counted, which is the
             * visible wrong answer rather than the silent one.
             */
            const keep = withholdUnapproved
              ? result.data.filter((row) => row.approvalStatus === 'approved')
              : result.data;
            return {
              rows: keep.map((row) => ({ id: row.id, label: row.name })),
              totalPages: result.meta.totalPages,
              withheld: result.data.length - keep.length,
            };
          })
        : resource === 'contacts'
          ? // ⚠️ A SINGLE id — `?companyId=15,16` is a 422 on this endpoint.
            api
              .listContacts({ ...named, companyId: scope as number }, controller.signal)
              .then((result) => ({
                rows: result.data.map((row) => ({ id: row.id, label: row.name })),
                totalPages: result.meta.totalPages,
                withheld: 0,
              }))
          : api.listDeals({ search: term, perPage: 100 }, controller.signal).then((result) => ({
              rows: result.data.map((row) => ({ id: row.id, label: row.dealName })),
              totalPages: result.meta.totalPages,
              withheld: 0,
            }));

    request
      .then((response) => {
        if (controller.signal.aborted) return;
        setResult({
          scope,
          rows: response.rows,
          truncated: response.totalPages > 1,
          failed: false,
          withheld: response.withheld,
        });
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty. Both render no options, and telling
         * somebody a company has no contacts when the request simply did not
         * come back sends them to create a person who already exists.
         *
         * ⚠️ **`withheld: 0` here is "we withheld nothing", NOT "nothing was
         * unapproved"** — the request never came back, so there is nothing to
         * report and the footnote must stay silent rather than claim a count of
         * zero. The SAVE reads the company's own record, not this, and a failed
         * read there lets the save through.
         */
        if (!controller.signal.aborted) {
          setResult({ scope, rows: [], truncated: false, failed: true, withheld: 0 });
        }
      })
      .finally(() => {
        // Records which term the list now answers, so `loading` is derived
        // during render rather than toggled from inside the effect.
        if (!controller.signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
    /*
     * ⚠️ `withholdUnapproved` is in here because the effect READS it, not
     * because it moves: it is `!isEdit && !isConverting`, which is fixed for the
     * life of a mounted form, so this re-fetches nothing today. Omitting it
     * would still be wrong — a list filtered under one rule and re-read under
     * another is the stale-closure bug that shows as a picker quietly offering
     * what it just withheld.
     */
  }, [resource, term, enabled, scope, withholdUnapproved]);

  /**
   * The result, but only while it describes the scope currently selected — the
   * previous company's rows are not a stale version of this company's list, they
   * are the wrong list, so they are dropped rather than shown dimmed.
   *
   * ⚠️ A STABLE reference in every branch, because `options` memoises on it. The
   * no-company answer is the module-level `NO_SCOPE_RESULT` rather than a fresh
   * `{rows: []}`, and an inline literal here would rebuild the option list on
   * every render — which is the memo silently doing nothing.
   */
  const answered =
    scope === null
      ? NO_SCOPE_RESULT
      : result !== null && result.scope === scope
        ? result
        : null;
  // With no company there is nothing in flight and nothing to wait for, so the
  // field says why it is empty instead of spinning at a request never coming.
  const loading = enabled && scope !== null && (loadedTerm !== term || answered === null);

  /**
   * True when the pinned value is NOT among the rows the list returned — i.e.
   * the option exists only because it was pinned back in.
   *
   * The call site turns this into the "not one of this company's contacts"
   * label. Computed here rather than there because only this hook can tell an
   * injected option from a fetched one once they are all `ComboboxOption`s.
   */
  const pinInjected =
    pinned !== null && !(answered?.rows ?? []).some((row) => row.id === pinned.id);

  const options = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = (answered?.rows ?? []).map((row) => ({
      value: String(row.id),
      label: row.label,
    }));
    /*
     * ⚠️ The pin is UNCONDITIONAL; only the "not on this company" REASON is
     * gated (at the call site, on `contactScopeIsAuthoritative`). A stored
     * contact dropped from the list renders as "Unassigned" over a record that
     * holds one, and the next save writes that emptiness back.
     */
    if (pinned && !list.some((option) => option.value === String(pinned.id))) {
      list.unshift({ value: String(pinned.id), label: pinned.label });
    }
    return list;
  }, [answered, pinned]);

  return {
    options,
    pinInjected,
    setTerm,
    // Disabled means "answered by the pin", not "still loading" — a permanent
    // spinner on a field that will never fetch would be a lie.
    loading,
    /*
     * 🔴 **The TRUNCATION clause is gone — owner's stricter pass, 2026-09-05.**
     * It read "Showing the first 100 matches — keep typing to narrow." and was
     * removed by name along with every other truncation footnote.
     * `answered.truncated` is still computed.
     *
     * ⚠️ **`withheldCompanyNote` STAYS.** It is not a truncation: it reports
     * records deliberately WITHHELD by the company scope, which is a rule this
     * form applied, not a page size. The join stays a join rather than
     * collapsing to that one value — the two were independently true and a
     * coalesce between them would have dropped one silently, which is the
     * `a ?? b` rule this codebase records four instances of, and it applies
     * again the moment a second clause is added.
     */
    footnote:
      [withheldCompanyNote(answered?.withheld ?? 0) ?? null]
        .filter((part): part is string => part !== null)
        .join(' ') || undefined,
    /** What the Contact field needs to word its own hint. */
    scope: {
      companyId: typeof scope === 'number' ? scope : null,
      loading,
      failed: answered?.failed ?? false,
      count: answered?.rows.length ?? 0,
      searching: term.trim() !== '',
    },
  };
}

/**
 * The answer for a scoped picker with no company chosen: no rows, not loading,
 * not failed.
 *
 * ⚠️ Module-level so the reference is STABLE. `useLookupSearch` memoises its
 * option list on this object's identity, and a literal rebuilt each render
 * would make the memo a no-op while still looking like one.
 */
const NO_SCOPE_RESULT = {
  scope: null as number | null | 'unscoped',
  rows: [] as { id: number; label: string }[],
  truncated: false,
  failed: false,
  // Nothing was read, so nothing was withheld. Only ever reached by the SCOPED
  // contacts picker, which does no approval filtering in any case.
  withheld: 0,
};
